/**
 * API service functions organized by entity / domain.
 *
 * Each service namespace exposes typed functions that map 1-to-1 to the
 * FastAPI backend endpoints. Parameters mirror the backend query params and
 * request bodies. All list endpoints accept a common `ListParams` shape.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient, getAccessToken, clearTokens, refreshAccessToken } from "./client";
import { browserTimeZone } from "@/lib/browserTimeZone";
export { meetingRoomApi } from "./meetingRoomApi";
import type {
  QueuedTestRun,
  MaintenanceState,
  ApprovalRequest,
  ApprovalRequestList,
  FleetPatchDetail,
  FleetPatchSummary, IntegrationsReport, CalendarEvent, CreateCalendarEventRequest, UpdateCalendarEventRequest, CalendarTimelineResponse, UserOrganization, EntityHomeOrg, ComplianceOperation, ChangeImpact,
  ApprovalMatrixRule,
  ApprovalMatrixRuleInput,
  ProjectBudget,
  ProjectBudgetInput,
  CustomerDataExport,
  MeetingReconciliation,
  ProgramGatesResponse,
  GateSignoffInput,
  TestBindingCandidate,
  TestBindingResult,
  OrgApiKey,
  DocumentVersionDiff,
  RecordCurationList,
  RecordCurationProposal,
  TranscriptCurationList,
  TranscriptCurationProposal,
  OpenErasureRequest,
  ErasureExtensionResult,
  ErasureRefusalResult,
  OrgErasureApproval,
  OrgErasureResult,
  OrgErasureContactedByHand,
  TranscriptCurationStatus,
  GuestErasureRequestFiled,
  GuestErasureResult,
  ObjectiveListResponse,
} from "./types";
import type {
  ImportFidelity,
  JiraImportDryRun,
  JiraImportResult,
  JiraExportReport,
  AuthEvidenceBundle,
  AuthLoginEvent,
  LoginSourceRollup,
  DispositionCertificate,
  LegalHold,
  RetentionAssessment,
  ImportMapping,
  ImportMappingConfirmed,
  ImportMappingTurn,
  AuthUser,
  Baseline,
  BaselineRestoreSummary,
  BaselineSignoff,
  BaselineFieldReadiness,
  ChangePasswordRequest,
  SocialLoginRequest,
  SocialProvidersResponse,
  ChangeRequest,
  ImpactTree,
  CreditBalance,
  CreditPricing,
  CreditTransaction,
  CreditUsageSummary,
  Component,
  ComponentSpecification,
  Defect,
  Document,
  DocumentIssuanceRecord,
  DocumentIssuanceVerdict,
  DocumentTemplate,
  DocumentAudience,
  GenerateDocumentRequest,
  PreflightResult,
  HealthAssessment,
  AuditTrail,
  LineageMap,
  ListParams,
  LoginRequest,
  Metric,
  Module,
  Notification,
  NotificationListParams,
  Objective,
  OutcomeRecordSummary,
  OutcomeCriterionEvidence,
  OutcomeRecordVerdict,
  WebhookEndpoint,
  WebhookEndpointCreate,
  WebhookEndpointUpdate,
  WebhookDelivery,
  ProjectSecret,
  ProjectSecretWrite,
  PaginatedResponse,
  Persona,
  Plan,
  DemoReadiness,
  Project,
  ProvenanceResult,
  QAAnswerResponse,
  QAAnswerSavedResponse,
  QASession,
  QASessionDetail,
  RegisterRequest,
  Report,

  Role,
  Setting,
  StatusChangeRequest,
  StatusChangeResult,
  StatusConfig,
  StatusRegistryEntry,
  Subscription,
  InvoiceListResponse,
  Task,
  TestCase,
  TestCaseListResponse,
  TestExecution,
  TokenResponse,
  UsageInfo,
  User,
  WorkflowCancelRequest,
  WorkflowDefinition,
  WorkflowResumeRequest,
  WorkflowRun,
  WorkflowRunListParams,
  WorkflowTriggerRequest,
  OAuthStatus,
  OAuthConnectResponse,
  GeneratedAgenda,
  ActivityEntry,
  DataExport,
  UserStory,
  UserPersona,
  ExportFormat,
  DocumentSchedule,
  DocumentVersion,
  DocumentLifetimeCost,
  RegenerateDocumentRequest,
  OrgSummary,
  WorkflowProgress,
  NDAStatus,
  NDADocument,
  NDASignRequest,
  NDASignature,
  NDAInvitation,
  NDAInviteRequest,
  NDADocumentVersion,
  NDAStats,
  NDAExternalInvitation,
  NDAExternalSignRequest,
  NDAExternalSignResult,
  NDAMeetingAttendees,
  NDAMeetingSendResult,
  QualityMetrics,
  VelocityData,
  CoverageMatrix,
  TeamContributions,
  RiskMatrix,
  ProjectAICosts,
  OrgAICostSummary,
  OrgAICostTrend,
  OrgAuthoritativeCost,
  EntityAICost,
  TranscriptionResult,
  TranscriptionStatus,
  ActiveProcessesResponse,
  QAProjectTypeInfo,
  QACategoryDetail,
  QATemplateProgress,
  UserOrgMembership,
  DevelopmentTimeline,
  ProjectCostBreakdown,
  OrgDevelopmentAnalytics,
  OrgQualityMetrics,
  OrgTeamContributions,
  OrgFinancialSummary,
  PlatformDevelopmentAnalytics,
  PlatformOverview,
  PlatformFinancialTrends,
  PlatformOrgComparison,
  PlatformUserAnalytics,
  QAActionItem,
  QAActionItemSLASummary,
  ActionItemRecommendation,
  OnboardingStatus,
  OnboardingSubmission,
  OnboardingCompleteResponse,
  OnboardingProfileResponse,
  CommunicationStyleResponse,
  ImpactAnalysisResult,
  ScopeViolationRequest,
  MeetingBotSession,
  MeetingBotStatus,
  MeetingDetail,
  MeetingDecisionArtifact,
  MeetingDecisionApprovalResult,
  MeetingParticipant,
  MeetingTranscriptSegment,
  MeetingIntelligenceState,
  MeetingReport,
  MeetingReportDistribution,
  CalendarSyncState,
  CalendarSyncResult,
  InfrastructureSummary,
  InfrastructureInvoice,
  InfrastructureInvoiceSummary,
  InfrastructureBillingSummary,
  InfrastructureBalance,
  InfrastructureDroplet,
  InfrastructureVolume,
  BillingHistoryEntry,
} from "./types";

// ---------------------------------------------------------------------------
// Helper: strip undefined values from params to keep query strings clean
// ---------------------------------------------------------------------------
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function cleanParams<T extends Record<string, any>>(
  params?: T,
): Record<string, string | number | boolean | undefined | null> | undefined {
  if (!params) return undefined;
  const cleaned: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && (typeof v === "string" || typeof v === "number" || typeof v === "boolean")) {
      cleaned[k] = v;
    }
  }
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const authApi = {
  // skipAuth: a 401 from a PRE-AUTH endpoint is the ANSWER ("wrong password"), not an
  // expired session. Without it the client treats the 401 as a dead session, tries to
  // refresh, fails, and then WIPES THE STORED CREDENTIALS AND REDIRECTS — so a mistyped
  // password looks like the app silently ate your session.
  login(body: LoginRequest): Promise<TokenResponse> {
    return apiClient.post<TokenResponse>("/auth/login", body, { skipAuth: true });
  },

  register(body: RegisterRequest): Promise<TokenResponse> {
    return apiClient.post<TokenResponse>("/auth/register", body, { skipAuth: true });
  },

  me(): Promise<AuthUser> {
    return apiClient.get<AuthUser>("/auth/me");
  },

  /** Clear the remembered pricing choice once it has been shown.
   *
   *  Called AFTER navigating, not before: an intent that is never consumed
   *  diverts every future login to billing, and one consumed too early is lost
   *  if the navigation fails. Row f8a46a1b. */
  consumePlanIntent(): Promise<{ cleared: string | null }> {
    return apiClient.post<{ cleared: string | null }>("/auth/plan-intent/consume");
  },

  changePassword(body: ChangePasswordRequest): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>("/auth/change-password", body);
  },

  logout(): Promise<{ message: string; tokens_revoked: number }> {
    return apiClient.post<{ message: string; tokens_revoked: number }>("/auth/logout");
  },

  refresh(refreshToken: string): Promise<TokenResponse> {
    return apiClient.post<TokenResponse>("/auth/refresh", {
      refresh_token: refreshToken,
    });
  },

  // ── Multi-org membership ──
  /** Organizations the caller belongs to (for the org switcher). */
  listOrganizations(): Promise<{ organizations: UserOrganization[] }> {
    return apiClient.get<{ organizations: UserOrganization[] }>("/auth/organizations");
  },
  /** Switch active org without re-login — returns a fresh token pair scoped to it. */
  switchOrganization(orgId: string): Promise<TokenResponse> {
    return apiClient.post<TokenResponse>("/auth/switch-organization", { org_id: orgId });
  },

  // ── TOTP MFA ──
  // Second step of login for an MFA-enrolled user: exchange the challenge token
  // + a TOTP (or recovery) code for a real session.
  loginMfa(body: { mfa_token: string; code: string }): Promise<import("./types").LoginResponse> {
    return apiClient.post<import("./types").LoginResponse>("/auth/login/mfa", body);
  },
  mfaStatus(): Promise<import("./types").MfaStatusResponse> {
    return apiClient.get<import("./types").MfaStatusResponse>("/auth/mfa/status");
  },
  // Begin enrolment — returns the provisioning URI (client renders the QR) + secret.
  // Uses whatever bearer token is set: a normal session (voluntary) OR a transient
  // enrol token (mandatory enrolment during login).
  mfaSetup(): Promise<import("./types").MfaSetupResponse> {
    return apiClient.post<import("./types").MfaSetupResponse>("/auth/mfa/setup");
  },
  mfaEnable(code: string): Promise<import("./types").MfaEnableResponse> {
    return apiClient.post<import("./types").MfaEnableResponse>("/auth/mfa/enable", { code });
  },
  mfaDisable(password: string): Promise<{ enabled: boolean }> {
    return apiClient.post<{ enabled: boolean }>("/auth/mfa/disable", { password });
  },
  mfaRegenerateRecoveryCodes(code: string): Promise<{ recovery_codes: string[] }> {
    return apiClient.post<{ recovery_codes: string[] }>("/auth/mfa/recovery-codes", { code });
  },
  // Org-admin: reset a user's MFA (lost authenticator + recovery codes) + unlock.
  mfaAdminReset(userId: string): Promise<{ reset: boolean; was_enabled: boolean }> {
    return apiClient.post<{ reset: boolean; was_enabled: boolean }>(`/auth/mfa/reset/${userId}`);
  },

  resetPassword(email: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>("/auth/reset-password", { email }, { skipAuth: true });
  },

  confirmResetPassword(
    token: string,
    newPassword: string,
  ): Promise<{ message: string }> {
    // Pre-auth: a 401 here is the answer (bad/expired token), not a dead session.
    return apiClient.post<{ message: string }>(
      "/auth/reset-password/confirm",
      { token, new_password: newPassword },
      { skipAuth: true },
    );
  },

  confirmEmailVerification(token: string): Promise<{ status: string }> {
    return apiClient.post<{ status: string }>("/auth/verify-email/confirm", {
      token,
    });
  },

  resendEmailVerification(email: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>("/auth/verify-email/resend", {
      email,
    });
  },

  getInviteInfo(token: string): Promise<{ email: string; org_name: string | null }> {
    return apiClient.get<{ email: string; org_name: string | null }>(
      `/auth/accept-invite/info?token=${encodeURIComponent(token)}`,
    );
  },

  acceptInvite(token: string, newPassword: string): Promise<{ status: string }> {
    return apiClient.post<{ status: string }>("/auth/accept-invite/confirm", {
      token,
      new_password: newPassword,
    });
  },

  socialLogin(body: SocialLoginRequest): Promise<TokenResponse> {
    return apiClient.post<TokenResponse>(
      `/auth/social/${body.provider}`,
      body,
    );
  },

  socialProviders(): Promise<SocialProvidersResponse> {
    return apiClient.get<SocialProvidersResponse>("/auth/social/providers");
  },
};

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export interface DependencySuggestion {
  predecessor_id: string;
  successor_id: string;
  dependency_type: string;
  reason: string;
}

export const projectsApi = {
  list(params?: ListParams): Promise<PaginatedResponse<Project>> {
    return apiClient.get<PaginatedResponse<Project>>("/projects", cleanParams(params));
  },

  /** Inferred (title-reference) task dependencies not yet recorded — the on-ramp
   *  that populates task_dependencies so the CPM/critical-path engine has input. */
  getDependencySuggestions(projectId: string): Promise<{
    project_id: string;
    suggestions: DependencySuggestion[];
    count: number;
  }> {
    return apiClient.get(`/projects/${projectId}/dependencies/suggestions`);
  },

  /** Is this project ready to demo? Founder 6467901243 (the ISW demo's lessons). */
  demoReadiness(id: string): Promise<DemoReadiness> {
    return apiClient.get<DemoReadiness>(`/projects/${id}/demo-readiness`);
  },

  get(id: string): Promise<Project> {
    return apiClient.get<Project>(`/projects/${id}`);
  },

  /** Row 2b6d6836: the org a project lives in — answered only to a member of
   *  that org (404 otherwise), so a cross-org link can offer the switch. */
  getHomeOrg(id: string): Promise<EntityHomeOrg> {
    return apiClient.get<EntityHomeOrg>(`/projects/${id}/home-org`);
  },

  create(body: Partial<Project>): Promise<Project> {
    return apiClient.post<Project>("/projects", body);
  },

  update(id: string, body: Partial<Project>): Promise<Project> {
    return apiClient.patch<Project>(`/projects/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/projects/${id}`);
  },

  changeStatus(id: string, body: StatusChangeRequest): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(`/projects/${id}/status`, body);
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/projects/${id}/history`,
      cleanParams(params),
    );
  },

  /** Get personalized "My Focus" data for the current user in a project. */
  focus(projectId: string): Promise<import("@/api/types").FocusData> {
    return apiClient.get(`/projects/${projectId}/focus`);
  },

  /** GET /gates?project_id= — every program gate, signed or UNSIGNED. */
  listGates(projectId: string): Promise<ProgramGatesResponse> {
    return apiClient.get<ProgramGatesResponse>(
      `/gates?project_id=${encodeURIComponent(projectId)}`,
    );
  },

  /** POST /gates/{code}/signoff — record a human ratification of one gate. */
  signOffGate(
    projectId: string, gateCode: string, input: GateSignoffInput,
  ): Promise<{ id: string; gate_code: string }> {
    return apiClient.post(
      `/gates/${encodeURIComponent(gateCode)}/signoff?project_id=${encodeURIComponent(projectId)}`,
      input,
    );
  },

  /** GET /projects/{id}/budget — time-phased plan + earned-value forecast.
   *  Check `has_budget` before reading any ratio: with no budget entered the
   *  figures are structural zeros, not a measurement. */
  getProjectBudget(projectId: string, asOf?: string): Promise<ProjectBudget> {
    const q = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
    return apiClient.get<ProjectBudget>(`/projects/${projectId}/budget${q}`);
  },

  /** PUT /projects/{id}/budget — replaces the stored plan. An empty `periods`
   *  clears the budget, which is a deliberate reachable state (a plan entered
   *  by mistake is withdrawn that way), not an error. */
  setProjectBudget(projectId: string, input: ProjectBudgetInput): Promise<ProjectBudget> {
    return apiClient.put<ProjectBudget>(`/projects/${projectId}/budget`, input);
  },
  workflowProgress(projectId: string): Promise<WorkflowProgress> {
    return apiClient.get<WorkflowProgress>(`/projects/${projectId}/workflow-progress`);
  },
  /** KI-0194: current SDLC phase + readiness without side-effects. */
  phaseStatus(projectId: string): Promise<import("@/api/types").PhaseStatus> {
    return apiClient.get(`/projects/${projectId}/sdlc/phase-status`);
  },
  /** KI-0194: attempt to advance to the next SDLC phase. */
  advancePhase(projectId: string): Promise<import("@/api/types").AdvancePhaseResult> {
    return apiClient.post(`/projects/${projectId}/sdlc/advance-phase`, {});
  },
};

// ---------------------------------------------------------------------------
// Portfolios + Programs — Phase 4 of the UX v2 redesign hierarchy.
//
// Top-down: Portfolio → Program → Project → Entity. The CRUD here is
// thin; the Portfolios/Programs pages compose it with EntityCard +
// LineageTrail + AttentionPanel for the full v2 experience.
// ---------------------------------------------------------------------------

export interface Portfolio {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  owner_id: string | null;
  strategic_intent: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface Program {
  id: string;
  org_id: string;
  portfolio_id: string;
  name: string;
  description: string | null;
  owner_id: string | null;
  status: string;
  target_completion_at: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// Portfolio KPI rollup (GET /portfolios/kpis) — the value-first Portfolio
// surface. Mirrors app/services/portfolio_kpi_service.py.
export interface PortfolioKpis {
  entities_total: number;
  human_entities: number;
  ai_entities: number;
  human_pct: number | null;
  ai_pct: number | null;
  by_type: Record<string, number>;
  open_issues_total: number;
  open_issues: { defects: number; change_requests: number; conflicts: number };
  critical_high_issues: number;
  /** critical_high_issues split by source: open critical/high defects vs
   *  unreviewed high/critical conflicts that detection flagged. Optional so an
   *  older payload still reads. */
  critical_high?: { defects: number; conflicts: number };
  cost_usd: number;
  project_count: number;
  // Outcome metrics. Counted from BOTH outcome models: the legacy
  // outcomes/metrics tables and the outcome_records/acceptance_criteria model
  // the product actually mounts. measured_pct is null when nothing is defined
  // yet — null means "no outcomes", NOT "nothing measured", and the UI must
  // keep those distinct.
  outcome_metrics_total: number;
  outcome_metrics_measured: number;
  outcome_metrics_measured_pct: number | null;
}
export interface KpiProjectNode {
  id: string;
  name: string;
  status: string | null;
  kpis: PortfolioKpis;
}
export interface KpiProgramNode extends KpiProjectNode {
  projects: KpiProjectNode[];
}
export interface KpiPortfolioNode extends KpiProjectNode {
  programs: KpiProgramNode[];
}
export interface PortfolioKpiRollup {
  portfolios: KpiPortfolioNode[];
  orphan_programs: KpiProgramNode[];
  unassigned_projects: KpiProjectNode[];
  org_totals: PortfolioKpis;
}

// Portfolio schedule-risk rollup (GET /portfolios/schedule-risk) — how many
// milestones the projected CPM schedule will miss + the worst slip, rolled up
// the hierarchy. Mirrors app/services/portfolio_schedule_risk_service.py.
export interface ScheduleRisk {
  milestones_total: number;
  at_risk_count: number;
  at_risk_projects: number;
  worst_slip_days: number | null;
  project_count: number;
}
export interface RiskProjectNode {
  id: string;
  name: string;
  status: string | null;
  risk: ScheduleRisk;
}
export interface RiskProgramNode extends RiskProjectNode {
  projects: RiskProjectNode[];
}
export interface RiskPortfolioNode extends RiskProjectNode {
  programs: RiskProgramNode[];
}
export interface PortfolioScheduleRiskRollup {
  portfolios: RiskPortfolioNode[];
  orphan_programs: RiskProgramNode[];
  unassigned_projects: RiskProjectNode[];
  org_totals: ScheduleRisk;
}

// The Portfolio Frontier (Bob 2026-06-10 reconception): the single thing that
// most needs the user across the whole portfolio, decision-first, with honest
// cost — not a dashboard grid.
export interface FrontierProject {
  id: string;
  name: string;
  status: string | null;
  /** portfolio › program lineage (null when the project sits unassigned). */
  path?: string | null;
  /** nearest still-open milestone (null when none scheduled). */
  next_milestone?: { name: string; target_date: string | null; status: string } | null;
  kpis: PortfolioKpis;
  attention_score: number;
  reason: string;
  decision: string;
  cost_narrative: string;
}
export interface PortfolioFrontier {
  frontier: FrontierProject | null;
  others: FrontierProject[];
  calm: boolean;
  project_count: number;
  cost: {
    org_total_usd: number;
    attributed_usd: number;
    unattributed_usd: number;
    attribution_pct: number | null;
  };
}

export const portfoliosApi = {
  list(params?: ListParams): Promise<PaginatedResponse<Portfolio>> {
    return apiClient.get<PaginatedResponse<Portfolio>>("/portfolios", cleanParams(params));
  },
  kpis(): Promise<PortfolioKpiRollup> {
    return apiClient.get<PortfolioKpiRollup>("/portfolios/kpis");
  },
  frontier(): Promise<PortfolioFrontier> {
    return apiClient.get<PortfolioFrontier>("/portfolios/frontier");
  },
  /** Ids of projects org-wide counts leave out (copies, tests, rehearsals). */
  hiddenProjects(): Promise<{ hidden_project_ids: string[] }> {
    return apiClient.get<{ hidden_project_ids: string[] }>("/portfolios/hidden-projects");
  },
  scheduleRisk(): Promise<PortfolioScheduleRiskRollup> {
    return apiClient.get<PortfolioScheduleRiskRollup>("/portfolios/schedule-risk");
  },
  get(id: string): Promise<Portfolio> {
    return apiClient.get<Portfolio>(`/portfolios/${id}`);
  },
  create(body: Partial<Portfolio>): Promise<Portfolio> {
    return apiClient.post<Portfolio>("/portfolios", body);
  },
  update(id: string, body: Partial<Portfolio>): Promise<Portfolio> {
    return apiClient.patch<Portfolio>(`/portfolios/${id}`, body);
  },
  delete(id: string): Promise<void> {
    return apiClient.delete(`/portfolios/${id}`);
  },
};

/**
 * Approval-matrix rules (WP-4.4).
 *
 * The gate itself has been enforced in production since #2776 and the authoring
 * routes since slice 2 — but the frontend had ZERO references to either, so
 * `approval_matrix_rules` held zero rows and no write was ever gated. An
 * enforced control nobody can configure is not a control.
 */
export const approvalMatrixApi = {
  listRules(activeOnly = false): Promise<{ rules: ApprovalMatrixRule[]; count: number }> {
    return apiClient.get<{ rules: ApprovalMatrixRule[]; count: number }>(
      "/approval-matrix/rules",
      cleanParams({ active_only: activeOnly }),
    );
  },
  createRule(body: ApprovalMatrixRuleInput): Promise<{ id: string; created_at: string }> {
    return apiClient.post<{ id: string; created_at: string }>(
      "/approval-matrix/rules",
      body,
    );
  },
  updateRule(id: string, body: Partial<ApprovalMatrixRuleInput>): Promise<ApprovalMatrixRule> {
    return apiClient.patch<ApprovalMatrixRule>(`/approval-matrix/rules/${id}`, body);
  },

  /** GET /approval-matrix/requests — what is waiting, and how much of it is
   *  yours to answer.
   *
   *  Without this, an org that turns on a gate blocks its own writes with no
   *  surface anywhere to unblock them: enforcement runs across nine entity
   *  modules and Felix's executor, the rules editor is reachable, and the
   *  decision path was not. */
  listRequests: (params: { status?: string; mineToDecide?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    q.set("status", params.status ?? "pending");
    if (params.mineToDecide) q.set("mine_to_decide", "true");
    q.set("limit", String(params.limit ?? 100));
    return apiClient.get<ApprovalRequestList>(`/approval-matrix/requests?${q}`);
  },

  /** "rejected", not "denied" — the server's _TERMINAL_DECISIONS. */
  decideRequest: (requestId: string, decision: "approved" | "rejected", notes?: string) =>
    apiClient.post<{ id: string; status: string }>(
      `/approval-matrix/requests/${requestId}/decide`, { decision, notes },
    ),
};

export const programsApi = {
  list(params?: ListParams & { portfolio_id?: string }): Promise<PaginatedResponse<Program>> {
    return apiClient.get<PaginatedResponse<Program>>("/programs", cleanParams(params));
  },
  get(id: string): Promise<Program> {
    return apiClient.get<Program>(`/programs/${id}`);
  },
  create(body: Partial<Program>): Promise<Program> {
    return apiClient.post<Program>("/programs", body);
  },
  update(id: string, body: Partial<Program>): Promise<Program> {
    return apiClient.patch<Program>(`/programs/${id}`, body);
  },
  delete(id: string): Promise<void> {
    return apiClient.delete(`/programs/${id}`);
  },
};

// ---------------------------------------------------------------------------
// Modules
// ---------------------------------------------------------------------------

export const modulesApi = {
  list(params?: ListParams & { module_type?: string }): Promise<PaginatedResponse<Module>> {
    return apiClient.get<PaginatedResponse<Module>>(
      "/modules",
      cleanParams(params),
    );
  },

  get(id: string): Promise<Module> {
    return apiClient.get<Module>(`/modules/${id}`);
  },

  create(body: Partial<Module>): Promise<Module> {
    return apiClient.post<Module>("/modules", body);
  },

  update(id: string, body: Partial<Module>): Promise<Module> {
    return apiClient.patch<Module>(`/modules/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/modules/${id}`);
  },

  changeStatus(
    id: string,
    body: StatusChangeRequest,
  ): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(
      `/modules/${id}/status`,
      body,
    );
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/modules/${id}/history`,
      cleanParams(params),
    );
  },
};

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

export const componentsApi = {
  list(params?: ListParams): Promise<PaginatedResponse<Component>> {
    // Trailing slash = the route; no 307 first (row e5cc6e4b).
    return apiClient.get<PaginatedResponse<Component>>(
      "/components/",
      cleanParams(params),
    );
  },

  get(id: string): Promise<Component> {
    return apiClient.get<Component>(`/components/${id}`);
  },

  create(body: Partial<Component>): Promise<Component> {
    return apiClient.post<Component>("/components", body);
  },

  update(id: string, body: Partial<Component>): Promise<Component> {
    return apiClient.patch<Component>(`/components/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/components/${id}`);
  },

  changeStatus(
    id: string,
    body: StatusChangeRequest,
  ): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(
      `/components/${id}/status`,
      body,
    );
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/components/${id}/history`,
      cleanParams(params),
    );
  },
};

// ---------------------------------------------------------------------------
// Component Specifications
// ---------------------------------------------------------------------------

export const componentSpecificationsApi = {
  list(params?: ListParams & { component_id?: string; spec_type?: string }): Promise<PaginatedResponse<ComponentSpecification>> {
    return apiClient.get<PaginatedResponse<ComponentSpecification>>(
      "/component-specifications",
      cleanParams(params),
    );
  },

  get(id: string): Promise<ComponentSpecification> {
    return apiClient.get<ComponentSpecification>(`/component-specifications/${id}`);
  },

  create(body: Partial<ComponentSpecification>): Promise<ComponentSpecification> {
    return apiClient.post<ComponentSpecification>("/component-specifications", body);
  },

  update(id: string, body: Partial<ComponentSpecification>): Promise<ComponentSpecification> {
    return apiClient.patch<ComponentSpecification>(`/component-specifications/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/component-specifications/${id}`);
  },

  changeStatus(
    id: string,
    body: StatusChangeRequest,
  ): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(
      `/component-specifications/${id}/status`,
      body,
    );
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/component-specifications/${id}/history`,
      cleanParams(params),
    );
  },
};

// ---------------------------------------------------------------------------
// Objectives
// ---------------------------------------------------------------------------

export const objectivesApi = {
  list(params?: ListParams): Promise<ObjectiveListResponse> {
    return apiClient.get<ObjectiveListResponse>(
      "/objectives",
      cleanParams(params),
    );
  },

  get(id: string): Promise<Objective> {
    return apiClient.get<Objective>(`/objectives/${id}`);
  },

  create(body: Partial<Objective>): Promise<Objective> {
    return apiClient.post<Objective>("/objectives", body);
  },

  update(id: string, body: Partial<Objective>): Promise<Objective> {
    return apiClient.patch<Objective>(`/objectives/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/objectives/${id}`);
  },

  changeStatus(
    id: string,
    body: StatusChangeRequest,
  ): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(
      `/objectives/${id}/status`,
      body,
    );
  },

  startQA(id: string): Promise<{
    workflow_run_id: string;
    workflow_id: string;
    qa_session_id: string;
    status: string;
  }> {
    return apiClient.post(`/objectives/${id}/qa/start`);
  },

  getOutcomes(id: string): Promise<{
    items: unknown[];
    total: number;
  }> {
    return apiClient.get(`/objectives/${id}/outcomes`);
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/objectives/${id}/history`,
      cleanParams(params),
    );
  },
};

// ---------------------------------------------------------------------------
// Tasks (impl_tasks)
// ---------------------------------------------------------------------------

export const tasksApi = {
  list(params?: ListParams): Promise<PaginatedResponse<Task>> {
    // The trailing slash is the route (impl_tasks.py `@router.get("/")`).
    // Without it every list was a 307 and then the real request: two round
    // trips in series, two hits on the rate limit (row e5cc6e4b, measured in
    // the access log for the Engagement Tasks page).
    return apiClient.get<PaginatedResponse<Task>>(
      "/impl-tasks/",
      cleanParams(params),
    );
  },

  get(id: string): Promise<Task> {
    return apiClient.get<Task>(`/impl-tasks/${id}`);
  },

  /** Smart-assignment: rank org users for this task by a blend of skill
   *  coverage, current availability (open WIP), and completion track record.
   *  Degrades gracefully — returns availability/record-ranked users even when
   *  the task has no recorded skill requirements. */
  recommendAssignees(taskId: string, limit = 3): Promise<import("./types").AssigneeRecommendationResponse> {
    return apiClient.get<import("./types").AssigneeRecommendationResponse>(
      `/skills/recommend/impl_task/${taskId}`,
      { limit },
    );
  },

  create(body: Partial<Task>): Promise<Task> {
    return apiClient.post<Task>("/impl-tasks", body);
  },

  update(id: string, body: Partial<Task>): Promise<Task> {
    return apiClient.patch<Task>(`/impl-tasks/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/impl-tasks/${id}`);
  },

  changeStatus(id: string, body: StatusChangeRequest): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(`/impl-tasks/${id}/status`, body);
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/impl-tasks/${id}/history`,
      cleanParams(params),
    );
  },

  bulkAssign(taskIds: string[], assignedTo: string | null): Promise<{
    updated: number;
    skipped: number;
    tasks: Array<{ id: string; title: string; assigned_to: string | null }>;
  }> {
    return apiClient.post("/impl-tasks/bulk-assign", {
      task_ids: taskIds,
      assigned_to: assignedTo,
    });
  },
};

// ---------------------------------------------------------------------------
// Test Cases
// ---------------------------------------------------------------------------

/**
 * Binding a test case to the task it verifies (row 7ce91780).
 *
 * A bound test is what counts as coverage for that task's story. Unbinding lives
 * here rather than on testCasesApi.update because the update endpoint drops null
 * fields, so it can never clear a binding.
 */
export const testBindingsApi = {
  candidates(projectId: string): Promise<{ candidates: TestBindingCandidate[]; count: number; limit: number }> {
    return apiClient.get(`/projects/${projectId}/test-bindings/candidates`);
  },

  /** `confirmed` must be true: a candidate shares a component, which is not proof. */
  bind(
    projectId: string,
    pairs: { test_case_id: string; impl_task_id: string }[],
  ): Promise<TestBindingResult> {
    return apiClient.post(`/projects/${projectId}/test-bindings`, { pairs, confirmed: true });
  },

  unbind(projectId: string, testCaseId: string): Promise<{ unbound: boolean }> {
    return apiClient.delete(`/projects/${projectId}/test-bindings/${testCaseId}`);
  },
};

export const testCasesApi = {
  list(params?: ListParams): Promise<TestCaseListResponse> {
    return apiClient.get<TestCaseListResponse>(
      "/test-cases",
      cleanParams(params),
    );
  },

  get(id: string): Promise<TestCase> {
    return apiClient.get<TestCase>(`/test-cases/${id}`);
  },

  create(body: Partial<TestCase>): Promise<TestCase> {
    return apiClient.post<TestCase>("/test-cases", body);
  },

  update(id: string, body: Partial<TestCase>): Promise<TestCase> {
    return apiClient.patch<TestCase>(`/test-cases/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/test-cases/${id}`);
  },

  changeStatus(
    id: string,
    body: StatusChangeRequest,
  ): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(
      `/test-cases/${id}/status`,
      body,
    );
  },

  /** Ask for this case to be run. 202 — the run is QUEUED, not finished.
   *
   *  Row 0f984474: every one of the 604 executions in production was written by
   *  a command line, because the product had no way to start one. A 409 means
   *  the case is not bound to an automated spec and there is genuinely nothing
   *  to run; the detail says so in words worth showing the user. */
  queueRun(id: string): Promise<QueuedTestRun> {
    return apiClient.post<QueuedTestRun>(`/test-cases/${id}/run`);
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/test-cases/${id}/history`,
      cleanParams(params),
    );
  },
};

// ---------------------------------------------------------------------------
// Test Executions
// ---------------------------------------------------------------------------

export const testExecutionsApi = {
  list(params?: ListParams): Promise<PaginatedResponse<TestExecution>> {
    return apiClient.get<PaginatedResponse<TestExecution>>(
      "/test-executions",
      cleanParams(params),
    );
  },

  get(id: string): Promise<TestExecution> {
    return apiClient.get<TestExecution>(`/test-executions/${id}`);
  },

  create(body: Partial<TestExecution>): Promise<TestExecution> {
    return apiClient.post<TestExecution>("/test-executions", body);
  },

  update(id: string, body: Partial<TestExecution>): Promise<TestExecution> {
    return apiClient.patch<TestExecution>(`/test-executions/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/test-executions/${id}`);
  },
};

// ---------------------------------------------------------------------------
// Defects
// ---------------------------------------------------------------------------

/**
 * Organisation-scoped API keys (founder decision 545b4659, 2026-09-12).
 *
 * Separate from any platform-admin key surface: these routes only ever see the
 * caller's own organisation, and the key is minted against a service account
 * rather than the signed-in person, so it is not somebody's login.
 */
export const orgApiKeysApi = {
  list(): Promise<OrgApiKey[]> {
    return apiClient.get<OrgApiKey[]>("/api-keys/");
  },

  /** The raw key comes back ONCE, in this response, and is never retrievable again. */
  create(body: {
    name: string;
    scopes: string[];
    expires_in_days: number;
  }): Promise<OrgApiKey & { raw_key: string }> {
    return apiClient.post<OrgApiKey & { raw_key: string }>("/api-keys/", body);
  },

  revoke(id: string): Promise<{ revoked: boolean }> {
    return apiClient.delete<{ revoked: boolean }>(`/api-keys/${id}`);
  },
};

export const defectsApi = {
  list(params?: ListParams): Promise<PaginatedResponse<Defect>> {
    return apiClient.get<PaginatedResponse<Defect>>(
      "/defects",
      cleanParams(params),
    );
  },

  get(id: string): Promise<Defect> {
    return apiClient.get<Defect>(`/defects/${id}`);
  },

  create(body: Partial<Defect>): Promise<Defect> {
    return apiClient.post<Defect>("/defects", body);
  },

  update(id: string, body: Partial<Defect>): Promise<Defect> {
    return apiClient.patch<Defect>(`/defects/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/defects/${id}`);
  },

  changeStatus(id: string, body: StatusChangeRequest): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(`/defects/${id}/status`, body);
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/defects/${id}/history`,
      cleanParams(params),
    );
  },
};

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export const documentsApi = {
  /** `include_content: false` omits the document body from the response. The
   *  bodies are large — the Platform User Guides are ~11.9M characters each —
   *  so any caller rendering a title and a date should pass it. */
  list(
    params?: ListParams & {
      scope?: "project" | "org";
      doc_type?: string;
      include_content?: boolean;
    },
  ): Promise<PaginatedResponse<Document>> {
    return apiClient.get<PaginatedResponse<Document>>(
      "/documents",
      cleanParams(params),
    );
  },

  get(id: string): Promise<Document> {
    return apiClient.get<Document>(`/documents/${id}`);
  },

  create(body: Partial<Document>): Promise<Document> {
    return apiClient.post<Document>("/documents", body);
  },

  update(id: string, body: Partial<Document>): Promise<Document> {
    return apiClient.patch<Document>(`/documents/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/documents/${id}`);
  },

  changeStatus(
    id: string,
    body: StatusChangeRequest,
  ): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(
      `/documents/${id}/status`,
      body,
    );
  },

  submitReview(id: string): Promise<{
    workflow_run_id: string;
    workflow_id: string;
    status: string;
  }> {
    return apiClient.post(`/documents/${id}/submit-review`);
  },

  // Records wedge (WP-2.10): issue the current APPROVED version as an
  // immutable, signed, hash-chained record.
  issue(id: string): Promise<DocumentIssuanceRecord> {
    return apiClient.post<DocumentIssuanceRecord>(`/documents/${id}/issue`);
  },

  getIssuance(id: string): Promise<{
    issued: boolean;
    record?: DocumentIssuanceRecord;
  }> {
    return apiClient.get(`/documents/${id}/issuance`);
  },

  // Verify an issued record against live state — drives the "Verified Record" badge.
  verifyIssuance(id: string): Promise<DocumentIssuanceVerdict> {
    return apiClient.get<DocumentIssuanceVerdict>(`/documents/${id}/issuance/verify`);
  },

  getDiff(id: string, version: number): Promise<DocumentVersionDiff> {
    // Backend serves the version as a PATH segment (/documents/{id}/diff/{version}),
    // not a query param — the old `/diff?version=` form 404'd on every call.
    return apiClient.get(`/documents/${id}/diff/${version}`);
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/documents/${id}/history`,
      cleanParams(params),
    );
  },

  listTemplates(scope?: "project" | "org"): Promise<{ templates: DocumentTemplate[] }> {
    const params = scope ? { scope } : undefined;
    return apiClient.get<{ templates: DocumentTemplate[] }>(
      "/documents/templates",
      params,
    );
  },

  preflight(body: {
    project_id?: string;
    doc_type: string;
  }): Promise<PreflightResult> {
    return apiClient.post<PreflightResult>("/documents/generate/preflight", body);
  },

  /** Fire-and-forget: starts generation and returns immediately with the job info.
   *  Felix will push-notify the user when the document is ready (or fails). */
  listAudiences(): Promise<{ items: DocumentAudience[] }> {
    return apiClient.get<{ items: DocumentAudience[] }>("/documents/audiences");
  },

  generate(body: GenerateDocumentRequest): Promise<{ job_id: string; status: string }> {
    return apiClient.post<{ job_id: string; status: string }>("/documents/generate", body);
  },

  /** Check the status of a running generation job (used for on-page banner). */
  generationStatus(jobId: string): Promise<{ status: string; document?: Document; error?: string }> {
    return apiClient.get<{ status: string; document?: Document; error?: string }>(
      `/documents/generate/${jobId}/status`,
    );
  },

  /** FX-01 (FB-0042): generate arbitrary LLM content (text/markdown/html)
   *  from a raw user prompt and store it as a real project document —
   *  for deliverables the built-in templates don't cover (competitive
   *  assessments, ad-hoc reports). Synchronous: returns the new document. */
  generateFreeform(body: {
    project_id?: string;
    title: string;
    prompt: string;
    format?: "text" | "markdown" | "html";
    instructions?: string;
    max_tokens?: number;
  }): Promise<Document> {
    return apiClient.post<Document>("/documents/generate-freeform", body);
  },

  listExportFormats(docType?: string): Promise<{ formats: ExportFormat[] }> {
    const params = docType ? { doc_type: docType } : undefined;
    return apiClient.get<{ formats: ExportFormat[] }>(
      "/documents/export/formats",
      params,
    );
  },

  getExportUrl(documentId: string, format: string): string {
    const base = import.meta.env.VITE_API_URL ?? "/api/v1";
    return `${base}/documents/${documentId}/export/${format}`;
  },

  listSchedules(projectId?: string): Promise<{ schedules: DocumentSchedule[] }> {
    const params = projectId ? { project_id: projectId } : undefined;
    return apiClient.get<{ schedules: DocumentSchedule[] }>(
      "/documents/schedules/list",
      params,
    );
  },

  createSchedule(body: {
    project_id: string;
    doc_type: string;
    interval_hours?: number;
    custom_instructions?: string;
  }): Promise<DocumentSchedule> {
    return apiClient.post<DocumentSchedule>("/documents/schedules", body);
  },

  updateSchedule(
    id: string,
    body: { interval_hours?: number; custom_instructions?: string; status?: string },
  ): Promise<DocumentSchedule> {
    return apiClient.patch<DocumentSchedule>(`/documents/schedules/${id}`, body);
  },

  deleteSchedule(id: string): Promise<void> {
    return apiClient.delete(`/documents/schedules/${id}`);
  },

  regenerate(
    id: string,
    body?: RegenerateDocumentRequest,
  ): Promise<{ job_id: string; status: string; document_id: string }> {
    return apiClient.post(`/documents/${id}/regenerate`, body ?? {});
  },

  listVersions(id: string): Promise<{
    versions: DocumentVersion[];
    current_document_id: string;
  }> {
    return apiClient.get(`/documents/${id}/versions`);
  },

  getLifetimeCost(id: string): Promise<DocumentLifetimeCost> {
    return apiClient.get<DocumentLifetimeCost>(`/documents/${id}/lifetime-cost`);
  },
};

// ---------------------------------------------------------------------------
// Ad-hoc Reports (conversational report builder)
// ---------------------------------------------------------------------------

import type {
  AdHocReportSession,
  AdHocReportTemplate,
  AdHocReportGenerated,
  AdHocReportSlot,
  AdHocReportVisualization,
} from "./types";

export const adHocReportsApi = {
  /** Start a new chat session for the report builder. */
  startSession(body: {
    project_id?: string | null;
    template_id?: string | null;
  }): Promise<AdHocReportSession> {
    return apiClient.post<AdHocReportSession>(
      "/ad-hoc-reports/sessions",
      body,
    );
  },

  /** Send a user turn and read the SSE stream back as a single
   *  resolved payload. Wraps EventSource-style parsing so callers
   *  get the same shape they would from a plain fetch. */
  async chat(
    sessionId: string,
    message: string,
    onProgress?: (event: { type: string; status?: string }) => void,
  ): Promise<AdHocReportSession> {
    const base = import.meta.env.VITE_API_URL ?? "/api/v1";
    const url = `${base}/ad-hoc-reports/sessions/${sessionId}/chat`;
    const makeHeaders = () => {
      const t = getAccessToken();
      return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
    };
    let response = await fetch(url, {
      method: "POST",
      headers: makeHeaders(),
      body: JSON.stringify({ message }),
    });
    // KI-0110 pattern: ad-hoc report chat is also a raw-fetch SSE endpoint.
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        response = await fetch(url, { method: "POST", headers: makeHeaders(), body: JSON.stringify({ message }) });
      }
      if (!response.ok) {
        clearTokens();
        window.location.href = "/login";
        throw new Error("Session expired. Please log in again.");
      }
    }
    if (!response.ok || !response.body) {
      throw new Error(`Chat failed: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let resolved: AdHocReportSession | null = null;
    let errorPayload: { code?: string; error?: string } | null = null;

    // Minimal SSE parser: split on \n\n, parse event:/data: pairs.
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let blankIndex = buffer.indexOf("\n\n");
      while (blankIndex !== -1) {
        const raw = buffer.slice(0, blankIndex);
        buffer = buffer.slice(blankIndex + 2);

        let eventName = "message";
        let dataLine = "";
        for (const line of raw.split("\n")) {
          if (line.startsWith("event: ")) eventName = line.slice(7).trim();
          else if (line.startsWith("data: ")) dataLine = line.slice(6);
        }
        if (dataLine) {
          try {
            const parsed = JSON.parse(dataLine);
            if (eventName === "progress" && onProgress) onProgress(parsed);
            else if (eventName === "message") resolved = parsed as AdHocReportSession;
            else if (eventName === "error") errorPayload = parsed;
          } catch {
            // ignore malformed events — heartbeat / keepalives
          }
        }
        blankIndex = buffer.indexOf("\n\n");
      }
    }

    if (errorPayload) {
      const err = new Error(errorPayload.error ?? "Unknown error");
      (err as { code?: string }).code = errorPayload.code;
      throw err;
    }
    if (!resolved) throw new Error("Chat stream closed without a message");
    return resolved;
  },

  /** Render the final report. */
  generate(
    sessionId: string,
    body?: {
      save_as_template?: boolean;
      template_name?: string;
      template_description?: string | null;
      template_scope?: "user" | "org";
    },
  ): Promise<AdHocReportGenerated> {
    return apiClient.post<AdHocReportGenerated>(
      `/ad-hoc-reports/sessions/${sessionId}/generate`,
      body ?? {},
    );
  },

  /** List templates visible to the current user. */
  listTemplates(scope: "mine" | "org" | "all" = "all"): Promise<{
    templates: AdHocReportTemplate[];
    total: number;
  }> {
    return apiClient.get<{ templates: AdHocReportTemplate[]; total: number }>(
      "/ad-hoc-reports/templates",
      { scope },
    );
  },

  getTemplate(id: string): Promise<AdHocReportTemplate> {
    return apiClient.get<AdHocReportTemplate>(`/ad-hoc-reports/templates/${id}`);
  },

  saveTemplate(body: {
    name: string;
    description?: string | null;
    scope: "user" | "org";
    prompt_template: string;
    parameters: AdHocReportSlot[];
    output_format?: "markdown" | "html" | "both";
    visualization_hints?: AdHocReportVisualization[];
  }): Promise<AdHocReportTemplate> {
    return apiClient.post<AdHocReportTemplate>(
      "/ad-hoc-reports/templates",
      body,
    );
  },

  deleteTemplate(id: string): Promise<void> {
    return apiClient.delete(`/ad-hoc-reports/templates/${id}`);
  },
};

// ---------------------------------------------------------------------------
// Change Requests
// ---------------------------------------------------------------------------

export const changeRequestsApi = {
  list(params?: ListParams): Promise<PaginatedResponse<ChangeRequest>> {
    return apiClient.get<PaginatedResponse<ChangeRequest>>(
      "/change-requests",
      cleanParams(params),
    );
  },

  get(id: string): Promise<ChangeRequest> {
    return apiClient.get<ChangeRequest>(`/change-requests/${id}`);
  },

  /** Row aabced55. 404 while BLAST_TREE is off. */
  impactTree(ids: string[]): Promise<ImpactTree> {
    return apiClient.get<ImpactTree>("/change-requests/impact-tree", { ids: ids.join(",") });
  },

  create(body: Partial<ChangeRequest>): Promise<ChangeRequest> {
    return apiClient.post<ChangeRequest>("/change-requests", body);
  },

  update(id: string, body: Partial<ChangeRequest>): Promise<ChangeRequest> {
    return apiClient.patch<ChangeRequest>(`/change-requests/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/change-requests/${id}`);
  },

  changeStatus(
    id: string,
    body: StatusChangeRequest,
  ): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(
      `/change-requests/${id}/status`,
      body,
    );
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/change-requests/${id}/history`,
      cleanParams(params),
    );
  },

  /** Row ace51a9b: re-run the stored impact assessment on today's graph and
   * the project's active baseline. 422 with no target; 409 once decided. */
  reassess(id: string): Promise<ChangeRequest> {
    return apiClient.post<ChangeRequest>(`/change-requests/${id}/reassess`);
  },
};

// ---------------------------------------------------------------------------
// Baselines
// ---------------------------------------------------------------------------

export const baselinesApi = {
  list(params?: ListParams): Promise<PaginatedResponse<Baseline>> {
    return apiClient.get<PaginatedResponse<Baseline>>(
      "/baselines",
      cleanParams(params),
    );
  },

  get(id: string): Promise<Baseline> {
    return apiClient.get<Baseline>(`/baselines/${id}`);
  },

  create(body: Partial<Baseline>): Promise<Baseline> {
    return apiClient.post<Baseline>("/baselines", body);
  },

  update(id: string, body: Partial<Baseline>): Promise<Baseline> {
    return apiClient.patch<Baseline>(`/baselines/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/baselines/${id}`);
  },

  changeStatus(
    id: string,
    body: StatusChangeRequest,
  ): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(
      `/baselines/${id}/status`,
      body,
    );
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/baselines/${id}/history`,
      cleanParams(params),
    );
  },

  getSnapshot(id: string): Promise<Baseline> {
    return apiClient.get<Baseline>(`/baselines/${id}/snapshot`);
  },

  /** Rows 994693df / 442010f0: named two-party sign-off, and restore. */
  listSignoffs(id: string): Promise<{
    items: BaselineSignoff[];
    status: { complete: boolean; missing: string[]; same_signer: boolean; parties: Record<string, unknown> };
    required: boolean;
  }> {
    return apiClient.get(`/baselines/${id}/signoffs`);
  },

  signOff(
    id: string,
    body: { party: "client" | "delivery"; decision?: "APPROVED" | "REJECTED"; note?: string },
  ): Promise<{ signoff: BaselineSignoff; status: Record<string, unknown> }> {
    return apiClient.post(`/baselines/${id}/signoffs`, body);
  },

  restore(id: string): Promise<BaselineRestoreSummary> {
    return apiClient.post<BaselineRestoreSummary>(`/baselines/${id}/restore`);
  },
};

// ---------------------------------------------------------------------------
// Change Request Scope Violation & Impact Analysis
// ---------------------------------------------------------------------------

export const changeRequestScopeApi = {
  fromScopeViolation(body: ScopeViolationRequest): Promise<ChangeRequest> {
    return apiClient.post<ChangeRequest>("/change-requests/from-scope-violation", body);
  },

  analyzeImpact(body: {
    entity_type: string;
    entity_id: string;
    change_description: string;
    project_id: string;
  }): Promise<ImpactAnalysisResult> {
    return apiClient.post<ImpactAnalysisResult>("/change-requests/analyze-impact", body);
  },

  approve(id: string, body: { approval_notes?: string; unlock_entity?: boolean }): Promise<ChangeRequest & { entity_unlocked: boolean }> {
    return apiClient.post<ChangeRequest & { entity_unlocked: boolean }>(`/change-requests/${id}/approve`, body);
  },

};

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export interface AuditLedgerIntegrity {
  /** True only when every anchor that HAS a predecessor verified against it.
   *  False when nothing is anchored at all — absent and intact are different
   *  answers, and only one should reassure a customer. */
  verified: boolean;
  anchor_count: number;
  broken_anchor_count: number;
  first_anchored_at: string | null;
  last_anchored_at: string | null;
  anchored_through: string | null;
  /** Past breaks a re-verification explained by reproducing the anchor's
   *  stored digest (row 30799be4). Not in broken_anchor_count; shown, never
   *  hidden. */
  reverified_breaks: { failed_on: string; cause: string }[];
}

export interface MeetingPlatformAvailability {
  platform: string;
  label: string;
  /** false when this deployment cannot actually create or join a meeting on it.
   *  Rows 923b3637 / 894b511a: Teams and Zoom are offered in three dialogs and
   *  the meeting is reported as created, but the one TEAMS and the one ZOOM
   *  meeting ever created both have no meeting_url and no platform_meeting_id. */
  available: boolean;
  missing_settings: string[];
  reason: string | null;
}

export const meetingPlatformsApi = {
  availability(): Promise<{ platforms: MeetingPlatformAvailability[] }> {
    return apiClient.get("/meetings/platform-availability");
  },
};

export const auditApi = {
  /** Ledger integrity attestation (row e5659a13). Carries NO count of ledger
   *  rows: audit_ledger_anchors is platform-wide and its row_count is every
   *  tenant's activity summed, which is a business metric rather than this
   *  tenant's data. */
  getIntegrity(): Promise<AuditLedgerIntegrity> {
    return apiClient.get<AuditLedgerIntegrity>("/audit/integrity");
  },

  getTimeline(
    entityType: string,
    entityId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    // Backend route is /audit/entity/{type}/{id}/timeline (audit.py
    // get_entity_timeline). The old /audit/timeline/{type}/{id} path was never
    // registered, so the entity audit timeline 404'd in the UI.
    return apiClient.get<PaginatedResponse<unknown>>(
      `/audit/entity/${entityType}/${entityId}/timeline`,
      cleanParams(params),
    );
  },

  getProjectAudit(
    projectId: string,
    params?: ListParams,
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/audit/project/${projectId}`,
      cleanParams(params),
    );
  },
};

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export const usersApi = {
  list(params?: ListParams): Promise<PaginatedResponse<User>> {
    // Trailing slash = the route; no 307 first (row e5cc6e4b).
    return apiClient.get<PaginatedResponse<User>>("/users/", cleanParams(params));
  },

  get(id: string): Promise<User> {
    return apiClient.get<User>(`/users/${id}`);
  },

  // role_id is REQUIRED: the server refuses an invite that names no role
  // (row c1b23e5d) -- there is no default role.
  invite(body: {
    email: string;
    display_name?: string;
    role_id: string;
  }): Promise<{ user: User; invite_sent: boolean }> {
    return apiClient.post<{ user: User; invite_sent: boolean }>("/users/invite", body);
  },

  resendInvite(userId: string): Promise<{ invite_sent: boolean; email: string }> {
    return apiClient.post<{ invite_sent: boolean; email: string }>(
      `/users/${userId}/resend-invite`,
      {},
    );
  },

  seats(): Promise<{
    used: number;
    limit: number | null;
    unlimited: boolean;
    has_seat: boolean;
  }> {
    return apiClient.get("/users/seats");
  },

  update(id: string, body: Partial<User>): Promise<User> {
    return apiClient.put<User>(`/users/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/users/${id}`);
  },

  assignRole(
    userId: string,
    roleId: string,
  ): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(`/users/${userId}/roles`, {
      role_id: roleId,
    });
  },

  removeRole(
    userId: string,
    roleId: string,
  ): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(
      `/users/${userId}/roles/${roleId}`,
    );
  },
};

// ---------------------------------------------------------------------------
// Roles (admin)
// ---------------------------------------------------------------------------

export const rolesApi = {
  list(): Promise<{ items: Role[]; members_total?: number }> {
    return apiClient.get<{ items: Role[]; members_total?: number }>("/admin/roles");
  },

  create(body: {
    role_code: string;
    display_name: string;
    description?: string;
  }): Promise<Role> {
    return apiClient.post<Role>("/admin/roles", {
      ...body,
      description: body.description ?? "",
    });
  },

  update(roleId: string, body: {
    role_code: string;
    display_name: string;
    description?: string;
  }): Promise<Role> {
    return apiClient.put<Role>(`/admin/roles/${roleId}`, {
      ...body,
      description: body.description ?? "",
    });
  },

  assign(
    roleId: string,
    userId: string,
  ): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>("/admin/roles/assign", {
      role_id: roleId,
      user_id: userId,
    });
  },

  delete(roleId: string): Promise<void> {
    return apiClient.delete(`/admin/roles/${roleId}`);
  },
};

// ---------------------------------------------------------------------------
// Status Config (admin)
// ---------------------------------------------------------------------------

export const statusConfigApi = {
  list(): Promise<{ items: StatusConfig[] }> {
    return apiClient.get<{ items: StatusConfig[] }>("/admin/status-config");
  },

  create(body: Partial<StatusConfig>): Promise<StatusConfig> {
    return apiClient.post<StatusConfig>("/admin/status-config", body);
  },

  /** The statuses an entity type can actually hold — the vocabulary, not the
   *  transition pairs. Anything offering a status list to a user should read
   *  this rather than hardcoding, so the offer cannot drift from what the
   *  status engine enforces. */
  registry(entityType: string): Promise<{ items: StatusRegistryEntry[] }> {
    return apiClient.get<{ items: StatusRegistryEntry[] }>(
      "/admin/status-config/registry",
      { entity_type: entityType },
    );
  },

  getTransitions(
    entityType: string,
    fromStatus: string,
  ): Promise<{ transitions: StatusConfig[] }> {
    return apiClient.get<{ transitions: StatusConfig[] }>(
      `/admin/status-config/transitions`,
      { entity_type: entityType, from_status: fromStatus },
    );
  },
};

// ---------------------------------------------------------------------------
// Settings (admin)
// ---------------------------------------------------------------------------

export const settingsApi = {
  list(): Promise<{ items: Setting[] }> {
    return apiClient.get<{ items: Setting[] }>("/admin/settings");
  },

  getByCategory(category: string): Promise<{ category: string; items: Setting[] }> {
    return apiClient.get<{ category: string; items: Setting[] }>(
      `/admin/settings/${category}`,
    );
  },

  update(settingId: string, value: unknown): Promise<Setting> {
    return apiClient.put<Setting>(`/admin/settings/${settingId}`, { value });
  },

  exportSettings(projectId?: string): Promise<{
    org_id: string;
    project_id: string | null;
    settings: Record<string, Record<string, unknown>>;
  }> {
    return apiClient.get("/admin/settings/export", cleanParams({ project_id: projectId }));
  },

  importSettings(body: {
    settings: Record<string, Record<string, unknown>>;
    project_id?: string;
    preview?: boolean;
  }): Promise<{
    status: string;
    changes_applied?: number;
    changes: unknown[];
  }> {
    return apiClient.post("/admin/settings/import", body);
  },

  resetOverride(settingId: string): Promise<{ status: string; setting_id: string }> {
    return apiClient.delete(`/admin/settings/${settingId}/override`);
  },
};

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export const notificationsApi = {
  list(
    params?: NotificationListParams,
  ): Promise<PaginatedResponse<Notification>> {
    return apiClient.get<PaginatedResponse<Notification>>(
      "/notifications",
      cleanParams(params),
    );
  },

  markRead(id: string): Promise<Notification> {
    return apiClient.post<Notification>(`/notifications/${id}/read`);
  },

  dismiss(id: string): Promise<Notification> {
    return apiClient.post<Notification>(`/notifications/${id}/dismiss`);
  },

  markAllRead(): Promise<{ marked_read: number }> {
    return apiClient.post<{ marked_read: number }>("/notifications/mark-all-read");
  },
};

// ---------------------------------------------------------------------------
// Webhook endpoints (Delivery UX Phase 1.7)
// ---------------------------------------------------------------------------

export const webhookEndpointsApi = {
  list(): Promise<WebhookEndpoint[]> {
    return apiClient.get<WebhookEndpoint[]>("/webhook-endpoints");
  },

  get(id: string): Promise<WebhookEndpoint> {
    return apiClient.get<WebhookEndpoint>(`/webhook-endpoints/${id}`);
  },

  /** Returns the full endpoint INCLUDING the secret — caller must
   * surface it to the user immediately, as subsequent reads omit it. */
  create(body: WebhookEndpointCreate): Promise<WebhookEndpoint> {
    return apiClient.post<WebhookEndpoint>("/webhook-endpoints", body);
  },

  update(id: string, body: WebhookEndpointUpdate): Promise<WebhookEndpoint> {
    return apiClient.patch<WebhookEndpoint>(`/webhook-endpoints/${id}`, body);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete<void>(`/webhook-endpoints/${id}`);
  },

  test(id: string): Promise<{ delivered: boolean }> {
    return apiClient.post<{ delivered: boolean }>(
      `/webhook-endpoints/${id}/test`,
    );
  },

  deliveries(id: string, limit = 50): Promise<WebhookDelivery[]> {
    return apiClient.get<WebhookDelivery[]>(
      `/webhook-endpoints/${id}/deliveries`,
      { limit },
    );
  },
};

// ---------------------------------------------------------------------------
// Project secrets vault (Delivery UX Phase 1.12)
// ---------------------------------------------------------------------------

export const projectSecretsApi = {
  list(projectId: string): Promise<ProjectSecret[]> {
    return apiClient.get<ProjectSecret[]>(`/projects/${projectId}/secrets`);
  },

  /** Create or overwrite by key_name. Server never echoes the value. */
  upsert(
    projectId: string,
    body: ProjectSecretWrite,
  ): Promise<{ id: string; key_name: string }> {
    return apiClient.post<{ id: string; key_name: string }>(
      `/projects/${projectId}/secrets`,
      body,
    );
  },

  rotate(
    projectId: string,
    keyName: string,
    value: string,
  ): Promise<{ rotated: boolean; key_name: string }> {
    return apiClient.post<{ rotated: boolean; key_name: string }>(
      `/projects/${projectId}/secrets/${keyName}/rotate`,
      { value },
    );
  },

  delete(projectId: string, keyName: string): Promise<void> {
    return apiClient.delete<void>(`/projects/${projectId}/secrets/${keyName}`);
  },
};

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export const metricsApi = {
  list(params?: ListParams): Promise<PaginatedResponse<Metric>> {
    return apiClient.get<PaginatedResponse<Metric>>(
      "/metrics",
      cleanParams(params),
    );
  },

  get(id: string): Promise<Metric> {
    return apiClient.get<Metric>(`/metrics/${id}`);
  },
};

// ---------------------------------------------------------------------------
// Personas
// ---------------------------------------------------------------------------

export const personasApi = {
  list(): Promise<{ items: Persona[] }> {
    return apiClient.get<{ items: Persona[] }>("/personas");
  },

  getVersions(personaCode: string): Promise<{ items: unknown[] }> {
    return apiClient.get<{ items: unknown[] }>(
      `/personas/${personaCode}/versions`,
    );
  },

  createVersion(
    personaCode: string,
    body: { system_prompt: string; description?: string },
  ): Promise<unknown> {
    return apiClient.post(`/personas/${personaCode}/versions`, body);
  },
};

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export const healthApi = {
  assess(body: { project_id: string }): Promise<HealthAssessment> {
    return apiClient.post<HealthAssessment>("/health/assess", body);
  },

  getLatest(projectId?: string): Promise<HealthAssessment> {
    return apiClient.get<HealthAssessment>(
      "/health/latest",
      cleanParams({ project_id: projectId }),
    );
  },
};

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------

export const workflowsApi = {
  getRegistry(): Promise<{ workflows: WorkflowDefinition[] }> {
    return apiClient.get<{ workflows: WorkflowDefinition[] }>(
      "/workflows/registry",
    );
  },

  trigger(body: WorkflowTriggerRequest): Promise<{
    workflow_run_id: string;
    workflow_id: string;
    status: string;
  }> {
    return apiClient.post("/workflows/trigger", body);
  },

  listRuns(
    params?: WorkflowRunListParams,
  ): Promise<PaginatedResponse<WorkflowRun>> {
    return apiClient.get<PaginatedResponse<WorkflowRun>>(
      "/workflows/runs",
      cleanParams(params),
    );
  },

  getStatus(runId: string): Promise<WorkflowRun> {
    return apiClient.get<WorkflowRun>(`/workflows/runs/${runId}`);
  },

  resume(
    runId: string,
    body: WorkflowResumeRequest,
  ): Promise<unknown> {
    return apiClient.post(`/workflows/runs/${runId}/resume`, body);
  },

  cancel(
    runId: string,
    body: WorkflowCancelRequest,
  ): Promise<unknown> {
    return apiClient.post(`/workflows/runs/${runId}/cancel`, body);
  },
};

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export const reportsApi = {
  list(params?: ListParams): Promise<PaginatedResponse<Report>> {
    return apiClient.get<PaginatedResponse<Report>>(
      "/reports",
      cleanParams(params),
    );
  },

  /** Download a generated report as a file (pdf/docx/json). */
  export(reportId: string, format: "pdf" | "docx" | "json"): Promise<{ blob: Blob; filename: string }> {
    return apiClient.blob(`/reports/${reportId}/export?format=${format}`);
  },

  generate(body: {
    project_id: string;
    report_type: string;
    title?: string;
    parameters?: Record<string, unknown>;
  }): Promise<Report> {
    return apiClient.post<Report>("/reports/generate", body);
  },
};

// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------

export const billingApi = {
  getSubscription(): Promise<{ subscription: Subscription | null }> {
    return apiClient.get<{ subscription: Subscription | null }>(
      "/billing/subscription",
    );
  },

  /** GET /billing/invoices — the org's Stripe invoices with hosted pay/PDF
   *  links. ORG_ADMIN only. `configured:false` = Stripe not wired; `true` +
   *  empty = no invoices yet. */
  getInvoices(limit = 12): Promise<InvoiceListResponse> {
    return apiClient.get<InvoiceListResponse>(
      "/billing/invoices",
      cleanParams({ limit }),
    );
  },

  checkout(
    planCode: string,
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkout_url: string }> {
    // Backend reads plan_code / success_url / cancel_url as QUERY params
    // (POST /billing/checkout?plan_code=...). Passing an empty body previously
    // meant no plan_code ever reached the server (422) — the reason the
    // Billing "Upgrade" CTA never produced a Stripe session.
    const params = new URLSearchParams({ plan_code: planCode });
    if (successUrl) params.set("success_url", successUrl);
    if (cancelUrl) params.set("cancel_url", cancelUrl);
    return apiClient.post<{ checkout_url: string }>(
      `/billing/checkout?${params.toString()}`,
      undefined,
    );
  },

  /** Add-ons on sale, with prices and whether this org already holds each.
   *  Row 43927708: the product told customers to "add the Design add-on" and
   *  linked here, and there were no add-ons on the page because nothing served
   *  them. `org_has` is what lets the page say "Included" rather than offering
   *  to sell someone something they already have — which matters today, since
   *  99 of 100 orgs hold DESIGN as a comp. */
  getAddons(): Promise<{ items: import("@/api/types").Addon[] }> {
    return apiClient.get<{ items: import("@/api/types").Addon[] }>("/billing/addons");
  },

  /** Start a Stripe checkout for one add-on. The grant happens on the webhook,
   *  never on this call. */
  createAddonCheckout(addonCode: string): Promise<{ checkout_url: string; session_id: string }> {
    return apiClient.post<{ checkout_url: string; session_id: string }>(
      `/billing/addons/${addonCode}/checkout`,
    );
  },

  getPlans(): Promise<{ plans: Plan[] }> {
    return apiClient.get<{ plans: Plan[] }>("/billing/plans");
  },

  /** GET /billing/public/plans — UNAUTHENTICATED plan tiers for the marketing
   *  /pricing page. Same source as getPlans so the two never drift. */
  getPublicPlans(): Promise<{ plans: Plan[] }> {
    return apiClient.get<{ plans: Plan[] }>("/billing/public/plans");
  },

  changePlan(planCode: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>("/billing/change-plan", {
      plan_code: planCode,
    });
  },

  /** POST /billing/subscription/cancel — cancel at period end (graceful; access
   *  continues until current_period_end). ORG_ADMIN only. */
  cancelSubscription(): Promise<{ cancel_at_period_end: boolean; current_period_end?: string | null }> {
    return apiClient.post("/billing/subscription/cancel", undefined);
  },

  /** POST /billing/portal — a Stripe Billing Portal session URL. ORG_ADMIN only.
   *
   *  THE ONLY WAY TO UPDATE A FAILED OR EXPIRING CARD. FTL shipped no
   *  payment-method surface at all, so a customer whose card failed could only
   *  buy a second subscription or cancel — while dunning emails instructed them
   *  to "update your payment method from Settings → Billing".
   *
   *  No URLs are passed: the return URL is fixed server-side to this
   *  deployment's /billing, so an authenticated billing action can never be
   *  turned into an attacker-chosen redirect. */
  openBillingPortal(): Promise<{ portal_url: string }> {
    return apiClient.post<{ portal_url: string }>("/billing/portal", undefined);
  },

  /** POST /billing/subscription/reactivate — undo a pending cancellation. ORG_ADMIN only. */
  reactivateSubscription(): Promise<{ cancel_at_period_end: boolean }> {
    return apiClient.post("/billing/subscription/reactivate", undefined);
  },

  getUsage(resource?: string): Promise<UsageInfo> {
    return apiClient.get<UsageInfo>(
      "/billing/usage",
      cleanParams({ resource }),
    );
  },

  getCredits(): Promise<{ balance: CreditBalance; summary: CreditUsageSummary }> {
    return apiClient.get<{ balance: CreditBalance; summary: CreditUsageSummary }>(
      "/billing/credits",
    );
  },

  getCreditHistory(
    limit?: number,
    offset?: number,
    transactionType?: string,
  ): Promise<{ items: CreditTransaction[]; total: number; limit: number; offset: number }> {
    return apiClient.get<{ items: CreditTransaction[]; total: number; limit: number; offset: number }>(
      "/billing/credits/history",
      cleanParams({ limit, offset, transaction_type: transactionType }),
    );
  },

  purchaseCredits(
    amount: number,
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ checkout_url?: string; status?: string; credits_added?: number }> {
    // Buy a prepaid AI-credit pack via the ORG_ADMIN-open, Stripe-payment-verified
    // endpoint. This previously POSTed to /billing/credits/purchase, which is
    // gated to platform admins (require_platform_admin) and 403'd for every real
    // customer. /billing/credits/checkout reads amount/success_url/cancel_url as
    // QUERY params and returns a Stripe checkout_url (or, in manual/dev mode, the
    // granted balance).
    const params = new URLSearchParams({ amount: String(amount) });
    if (successUrl) params.set("success_url", successUrl);
    if (cancelUrl) params.set("cancel_url", cancelUrl);
    return apiClient.post<{ checkout_url?: string; status?: string; credits_added?: number }>(
      `/billing/credits/checkout?${params.toString()}`,
      undefined,
    );
  },

  getCreditPricing(): Promise<CreditPricing> {
    return apiClient.get<CreditPricing>("/billing/credits/pricing");
  },
};

// ---------------------------------------------------------------------------
// QA Sessions
// ---------------------------------------------------------------------------

export interface QASessionListParams {
  project_id?: string;
  entity_type?: string;
  entity_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

export const qaSessionsApi = {
  list(params?: QASessionListParams): Promise<PaginatedResponse<QASession>> {
    return apiClient.get<PaginatedResponse<QASession>>("/qa/sessions", cleanParams(params));
  },

  get(sessionId: string): Promise<QASessionDetail> {
    return apiClient.get<QASessionDetail>(`/qa/sessions/${sessionId}`);
  },

  submitAnswer(sessionId: string, answer: string): Promise<QAAnswerSavedResponse> {
    return apiClient.post<QAAnswerSavedResponse>(`/qa/sessions/${sessionId}/answer`, { answer });
  },

  editInteraction(
    sessionId: string,
    interactionId: string,
    answer: string,
  ): Promise<QAAnswerResponse> {
    return apiClient.post<QAAnswerResponse>(
      `/qa/sessions/${sessionId}/edit/${interactionId}`,
      { answer },
    );
  },

  getConflicts(sessionId: string): Promise<{
    session_id: string;
    conflicts: Array<{ rounds: number[]; description: string; severity: string }>;
    consistency_score: number;
  }> {
    return apiClient.get(`/qa/sessions/${sessionId}/conflicts`);
  },

  getImpact(sessionId: string): Promise<{
    session_id: string;
    priority_areas: unknown[];
    gaps: unknown[];
    recommended_dimension: string;
  }> {
    return apiClient.get(`/qa/sessions/${sessionId}/impact`);
  },

  /** Approve an entity after Q&A completion (IN_REVIEW → APPROVED) */
  approve(sessionId: string): Promise<{ status: string; entity_type: string; entity_id: string }> {
    return apiClient.post(`/qa/sessions/${sessionId}/approve`, {});
  },

  /** Reject an entity after Q&A completion (IN_REVIEW → DRAFT for rework) */
  reject(sessionId: string): Promise<{ status: string; entity_type: string; entity_id: string }> {
    return apiClient.post(`/qa/sessions/${sessionId}/reject`, {});
  },
};

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

export const provenanceApi = {
  get(
    entityType: string,
    entityId: string,
    params?: {
      include_qa_history?: boolean;
      include_audit_trail?: boolean;
      include_upstream?: boolean;
      include_downstream?: boolean;
    },
  ): Promise<ProvenanceResult> {
    return apiClient.get<ProvenanceResult>(
      `/provenance/${entityType}/${entityId}`,
      cleanParams(params),
    );
  },

  getAuditTrail(entityType: string, entityId: string): Promise<AuditTrail> {
    return apiClient.get<AuditTrail>(`/provenance/${entityType}/${entityId}/audit-trail`);
  },

  buildAuditTrail(entityType: string, entityId: string): Promise<AuditTrail> {
    return apiClient.post<AuditTrail>(`/provenance/${entityType}/${entityId}/audit-trail`, {});
  },

  getLineageMap(
    entityType: string,
    entityId: string,
    params?: { max_depth?: number },
  ): Promise<LineageMap> {
    return apiClient.get<LineageMap>(
      `/provenance/${entityType}/${entityId}/lineage-map`,
      cleanParams(params),
    );
  },
};

// ---------------------------------------------------------------------------
// Traceability Links
// ---------------------------------------------------------------------------

export const traceabilityLinksApi = {
  get(entityType: string, entityId: string, params?: {
    depth?: number;
    direction?: string;
    limit?: number;
  }): Promise<{
    entity_type: string;
    entity_id: string;
    upstream: unknown[];
    downstream: unknown[];
    truncated: boolean;
  }> {
    return apiClient.get(
      `/traceability/${entityType}/${entityId}`,
      cleanParams(params),
    );
  },

  create(body: {
    source_entity_type: string;
    source_entity_id: string;
    target_entity_type: string;
    target_entity_id: string;
    link_type?: string;
  }): Promise<unknown> {
    return apiClient.post("/traceability/links", body);
  },

  delete(linkId: string): Promise<{ id: string; deleted: boolean }> {
    return apiClient.delete(`/traceability/links/${linkId}`);
  },

  /** Get a graph-format neighborhood for inline traceability visualization. */
  getGraph(entityType: string, entityId: string, depth: number = 2): Promise<{
    format: string;
    root: string;
    nodes: { id: string; entity_type: string; entity_id: string; is_root?: boolean; label?: string }[];
    edges: { id: string; source: string; target: string; depth?: number }[];
    node_count: number;
    edge_count: number;
    truncated: boolean;
  }> {
    return apiClient.get(
      `/traceability/${entityType}/${entityId}`,
      { format: "graph", depth },
    );
  },
};

// ---------------------------------------------------------------------------
// Entity Conflicts (universal)
// ---------------------------------------------------------------------------

import type {
  EntityConflict,
  UserProfile,
  Meeting,
  EndMeetingResponse,
  MeetingActionItem,
  MeetingMinutes,
  Sprint,
  SprintItem,
  BurndownSnapshot,
  Milestone as MilestoneType,
  DocumentImport,
  DocumentImportItem,
  DocumentImportProgress,
  DocumentTypeInfo,
  ProjectDocumentPlan,
} from "./types";

export const conflictsApi = {
  listForProject(projectId: string, params?: {
    status?: string;
    severity?: string;
    entity_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<EntityConflict>> {
    return apiClient.get<PaginatedResponse<EntityConflict>>(
      `/conflicts/project/${projectId}`,
      cleanParams(params),
    );
  },

  get(id: string): Promise<EntityConflict> {
    return apiClient.get<EntityConflict>(`/conflicts/${id}`);
  },

  listForEntity(entityType: string, entityId: string, params?: {
    status?: string;
  }): Promise<{ items: EntityConflict[]; total: number }> {
    return apiClient.get(
      `/conflicts/entity/${entityType}/${entityId}`,
      cleanParams(params),
    );
  },

  update(id: string, body: { status?: string }): Promise<EntityConflict> {
    return apiClient.patch<EntityConflict>(`/conflicts/${id}`, body);
  },

  resolve(id: string, body: {
    resolution_type: string;
    resolution_notes?: string;
  }): Promise<EntityConflict> {
    return apiClient.post<EntityConflict>(`/conflicts/${id}/resolve`, body);
  },

  detect(entityType: string, entityId: string): Promise<{
    entity_type: string;
    entity_id: string;
    conflicts_detected: number;
    conflicts: EntityConflict[];
  }> {
    return apiClient.post(`/conflicts/detect/${entityType}/${entityId}`);
  },
};

// ---------------------------------------------------------------------------
// User Profiles
// ---------------------------------------------------------------------------

export const userProfilesApi = {
  getMyProfile(): Promise<UserProfile> {
    return apiClient.get<UserProfile>("/user-profiles/me");
  },

  getProfile(userId: string): Promise<UserProfile> {
    return apiClient.get<UserProfile>(`/user-profiles/${userId}`);
  },

  updateMyProfile(body: Partial<UserProfile>): Promise<UserProfile> {
    return apiClient.put<UserProfile>("/user-profiles/me", body);
  },

  listTeam(): Promise<{ items: UserProfile[]; total: number }> {
    return apiClient.get("/user-profiles/team");
  },

  /** The browser's zone, stored only while the member never chose one (row 511fdc25). */
  recordDetectedTimeZone(timezone: string): Promise<{ recorded: boolean; timezone: string | null }> {
    return apiClient.put("/user-profiles/me/detected-timezone", { timezone });
  },

  uploadAvatar(file: File): Promise<UserProfile> {
    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("ftl_access_token");
    const base = import.meta.env.VITE_API_URL ?? "/api/v1";
    return fetch(`${base}/user-profiles/me/avatar`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    }).then(async (res) => {
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    });
  },

  deleteAvatar(): Promise<UserProfile> {
    return apiClient.delete<UserProfile>("/user-profiles/me/avatar");
  },

  getMyActivity(params?: { limit?: number; offset?: number }): Promise<{ items: ActivityEntry[]; total: number }> {
    return apiClient.get("/user-profiles/me/activity", params);
  },

  exportMyData(): Promise<DataExport> {
    return apiClient.get("/user-profiles/me/export");
  },

  /**
   * Self-service account erasure (GDPR Art.17). `confirmEmail` must match the
   * caller's own email — the server re-checks it (422 on mismatch). If the
   * caller is the last Org Admin of any org with other members, the server
   * answers 409 `successor_required` with `orgs` (each org and who can be
   * chosen there), and the request is retried with `successors`, one member per
   * org id — each becomes Org Admin before anything is erased (rows c1b23e5d,
   * 88a80a65). An org with no other member is archived, not asked about. On
   * success the account is anonymized + personal data erased and tokens are
   * revoked server-side.
   */
  deleteMyAccount(
    confirmEmail: string,
    successors?: Record<string, string>,
  ): Promise<{ status: string; deleted_rows: Record<string, number> }> {
    return apiClient.delete("/user-profiles/me/account", {
      confirm_email: confirmEmail,
      ...(successors && Object.keys(successors).length ? { successors } : {}),
    });
  },
};

// ---------------------------------------------------------------------------
// Inventor prior-experience (patent docket support)
// ---------------------------------------------------------------------------

import type {
  InventorPriorExperience,
  InventorPriorExperienceUpsert,
  InventorPriorExperienceTaskSurface,
} from "./types";

export const platformPatentDocketApi = {
  listInventorRecords(): Promise<{
    items: Array<{
      user_id: string;
      org_id: string;
      full_legal_name: string | null;
      status: string;
      completion_pct: number;
      last_felix_review_at: string | null;
      updated_at: string;
      user_display_name: string | null;
      user_email: string;
      org_name: string;
    }>;
    total: number;
  }> {
    return apiClient.get("/platform/patent-docket/inventor-records");
  },
  getCounselHandoffChecklist(): Promise<{
    path: string;
    content: string;
    format: "markdown";
  }> {
    return apiClient.get("/platform/patent-docket/counsel-handoff-checklist");
  },
  getSpecCurrent(): Promise<{
    path: string;
    content: string;
    format: "markdown";
    head_sha: string;
  }> {
    return apiClient.get("/platform/patent-docket/spec");
  },
  listSpecVersions(): Promise<{
    items: Array<{
      sha: string;
      short_sha: string;
      date: string;
      author: string;
      subject: string;
    }>;
    total: number;
  }> {
    return apiClient.get("/platform/patent-docket/spec/versions");
  },
  getSpecAtVersion(sha: string): Promise<{
    sha: string;
    short_sha: string;
    date: string;
    author: string;
    subject: string;
    path: string;
    content: string;
    format: "markdown";
  }> {
    return apiClient.get(`/platform/patent-docket/spec/versions/${sha}`);
  },
  /**
   * Provenance audit-trail surface for §7.13. Returns per-value
   * counts over the trailing window, plus a synthetic-rate-per-
   * million figure. Healthy operation: zero `client_synthetic`
   * rows. Non-zero is the smoking-gun signal that a synthetic-
   * message transport reached persistence.
   */
  getProvenanceDistribution(params?: {
    days?: number;
    org_id_filter?: string;
  }): Promise<{
    window_days: number;
    counts: Record<string, number>;
    synthetic_count: number;
    synthetic_rate_per_million: number | null;
  }> {
    return apiClient.get(
      "/platform/patent-docket/provenance-distribution",
      params,
    );
  },
  /**
   * § 7.17 in-loop self-grade telemetry. Verdict + confidence +
   * routing-action distribution from `felix_in_loop_grades` over
   * the trailing window. Returns ``available=false`` until
   * migration 205 has been applied — UI must handle that case.
   */
  getInLoopGradeDistribution(params?: {
    days?: number;
    org_id_filter?: string;
  }): Promise<{
    available: boolean;
    reason?: string;
    verdict_counts: Record<string, number>;
    confidence_counts: Record<string, number>;
    routing_action_counts: Record<string, number>;
    total: number;
    days: number;
  }> {
    return apiClient.get(
      "/platform/patent-docket/in-loop-grade-distribution",
      params,
    );
  },
  /**
   * Felix-to-A-grade five-axis verdict (A1-A5). Computed from
   * production data via shared service module — same code path
   * as `python scripts/felix_a_grade_metrics.py`.
   */
  getAGradeMetrics(params?: {
    days?: number;
  }): Promise<{
    window_days: number;
    since: string;
    metrics: Array<{
      metric: string;
      passing: boolean;
      threshold?: number;
      threshold_ms?: number;
      [key: string]: unknown;
    }>;
    all_passing: boolean;
  }> {
    return apiClient.get(
      "/platform/patent-docket/a-grade-metrics",
      params,
    );
  },
};

export const inventorPriorExperienceApi = {
  getMine(): Promise<InventorPriorExperience> {
    return apiClient.get<InventorPriorExperience>(
      "/inventor-prior-experience/me",
    );
  },
  updateMine(body: InventorPriorExperienceUpsert): Promise<InventorPriorExperience> {
    return apiClient.put<InventorPriorExperience>(
      "/inventor-prior-experience/me",
      body,
    );
  },
  requestFelixReview(): Promise<InventorPriorExperience> {
    return apiClient.post<InventorPriorExperience>(
      "/inventor-prior-experience/me/felix-review",
      {},
    );
  },
  getMyTaskSurface(): Promise<InventorPriorExperienceTaskSurface> {
    return apiClient.get<InventorPriorExperienceTaskSurface>(
      "/inventor-prior-experience/me/task",
    );
  },
  listOrgRecords(): Promise<{ items: InventorPriorExperience[] }> {
    return apiClient.get("/inventor-prior-experience/");
  },
};

// ---------------------------------------------------------------------------
// Meetings
// ---------------------------------------------------------------------------

export interface MeetingCostBreakdown {
  meeting_id: string;
  total_cost_usd: number;
  by_service: Array<{ service: string; cost_usd: number; operations?: number }>;
}

export const meetingsApi = {
  listForProject(projectId: string, params?: {
    status?: string;
    meeting_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Meeting>> {
    return apiClient.get<PaginatedResponse<Meeting>>(
      `/meetings/project/${projectId}`,
      cleanParams(params),
    );
  },

  get(id: string): Promise<Meeting & { participants: unknown[] }> {
    return apiClient.get(`/meetings/${id}`);
  },

  getDetail(meetingId: string): Promise<MeetingDetail> {
    return apiClient.get<MeetingDetail>(`/meetings/${meetingId}/detail`);
  },

  /** Row 2b6d6836: the org a meeting lives in — answered only to a member of
   *  that org (404 otherwise), so a cross-org lobby link can offer the switch. */
  getHomeOrg(meetingId: string): Promise<EntityHomeOrg> {
    return apiClient.get<EntityHomeOrg>(`/meetings/${meetingId}/home-org`);
  },

  // The decisions a meeting captured, with the approval record post-processing
  // writes. `decision_approval` is null until Felix has asked, REQUESTED once he
  // has, and APPROVED/DECLINED once someone answered — three states that must
  // stay distinguishable, because "nobody was asked" and "nobody replied" are
  // different failures.
  listDecisionArtifacts(meetingId: string): Promise<{ items: MeetingDecisionArtifact[] }> {
    return apiClient.get<{ items: MeetingDecisionArtifact[] }>(
      `/meetings/${meetingId}/artifacts`,
      { artifact_type: "decision" },
    );
  },

  answerDecisionApproval(
    meetingId: string,
    artifactId: string,
    body: { approved: boolean; answer?: string },
  ): Promise<MeetingDecisionApprovalResult> {
    return apiClient.post<MeetingDecisionApprovalResult>(
      `/meetings/${meetingId}/decisions/${artifactId}/approval`,
      body,
    );
  },

  // Report delivery is keyed on the MEETING, not on a bot session. Reports
  // are stored with session_id NULL for every meeting that had no bot, so the
  // session-keyed meeting-bot route could never reach them.
  distributeReports(
    meetingId: string,
    reportType?: string,
  ): Promise<MeetingReportDistribution> {
    // apiClient.post's third argument is { skipAuth }, NOT query params —
    // passing report_type there would be silently dropped and every send
    // would go out as "all reports".
    const qs = reportType
      ? `?report_type=${encodeURIComponent(reportType)}`
      : "";
    return apiClient.post<MeetingReportDistribution>(
      `/meetings/${meetingId}/reports/distribute${qs}`,
    );
  },

  // Per-meeting AI cost (Bob 2026-06-09: "know the cost of a given meeting").
  // Backed by meeting_cost_service.get_meeting_cost_breakdown.
  cost(meetingId: string): Promise<MeetingCostBreakdown> {
    return apiClient.get<MeetingCostBreakdown>(`/meetings/${meetingId}/cost`);
  },

  create(body: {
    // project_id + platform are optional. Backend resolves the default platform
    // from org settings when omitted; an omitted project_id is the explicit
    // "Ad hoc" choice -- the server files the meeting in the org's Ad hoc
    // project (row 243addfa), never leaves it projectless.
    project_id?: string;
    title: string;
    description?: string;
    meeting_type: string;
    platform?: string;
    scheduled_start: string;
    scheduled_end?: string;
    agenda?: unknown[];
    participant_ids?: string[];
    external_participants?: { email: string; name: string }[];
    felix_role?: string;
    felix_roles?: string[];
    felix_scope?: string;
    felix_communication_mode?: string;
    meeting_category?: string;
    // v3 behavioral engine fields
    felix_role_preset?: string;
    felix_custom_role_desc?: string;
    interaction_scope?: string;
    aggressiveness?: string;
    formality?: string;
    output_channel?: string;
    active_controls?: Record<string, unknown>;
    guest_access_enabled?: boolean;
    waiting_room_enabled?: boolean;
    template_id?: string;
    // Recurrence dict; backend creates a linked recurring template.
    recurrence?: Record<string, unknown>;
    // IANA zone the meeting is booked in; defaults to this browser's.
    timezone?: string;
  }): Promise<Meeting> {
    // Row bec6d137: the invitation shows the start time in the zone the
    // meeting was booked in, so every booking door through here sends it.
    const timezone = body.timezone ?? browserTimeZone();
    return apiClient.post<Meeting>("/meetings", timezone ? { ...body, timezone } : body);
  },

  /**
   * Unified calendar display — Calendar.tsx and WorkspaceHub both call this
   * to merge FTL meetings with Google Calendar events on the same timeline.
   *
   * Window semantics: `start <= scheduled_start < end`.
   */
  listByDateRange(params: {
    start: string; // ISO-8601
    end: string;   // ISO-8601
    project_id?: string;
    include_cancelled?: boolean;
    limit?: number;
  }): Promise<{ items: Meeting[]; total: number; start: string; end: string }> {
    return apiClient.get(
      "/meetings/by-date-range",
      cleanParams(params),
    );
  },

  update(id: string, body: Partial<Meeting>): Promise<Meeting> {
    return apiClient.patch<Meeting>(`/meetings/${id}`, body);
  },

  // The org's "Ad hoc" meeting project (row 243addfa); id is null until the
  // first meeting scheduled without a project creates it.
  getAdHocProject(): Promise<{ id: string | null; name: string }> {
    return apiClient.get<{ id: string | null; name: string }>(`/meetings/ad-hoc-project`);
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/meetings/${id}`);
  },

  // Participant management
  listParticipants(meetingId: string): Promise<{ items: MeetingParticipant[]; total: number }> {
    return apiClient.get(`/meetings/${meetingId}/participants`);
  },

  addParticipant(meetingId: string, body: { user_id?: string; email?: string; name?: string; role?: string }): Promise<MeetingParticipant> {
    return apiClient.post(`/meetings/${meetingId}/participants`, body);
  },

  removeParticipant(meetingId: string, participantId: string): Promise<void> {
    return apiClient.delete(`/meetings/${meetingId}/participants/${participantId}`);
  },

  start(id: string): Promise<Meeting> {
    return apiClient.post<Meeting>(`/meetings/${id}/start`);
  },

  end(id: string): Promise<EndMeetingResponse> {
    return apiClient.post<EndMeetingResponse>(`/meetings/${id}/end`);
  },

  getTranscript(id: string, scopeChangesOnly?: boolean): Promise<{ items: unknown[]; total: number }> {
    return apiClient.get(`/meetings/${id}/transcript`, cleanParams({ scope_changes_only: scopeChangesOnly }));
  },

  getMinutes(id: string): Promise<MeetingMinutes> {
    return apiClient.get<MeetingMinutes>(`/meetings/${id}/minutes`);
  },

  listActionItems(id: string, params?: { status?: string }): Promise<{ items: MeetingActionItem[]; total: number }> {
    return apiClient.get(`/meetings/${id}/action-items`, cleanParams(params));
  },

  /** GET /meetings/{id}/reconciliation — which captured action items actually
   *  became tracked tasks, and whether each carries an owner and a date.
   *
   *  Read-only. Promotion stays the confirmation-gated path; this only surfaces
   *  the gap. Measured 2026-08-28 across four real meetings: 69 action items,
   *  47 promoted, 22 STRANDED — one meeting had all 15 of its items strand —
   *  and this route had never been called, so nobody had seen any of it. */
  reconciliation(meetingId: string): Promise<MeetingReconciliation> {
    return apiClient.get<MeetingReconciliation>(`/meetings/${meetingId}/reconciliation`);
  },

  createActionItem(meetingId: string, body: {
    title: string;
    description?: string;
    assigned_to: string;
    due_date?: string;
    priority?: string;
  }): Promise<MeetingActionItem> {
    return apiClient.post<MeetingActionItem>(`/meetings/${meetingId}/action-items`, body);
  },

  updateActionItem(meetingId: string, itemId: string, body: Partial<MeetingActionItem>): Promise<MeetingActionItem> {
    return apiClient.patch<MeetingActionItem>(`/meetings/${meetingId}/action-items/${itemId}`, body);
  },

  convertActionItemToTask(meetingId: string, itemId: string): Promise<{ task: unknown; action_item_id: string }> {
    return apiClient.post(`/meetings/${meetingId}/action-items/${itemId}/convert`);
  },

  generateAgenda(meetingId: string): Promise<GeneratedAgenda> {
    return apiClient.post<GeneratedAgenda>(`/meetings/${meetingId}/generate-agenda`);
  },

  rsvp(meetingId: string, status: "ACCEPTED" | "DECLINED" | "TENTATIVE"): Promise<{ meeting_id: string; rsvp_status: string }> {
    return apiClient.post(`/meetings/${meetingId}/rsvp`, { status });
  },

  /** Aggregated meeting intelligence for the dashboard widget */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getIntelligenceDashboard(projectId: string, days = 30): Promise<any> {
    return apiClient.get(`/meetings/project/${projectId}/intelligence-dashboard?days=${days}`);
  },

  /** Cross-meeting trends for a recurring series */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSeriesTrends(templateId: string, numMeetings = 10): Promise<any> {
    return apiClient.get(`/meetings/template/${templateId}/series-trends?num_meetings=${numMeetings}`);
  },

  /** Sentiment distribution for a single meeting */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSentiment(meetingId: string): Promise<any> {
    return apiClient.get(`/meetings/${meetingId}/sentiment`);
  },
};

// ---------------------------------------------------------------------------
// OAuth / Platform Integrations
// ---------------------------------------------------------------------------

export const oauthApi = {
  /** Get connection status for all providers */
  getStatus(): Promise<OAuthStatus> {
    return apiClient.get<OAuthStatus>("/oauth/status");
  },

  /** Get the OAuth authorization URL for a provider */
  connect(provider: string): Promise<OAuthConnectResponse> {
    return apiClient.get<OAuthConnectResponse>(`/oauth/${provider}/connect`);
  },

  /** Disconnect a provider (remove stored tokens) */
  disconnect(provider: string): Promise<{ status: string; provider: string }> {
    return apiClient.post(`/oauth/${provider}/disconnect`);
  },
};

// ---------------------------------------------------------------------------
// Sprints
// ---------------------------------------------------------------------------

export const sprintsApi = {
  listForProject(projectId: string, params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Sprint>> {
    return apiClient.get<PaginatedResponse<Sprint>>(
      `/sprints/project/${projectId}`,
      cleanParams(params),
    );
  },

  get(id: string): Promise<Sprint & { items: SprintItem[] }> {
    return apiClient.get(`/sprints/${id}`);
  },

  create(body: {
    project_id: string;
    name: string;
    goal?: string;
    start_date: string;
    end_date: string;
    capacity_points?: number;
  }): Promise<Sprint> {
    return apiClient.post<Sprint>("/sprints", body);
  },

  update(id: string, body: Partial<Sprint>): Promise<Sprint> {
    return apiClient.patch<Sprint>(`/sprints/${id}`, body);
  },

  activate(id: string): Promise<Sprint> {
    return apiClient.post<Sprint>(`/sprints/${id}/activate`);
  },

  complete(id: string): Promise<Sprint> {
    return apiClient.post<Sprint>(`/sprints/${id}/complete`);
  },

  getBacklog(sprintId: string, params?: { status?: string }): Promise<{ items: SprintItem[]; total: number }> {
    return apiClient.get(`/sprints/${sprintId}/backlog`, cleanParams(params));
  },

  addItem(sprintId: string, body: {
    entity_type: string;
    entity_id: string;
    story_points?: number;
    assigned_to?: string;
  }): Promise<SprintItem> {
    return apiClient.post<SprintItem>(`/sprints/${sprintId}/items`, body);
  },

  updateItem(sprintId: string, itemId: string, body: Partial<SprintItem>): Promise<SprintItem> {
    return apiClient.patch<SprintItem>(`/sprints/${sprintId}/items/${itemId}`, body);
  },

  removeItem(sprintId: string, itemId: string): Promise<void> {
    return apiClient.delete(`/sprints/${sprintId}/items/${itemId}`);
  },

  reorderItems(sprintId: string, itemOrder: string[]): Promise<{ reordered: number }> {
    return apiClient.put(`/sprints/${sprintId}/items/reorder`, itemOrder);
  },

  getBurndown(sprintId: string): Promise<{
    sprint_id: string;
    committed_points: number;
    start_date: string;
    end_date: string;
    snapshots: BurndownSnapshot[];
  }> {
    return apiClient.get(`/sprints/${sprintId}/burndown`);
  },

  getVelocity(projectId: string, window?: number): Promise<{
    project_id: string;
    sprints: Sprint[];
    /** null when no sprint has ever completed — "unmeasured", not zero.
     *  A real 0 means a completed sprint landed no points (row 830a5537). */
    average_velocity: number | null;
    completed_sprint_count: number;
    window: number;
  }> {
    return apiClient.get(`/sprints/project/${projectId}/velocity`, cleanParams({ window }));
  },

  // --- Delivery forecasting (deterministic; backed by sprint burndown snapshots) ---
  getForecast(sprintId: string): Promise<import("./types").SprintForecast> {
    return apiClient.get(`/sprints/${sprintId}/forecast`);
  },

  getDeliveryProbability(sprintId: string): Promise<import("./types").SprintDeliveryProbability> {
    return apiClient.get(`/sprints/${sprintId}/delivery-probability`);
  },

  getCapacity(sprintId: string): Promise<import("./types").SprintCapacity> {
    return apiClient.get(`/sprints/${sprintId}/capacity`);
  },

  // Dependencies
  createDependency(body: {
    predecessor_type: string;
    predecessor_id: string;
    successor_type: string;
    successor_id: string;
    dependency_type?: string;
    lag_days?: number;
  }): Promise<unknown> {
    return apiClient.post("/sprints/dependencies", body);
  },

  deleteDependency(id: string): Promise<void> {
    return apiClient.delete(`/sprints/dependencies/${id}`);
  },

  getDependencyChain(entityType: string, entityId: string): Promise<{
    predecessors: unknown[];
    successors: unknown[];
  }> {
    return apiClient.get(`/sprints/dependencies/chain/${entityType}/${entityId}`);
  },

  // Milestones
  listMilestones(projectId: string): Promise<{ items: MilestoneType[]; total: number }> {
    return apiClient.get(`/sprints/milestones/project/${projectId}`);
  },

  createMilestone(body: {
    project_id: string;
    name: string;
    description?: string;
    target_date: string;
    linked_sprint_id?: string;
    criteria?: unknown[];
  }): Promise<MilestoneType> {
    return apiClient.post<MilestoneType>("/sprints/milestones", body);
  },

  updateMilestone(id: string, body: Partial<MilestoneType>): Promise<MilestoneType> {
    return apiClient.patch<MilestoneType>(`/sprints/milestones/${id}`, body);
  },

  /**
   * Remove a sprint. Soft on the server (row 7def1db9): the sprint leaves the
   * board and its items keep their history, because a burndown that silently
   * loses rows is worse than a stale one.
   */
  deleteSprint(id: string): Promise<void> {
    return apiClient.delete<void>(`/sprints/${id}`);
  },

  /** Remove a milestone. Soft on the server, so historical briefings that name
   *  it still resolve. */
  deleteMilestone(id: string): Promise<void> {
    return apiClient.delete<void>(`/sprints/milestones/${id}`);
  },
};

// ---------------------------------------------------------------------------
// Document Import
// ---------------------------------------------------------------------------

/** The migration path off Jira.
 *
 *  The importer maps Epics->objectives, Stories->user_stories, Tasks->impl_tasks
 *  and Bugs->defects, preserving the hierarchy. It was mounted with no UI, so a
 *  customer with a Jira backlog had no way to bring it across. */
export const jiraImportApi = {
  /** Reports the per-kind counts that WOULD be created. Writes nothing. */
  dryRun: (projectId: string, content: string, format: "csv" | "json"):
    Promise<JiraImportDryRun> =>
    apiClient.post(`/projects/${projectId}/import/jira`, {
      content, format, dry_run: true,
    }),

  run: (projectId: string, content: string, format: "csv" | "json"):
    Promise<JiraImportResult> =>
    apiClient.post(`/projects/${projectId}/import/jira`, {
      content, format, dry_run: false,
    }),
}

/** Row 079341c8: what a Jira export proves about how the project was run.
 *  Stateless: no project is created and nothing is stored. */
export const jiraExportReportApi = {
  run: (file: File): Promise<JiraExportReport> => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.upload<JiraExportReport>("/imports/jira/report", formData);
  },
};

/** WP-4.3 — a Smartsheet plan export, brought across with its hierarchy.
 *
 *  Smartsheet states parentage by INDENTATION, not by a parent column, so the
 *  server reconstructs the tree before writing. Imported without that, a plan
 *  arrives as a flat list of unrelated tasks that looks like it worked. */
export const smartsheetImportApi = {
  /** Reports the per-kind counts that WOULD be created. Writes nothing. */
  dryRun: (projectId: string, content: string): Promise<JiraImportDryRun> =>
    apiClient.post(`/projects/${projectId}/import/smartsheet`, {
      content, format: "csv", dry_run: true,
    }),

  run: (projectId: string, content: string): Promise<JiraImportResult> =>
    apiClient.post(`/projects/${projectId}/import/smartsheet`, {
      content, format: "csv", dry_run: false,
    }),
}

/** WP-4.3 — an MS Project plan, from Microsoft's own MSPDI XML export.
 *
 *  File > Save As > XML in Project. The .mpp binary is not supported: it needs a
 *  third-party library, and MSPDI carries the same outline. Hierarchy comes from
 *  OutlineLevel, so parentage is read rather than guessed. */
export const msprojectImportApi = {
  dryRun: (projectId: string, content: string): Promise<JiraImportDryRun> =>
    apiClient.post(`/projects/${projectId}/import/msproject`, {
      content, format: "csv", dry_run: true,
    }),

  run: (projectId: string, content: string): Promise<JiraImportResult> =>
    apiClient.post(`/projects/${projectId}/import/msproject`, {
      content, format: "csv", dry_run: false,
    }),
}
/** WP-2.1 fleet patch — the platform-admin surface for rolling a rebuilt base
 *  image across the customer apps already running.
 *
 *  These five routes shipped in #2770 and had NO caller of any kind: the
 *  rollout could only be driven by an operator with curl on the prod box, so
 *  the "T6 container dividend" was unreachable from the product it belongs to.
 *
 *  Execution is deliberately two-gated server-side (the rollout's own
 *  `confirmed` flag AND the process env flag), so nothing here can start a
 *  rollout on its own. */
/** Row 893456c9: the integrations switch report (platform admin). */
export const integrationsApi = {
  report: (): Promise<IntegrationsReport> => apiClient.get("/platform/integrations"),
};

export const fleetPatchApi = {
  list: (limit = 25): Promise<{ patches: FleetPatchSummary[] }> =>
    apiClient.get(`/platform/fleet-patch?limit=${limit}`),

  get: (patchId: string): Promise<FleetPatchDetail> =>
    apiClient.get(`/platform/fleet-patch/${patchId}`),

  create: (body: {
    target_image_ref: string;
    reason?: string;
    batch_size?: number;
    max_failures?: number;
  }): Promise<{ id: string; status: string }> =>
    apiClient.post("/platform/fleet-patch", body),

  /** Arms one rollout. The env flag is the other gate — this alone executes
   *  nothing, which is why it is a separate deliberate call. */
  confirm: (patchId: string, confirmed = true): Promise<{ id: string; confirmed: boolean }> =>
    apiClient.post(`/platform/fleet-patch/${patchId}/confirm`, { confirmed }),

  execute: (patchId: string): Promise<{ id: string; status: string }> =>
    apiClient.post(`/platform/fleet-patch/${patchId}/execute`, {}),

  /** Stops a rollout for good. Already-patched tenants stay on the new image —
   *  cancelling stops advancing, it does not undo. */
  cancel: (patchId: string, reason?: string): Promise<{ id: string; status: string }> =>
    apiClient.post(`/platform/fleet-patch/${patchId}/cancel`, { reason }),
}
;

export const importsApi = {
  /** GET /imports/{id}/fidelity — ING-01.
   *
   *  "How much of my document became something I can see", which is NOT the
   *  same question as the importer's own accepted/rejected counters. Mounted
   *  with no caller, so a customer never learned when an import silently lost
   *  content. */
  fidelity: (importId: string): Promise<ImportFidelity> =>
    apiClient.get(`/imports/${importId}/fidelity`),

  /** POST /imports/{id}/mapping — what the AI made of the spreadsheet.
   *
   *  POST because it carries the conversation so far, but it WRITES NOTHING:
   *  the mapping is only persisted by confirmMapping. The model re-derives the
   *  whole mapping from the whole conversation each turn, so an answer that
   *  undoes an earlier assumption takes effect instead of leaving a
   *  contradiction. */
  mapping(
    importId: string,
    conversation: ImportMappingTurn[] = [],
  ): Promise<ImportMapping> {
    return apiClient.post<ImportMapping>(`/imports/${importId}/mapping`, {
      conversation,
    });
  },

  /** POST /imports/{id}/mapping-confirm — record what the conversation settled.
   *
   *  `columns` carries an explicit null for every column that maps to nothing,
   *  rather than omitting it: "this maps to nothing" and "I never saw this
   *  column" must not arrive as the same payload. The conversation is stored
   *  with it because it IS the justification — six months on, "why is column 3
   *  the priority" is answered by what the user said. */
  confirmMapping(
    importId: string,
    columns: Record<string, string | null>,
    conversation: ImportMappingTurn[] = [],
  ): Promise<ImportMappingConfirmed> {
    return apiClient.post(`/imports/${importId}/mapping-confirm`, {
      columns,
      conversation,
    });
  },

  /** POST /imports/{id}/start — read a parked spreadsheet WITHOUT a mapping,
   *  the way every import was read before (row 332fe10c's escape hatch). */
  startAsIs(importId: string): Promise<{ import_id: string; status: string }> {
    return apiClient.post(`/imports/${importId}/start`, {});
  },

  /** `holdForMapping` parks a csv/xlsx at AWAITING_MAPPING so the mapping the
   *  user confirms next is the one its rows are read through (row 332fe10c). */
  upload(
    projectId: string,
    file: File,
    opts: { holdForMapping?: boolean } = {},
  ): Promise<DocumentImport> {
    const formData = new FormData();
    formData.append("file", file);
    // Use raw fetch for FormData (apiClient sets Content-Type: json)
    const token = localStorage.getItem("ftl_access_token");
    const base = import.meta.env.VITE_API_URL ?? "/api/v1";
    const hold = opts.holdForMapping ? "?hold_for_mapping=true" : "";
    return fetch(`${base}/imports/project/${projectId}${hold}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    }).then(async (res) => {
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    });
  },

  get(importId: string): Promise<DocumentImport> {
    return apiClient.get<DocumentImport>(`/imports/${importId}`);
  },

  getProgress(importId: string): Promise<DocumentImportProgress> {
    return apiClient.get<DocumentImportProgress>(`/imports/${importId}/progress`);
  },

  listItems(importId: string, params?: {
    status?: string;
    classification?: string;
    item_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<DocumentImportItem>> {
    return apiClient.get<PaginatedResponse<DocumentImportItem>>(
      `/imports/${importId}/items`,
      cleanParams(params),
    );
  },

  getFollowupItems(importId: string): Promise<{ items: DocumentImportItem[]; total: number }> {
    return apiClient.get(`/imports/${importId}/followup`);
  },

  approveItem(importId: string, itemId: string, body?: {
    title?: string;
    description?: string;
    priority?: string;
    item_type?: string;
  }): Promise<{ success: boolean; entity_id?: string; entity_type?: string; title?: string; error?: string }> {
    return apiClient.post(`/imports/${importId}/items/${itemId}/approve`, body || {});
  },

  rejectItem(importId: string, itemId: string): Promise<{ success: boolean; item_id?: string; title?: string }> {
    return apiClient.post(`/imports/${importId}/items/${itemId}/reject`);
  },

  /** Routes to the REAL approve/reject endpoints. The `/review` endpoint was
   *  never implemented (imports.py exposes only `/approve` and `/reject`), so the
   *  old call silently 404'd and the Upload+Decompose review actions did nothing
   *  (DI-06, Bob 2026-07-06). Kept for RequirementsImport's reviewMutation(status). */
  reviewItem(importId: string, itemId: string, body: Record<string, unknown>): Promise<unknown> {
    const status = String(body?.status ?? "").toUpperCase();
    if (status === "REJECTED") {
      return apiClient.post(`/imports/${importId}/items/${itemId}/reject`);
    }
    // NEEDS_CLARIFICATION is a REVIEW answer, not an approval. It used to fall
    // through to /approve below, so pressing "Needs Clarification" created the
    // item — the opposite of what the reviewer asked for, with no way to tell.
    // The status has always been valid (it is in the DB CHECK constraint, the
    // ImportItemReview pattern, and import_fidelity_service's _UNDECIDED set);
    // only the endpoint was missing.
    if (status === "NEEDS_CLARIFICATION") {
      return apiClient.post(
        `/imports/${importId}/items/${itemId}/needs-clarification`,
      );
    }
    // Anything else that is NOT an approval must not be silently approved.
    // The fall-through below creates a record, so an unrecognised status
    // reaching it is a data-writing bug, not a no-op.
    if (status && status !== "ACCEPTED" && status !== "APPROVED") {
      return Promise.reject(
        new Error(
          `Unsupported review status "${status}". Approving would create the ` +
            "item, so this is refused rather than guessed.",
        ),
      );
    }
    // The Edit dialog sends edited_* keys; the /approve model accepts
    // title/description/priority/item_type. Map them (previously it read the
    // wrong keys, so EVERY edit was silently dropped and the item saved
    // unchanged). Accept the plain keys too for any other caller.
    const b = body as {
      title?: string; description?: string; priority?: string; item_type?: string;
      edited_title?: string; edited_description?: string; edited_priority?: string;
    };
    return apiClient.post(`/imports/${importId}/items/${itemId}/approve`, {
      title: b.edited_title ?? b.title,
      description: b.edited_description ?? b.description,
      priority: b.edited_priority ?? b.priority,
      item_type: b.item_type,
    });
  },

  acceptAll(importId: string): Promise<{ import_id: string; created: number; accepted: number; by_type: Record<string, number>; errors: unknown[] }> {
    return apiClient.post(`/imports/${importId}/accept-all`);
  },

  getGaps(importId: string): Promise<{ import_id: string; gaps: unknown }> {
    return apiClient.get(`/imports/${importId}/gaps`);
  },

  getConflicts(importId: string): Promise<{ import_id: string; conflicts: unknown }> {
    return apiClient.get(`/imports/${importId}/conflicts`);
  },

  /** Post-import verification — cross-checks for lost items, orphaned entity
   *  refs, missed auto-creations, and count mismatches. Confirms nothing was
   *  silently dropped during a bulk import. */
  verify(importId: string): Promise<import("./types").ImportVerifyResult> {
    return apiClient.get<import("./types").ImportVerifyResult>(`/imports/${importId}/verify`);
  },
};

// ---------------------------------------------------------------------------
// Document Plans
// ---------------------------------------------------------------------------

export const documentPlansApi = {
  listTypes(): Promise<{ types: DocumentTypeInfo[] }> {
    return apiClient.get("/document-plans/types");
  },

  getForProject(projectId: string): Promise<{
    project_id: string;
    total: number;
    by_category: Record<string, ProjectDocumentPlan[]>;
    items: ProjectDocumentPlan[];
  }> {
    return apiClient.get(`/document-plans/project/${projectId}`);
  },

  assessRelevance(projectId: string): Promise<{
    project_id: string;
    plans_created: number;
    total_document_types: number;
  }> {
    return apiClient.post(`/document-plans/project/${projectId}/assess`);
  },

  update(planId: string, body: {
    relevance?: string;
    auto_generate?: boolean;
    status?: string;
  }): Promise<ProjectDocumentPlan> {
    return apiClient.patch<ProjectDocumentPlan>(`/document-plans/${planId}`, body);
  },

  generate(planId: string): Promise<{
    workflow_run_id: string;
    plan_id: string;
    doc_type: string;
    status: string;
  }> {
    return apiClient.post(`/document-plans/${planId}/generate`);
  },
};

// ── Pipeline ──────────────────────────────────────────────────────────────

export const pipelineApi = {
  getStatus(projectId: string): Promise<any> {
    return apiClient.get(`/pipeline/projects/${projectId}/status`);
  },

  start(projectId: string): Promise<any> {
    return apiClient.post(`/pipeline/projects/${projectId}/start`);
  },

  pause(projectId: string): Promise<any> {
    return apiClient.post(`/pipeline/projects/${projectId}/pause`);
  },

  resume(projectId: string): Promise<any> {
    return apiClient.post(`/pipeline/projects/${projectId}/resume`);
  },

  history(projectId: string): Promise<any> {
    return apiClient.get(`/pipeline/projects/${projectId}/history`);
  },

  triggerDevelopment(body: {
    task_description: string;
    target_files?: string[];
    acceptance_criteria?: string[];
    feedback_ticket?: string | null;
    priority?: string;
  }): Promise<{
    success: boolean;
    task_id: string;
    workflow_run_id: string;
    message: string;
    feedback_ticket?: string | null;
  }> {
    return apiClient.post("/pipeline/trigger-development", body);
  },
};

// ── Milestones ────────────────────────────────────────────────────────────

export const milestonesApi = {
  list(projectId: string): Promise<any> {
    return apiClient.get(`/sprints/milestones/project/${projectId}`);
  },

  /**
   * Remove a milestone. Row 7def1db9: "Generate" milestones is the documented
   * second action a tester takes and it produces several at once, so getting it
   * wrong is easy — and there was no way back. Soft on the server, so briefings
   * that already name the milestone still resolve.
   */
  remove(id: string): Promise<void> {
    return apiClient.delete<void>(`/sprints/milestones/${id}`);
  },

  create(body: {
    project_id: string;
    name: string;
    description?: string;
    target_date?: string;
    linked_sprint_id?: string;
  }): Promise<any> {
    return apiClient.post("/sprints/milestones", body);
  },

  /** Deterministically seed one milestone per dated sprint that lacks one.
   *  Idempotent (skips sprints already linked to a milestone). */
  generateFromSprints(projectId: string): Promise<{
    created: number;
    skipped: number;
    milestones: unknown[];
  }> {
    return apiClient.post(`/projects/${projectId}/milestones/generate`, {});
  },

  update(milestoneId: string, body: {
    name?: string;
    description?: string;
    status?: string;
    target_date?: string;
    actual_date?: string;
    /** Exit criteria, one entry per criterion (the shape Felix writes too). */
    criteria?: { criterion: string }[];
  }): Promise<any> {
    return apiClient.patch(`/sprints/milestones/${milestoneId}`, body);
  },

  /** Read-only: milestones the *projected* schedule (CPM) will miss, with slip days. */
  atRisk(projectId: string): Promise<{
    items: {
      id: string;
      name: string;
      target_date: string;
      projected_finish_date: string | null;
      slip_days: number | null;
      at_risk: boolean;
      linked: boolean;
    }[];
    project_projected_finish_date: string | null;
    at_risk_count: number;
  }> {
    return apiClient.get(`/projects/${projectId}/milestones/at-risk`);
  },
};

// ── Org working-calendar holidays (dates the CPM schedule skips) ──────────────
export interface OrgHoliday {
  id: string;
  holiday_date: string;
  name: string | null;
  created_at: string;
}

export const holidaysApi = {
  list(): Promise<{ items: OrgHoliday[]; total: number }> {
    return apiClient.get("/sprints/holidays");
  },
  create(body: { holiday_date: string; name?: string | null }): Promise<OrgHoliday> {
    return apiClient.post("/sprints/holidays", body);
  },
  remove(id: string): Promise<{ deleted: boolean }> {
    return apiClient.delete(`/sprints/holidays/${id}`);
  },
};

// ── Success Policies ──────────────────────────────────────────────────────

export interface SuccessPolicy {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  entity_type: string;
  target_status: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export const successPoliciesApi = {
  list(params?: { limit?: number; offset?: number }): Promise<PaginatedResponse<SuccessPolicy>> {
    return apiClient.get<PaginatedResponse<SuccessPolicy>>("/admin/success-policies", cleanParams(params));
  },

  create(body: {
    name: string;
    description?: string;
    entity_type: string;
    target_status: string;
    is_default?: boolean;
  }): Promise<SuccessPolicy> {
    return apiClient.post<SuccessPolicy>("/admin/success-policies", body);
  },

  update(id: string, body: {
    name?: string;
    description?: string;
    is_default?: boolean;
  }): Promise<SuccessPolicy> {
    return apiClient.put<SuccessPolicy>(`/admin/success-policies/${id}`, body);
  },

  bind(policyId: string, projectId: string): Promise<{ id: string }> {
    return apiClient.post(`/admin/success-policies/${policyId}/bind`, { project_id: projectId });
  },

  preview(policyId: string): Promise<{
    policy: SuccessPolicy;
    bound_projects: { project_id: string; project_name: string }[];
  }> {
    return apiClient.post(`/admin/success-policies/${policyId}/preview`);
  },
};

// ---------------------------------------------------------------------------
// Document Export
// ---------------------------------------------------------------------------

export const documentExportApi = {
  exportHtml(documentId: string): Promise<any> {
    return apiClient.get(`/documents/${documentId}/export/html`);
  },

  exportPdf(documentId: string): Promise<any> {
    return apiClient.get(`/documents/${documentId}/export/pdf`);
  },

  exportDocx(documentId: string): Promise<any> {
    return apiClient.get(`/documents/${documentId}/export/docx`);
  },
};

// ---------------------------------------------------------------------------
// Demo Feedback
// ---------------------------------------------------------------------------

export const demoFeedbackApi = {
  getCandidates(meetingId: string, projectId: string): Promise<{ items: unknown[]; total: number }> {
    return apiClient.get(`/meetings/${meetingId}/demo-candidates`, { project_id: projectId });
  },

  submitFeedback(meetingId: string, body: {
    feedback_type?: string;
    content: string;
    linked_objective_id?: string;
  }): Promise<unknown> {
    return apiClient.post(`/meetings/${meetingId}/feedback`, body);
  },

  getFeedback(meetingId: string): Promise<{ items: unknown[]; total: number }> {
    return apiClient.get(`/meetings/${meetingId}/feedback`);
  },

  routeFeedback(meetingId: string, itemId: string, body: {
    route_to: "defect" | "specification" | "clarification";
  }): Promise<unknown> {
    return apiClient.post(`/meetings/${meetingId}/feedback/${itemId}/route`, body);
  },
};

// ---------------------------------------------------------------------------
// AD Integration (admin)
// ---------------------------------------------------------------------------

export const adIntegrationApi = {
  testConnection(): Promise<unknown> {
    return apiClient.post("/admin/ad/test-connection");
  },

  getStatus(): Promise<unknown> {
    return apiClient.get("/admin/ad/status");
  },

  syncUsers(): Promise<unknown> {
    return apiClient.post("/admin/ad/sync");
  },

  getUsers(): Promise<{ items: unknown[]; total: number }> {
    return apiClient.get("/admin/ad/users");
  },

  importUsers(body: {
    ad_user_ids: string[];
    default_role?: string;
  }): Promise<unknown> {
    return apiClient.post("/admin/ad/import", body);
  },

  getGroups(): Promise<{ items: unknown[]; total: number }> {
    return apiClient.get("/admin/ad/groups");
  },

  updateGroupMapping(groupName: string, body: {
    ftl_role: string;
  }): Promise<unknown> {
    return apiClient.put(`/admin/ad/groups/${groupName}/mapping`, body);
  },
};

// ---------------------------------------------------------------------------
// User Personas
// ---------------------------------------------------------------------------

export const userPersonasApi = {
  list(params?: ListParams): Promise<PaginatedResponse<UserPersona>> {
    return apiClient.get<PaginatedResponse<UserPersona>>("/user-personas", cleanParams(params));
  },

  get(id: string): Promise<UserPersona> {
    return apiClient.get<UserPersona>(`/user-personas/${id}`);
  },

  create(body: Record<string, unknown>): Promise<UserPersona> {
    return apiClient.post<UserPersona>("/user-personas", body);
  },

  update(id: string, body: Record<string, unknown>): Promise<UserPersona> {
    return apiClient.patch<UserPersona>(`/user-personas/${id}`, body);
  },

  changeStatus(id: string, body: StatusChangeRequest): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(`/user-personas/${id}/status`, body);
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/user-personas/${id}/history`,
      cleanParams(params),
    );
  },

  getStories(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<UserStory>> {
    return apiClient.get<PaginatedResponse<UserStory>>(
      `/user-personas/${id}/stories`,
      cleanParams(params),
    );
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/user-personas/${id}`);
  },
};

// ---------------------------------------------------------------------------
// User Stories
// ---------------------------------------------------------------------------

export const userStoriesApi = {
  list(params?: ListParams): Promise<PaginatedResponse<UserStory>> {
    return apiClient.get<PaginatedResponse<UserStory>>("/user-stories", cleanParams(params));
  },

  get(id: string): Promise<UserStory> {
    return apiClient.get<UserStory>(`/user-stories/${id}`);
  },

  create(body: Record<string, unknown>): Promise<UserStory> {
    return apiClient.post<UserStory>("/user-stories", body);
  },

  update(id: string, body: Record<string, unknown>): Promise<UserStory> {
    return apiClient.patch<UserStory>(`/user-stories/${id}`, body);
  },

  changeStatus(id: string, body: StatusChangeRequest): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(`/user-stories/${id}/status`, body);
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/user-stories/${id}/history`,
      cleanParams(params),
    );
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/user-stories/${id}`);
  },
};

// ---------------------------------------------------------------------------
// Outcomes
// ---------------------------------------------------------------------------

export const outcomesApi = {
  list(params?: ListParams): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>("/outcomes", cleanParams(params));
  },

  get(id: string): Promise<unknown> {
    return apiClient.get(`/outcomes/${id}`);
  },

  create(body: Record<string, unknown>): Promise<unknown> {
    return apiClient.post("/outcomes", body);
  },

  update(id: string, body: Record<string, unknown>): Promise<unknown> {
    return apiClient.patch(`/outcomes/${id}`, body);
  },

  changeStatus(id: string, body: StatusChangeRequest): Promise<StatusChangeResult> {
    return apiClient.post<StatusChangeResult>(`/outcomes/${id}/status`, body);
  },

  getHistory(
    id: string,
    params?: { limit?: number; offset?: number },
  ): Promise<PaginatedResponse<unknown>> {
    return apiClient.get<PaginatedResponse<unknown>>(
      `/outcomes/${id}/history`,
      cleanParams(params),
    );
  },

  delete(id: string): Promise<void> {
    return apiClient.delete(`/outcomes/${id}`);
  },
};

// ---------------------------------------------------------------------------
// Outcome Record (OUT-01/02/03, PR-14) — one per project; distinct from the
// legacy outcomesApi above (dead mig-011 vertical, slated for retirement).
// ---------------------------------------------------------------------------

export const outcomeRecordApi = {
  /** The "are we done?" payload — always 200; exists=false when undrafted. */
  getVerdict(projectId: string): Promise<OutcomeRecordVerdict> {
    return apiClient.get<OutcomeRecordVerdict>(`/projects/${projectId}/outcome-record`);
  },

  /** FLX-09 one-tap confirm: DRAFT → CONFIRMED. */
  confirm(projectId: string): Promise<OutcomeRecordSummary> {
    return apiClient.post<OutcomeRecordSummary>(
      `/projects/${projectId}/outcome-record/confirm`,
      {},
    );
  },

  /** OUT-03: score the criteria for one iteration.
   *
   *  THE ONLY CALLER of the only writer of `acceptance_scores`. That endpoint
   *  has been mounted since OUT-03 shipped and nothing in the product invoked
   *  it, so the table held zero rows -- not because nobody pressed a button,
   *  but because there was no button. portfolio_kpi_service step 6b counts
   *  measured outcomes off that table, so it was pinned at 0 permanently and
   *  PortfolioHub rendered "—" no matter how many outcomes anyone recorded.
   *
   *  `scored_by_type` is HUMAN: a person clicked. AI and AUTOMATED are the
   *  other accepted values and belong to Felix and to a future automated
   *  scorer respectively.
   */
  score(
    projectId: string,
    evidence: Record<string, OutcomeCriterionEvidence>,
  ): Promise<OutcomeRecordVerdict> {
    return apiClient.post<OutcomeRecordVerdict>(
      `/projects/${projectId}/outcome-record/score`,
      { evidence, scored_by_type: "HUMAN" },
    );
  },
};

// ---------------------------------------------------------------------------
// Felix — AI Program Manager assistant
// ---------------------------------------------------------------------------

export interface FelixChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface FelixPendingAction {
  type: string;
  label: string;
}

export interface FelixChatRequest {
  message: string;
  /**
   * Invention G — 6th domain (spec § 7.13). Tags the message
   * envelope with its origin so the server-side gate can refuse
   * autonomous-action tools when the message wasn't authored by a
   * human. Frontend default is "human" for handleSend(), which
   * is the user-input transport.
   */
  provenance?: "human" | "client_synthetic" | "tool_result" | "system_notice";
  page?: string;
  page_path?: string;
  project_id?: string;
  entity_type?: string;
  entity_id?: string;
  history?: FelixChatMessage[];
  recent_errors?: string[];
  /** Actions from previous turns still awaiting user confirmation (FB-0005). */
  pending_actions?: FelixPendingAction[];
  ui_state?: {
    viewport_width?: number;
    viewport_height?: number;
    sidebar_collapsed?: boolean;
  };
  /** When true, autonomous mode returns previews instead of executing. */
  preview_mode?: boolean;
  /**
   * Row a0fe2825 (FB-0071): what this build of the panel can show. Set by
   * withClientFeatures on every chat call; a tab loaded before a feature
   * shipped sends nothing, and Felix then tells the person to reload.
   */
  client_features?: string[];
  /**
   * KI-0214/0215 (FB-0008): IANA timezone string from the browser
   * (Intl.DateTimeFormat().resolvedOptions().timeZone). Backend converts
   * UTC server time to the user's local date/weekday so Felix doesn't
   * mis-anchor on UTC.
   */
  client_timezone?: string;
}

export interface FelixAction {
  type:
    // Broadcast. The API and its client were built long before Felix could
    // reach them (app/api/v1/broadcast.py, 53 endpoints;
    // frontend/src/api/broadcastApi.ts, 29 methods) — these entries are what
    // let FelixChat dispatch to them at all. Adding a case without adding the
    // type here is a build error, which is the right failure: the union is the
    // single list of what Felix is allowed to ask the client to do.
    | "broadcast_create_campaign"
    | "campaign_performance"
    | "list_campaign_templates"
    | "campaign_from_template"
    | "lead_capture"
    | "lead_metrics"
    | "draft_social_post"
    | "generate_content"
    | "list_pending_approvals"
    | "request_campaign_approval"
    | "review_campaign_approval"
    | "broadcast_list_campaigns"
    | "broadcast_get_report"
    | "create_project"
    | "create_objective"
    | "create_test_case"
    | "create_user_story"
    | "create_user_persona"
    | "create_component"
    | "create_component_specification"
    | "create_task"
    | "create_module"
    | "create_meeting"
    | "start_qa"
    | "navigate"
    | "log_feedback"
    | "update_feedback"
    | "dev_status"
    | "check_execution"
    | "send_guidance"
    | "list_executions"
    | "respond_to_agent"
    | "retry_execution"
    | "trigger_development"
    | "update_project"
    | "update_component"
    | "update_component_specification"
    | "update_objective"
    | "update_user_story"
    | "update_user_persona"
    | "update_task"
    | "update_test_case"
    | "update_module"
    | "update_meeting"
    | "delete_objective"
    | "delete_user_story"
    | "delete_user_persona"
    | "delete_component"
    | "delete_component_specification"
    | "delete_task"
    | "delete_test_case"
    | "delete_module"
    | "delete_meeting"
    | "delete_project"
    | "create_change_request"
    // Reaches the client only as a card the org's autonomy level withheld;
    // chat files defects server-side.
    | "create_defect"
    | "create_baseline"
    | "change_status"
    | "join_meeting"
    | "leave_meeting"
    | "create_sprint"
    | "add_sprint_item"
    // OUT-02 FLX-09 — the Outcome Record one-tap confirm card
    | "confirm_outcome_record"
    // Phase 6 Patent I — Felix-driven nav surface state (sidebar.open_category)
    | "sidebar.open_category";
  params: Record<string, unknown>;
  label: string;
  /**
   * Whether the UI must ask before running this — decided by the SERVER from
   * the org's `felix_autonomy_level` dial.
   *
   * Optional because an older server does not send it. When it is absent the
   * client falls back to its own hardcoded sets, which is the pre-existing
   * behaviour and errs toward asking.
   */
  requires_confirmation?: boolean;
  /**
   * T1 (row 7f612cac): when the person explicitly directed a write that cannot
   * be undone, Felix ADVISES instead of asking. This is that advice; the chat
   * shows it when the action runs.
   */
  break_risk_warning?: string | null;
}

/** Token usage and cost information for a single AI interaction. */
export interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  total_tokens: number;
  cost_usd: number;
  model: string;
}

/** Human-readable model display names.
 * Current lineup MUST stay in sync with the backend registry + Platform Config
 * (migration 435). Legacy IDs are kept so historical chat turns genuinely
 * served by an older model still render a correct name. Without a
 * claude-fable-5 entry the per-message badge in TokenUsageIndicator fell
 * through to a broken bare "fable" label (workflow finding 2026-06-10). */
export const MODEL_DISPLAY_NAMES: Record<string, string> = {
  // Current
  "claude-fable-5": "Claude Fable 5",
  "claude-opus-4-8": "Claude Opus 4.8",
  "claude-sonnet-4-6": "Claude Sonnet 4.6",
  "claude-haiku-4-5-20251001": "Claude Haiku 4.5",
  "claude-opus-5-5": "Claude Opus 5.5",
  "claude-sonnet-5-5": "Claude Sonnet 5.5",
  "claude-haiku-5-5": "Claude Haiku 5.5",
  // Legacy (historical turns)
  "claude-sonnet-4-5-20250929": "Claude Sonnet 4.5",
  "claude-opus-4-6": "Claude Opus 4.6",
};

export interface QuickReply {
  label: string;
  message: string;
  style?: "primary" | "secondary" | "danger";
}

export interface FelixChatResponse {
  response: string;
  actions: FelixAction[];
  /** Row fad9bff7: the approval cards Felix proposed in this turn. */
  proposals?: FelixProposal[];
  quick_replies?: QuickReply[];
  context_used?: Record<string, unknown>;
  token_usage?: TokenUsage;
  /**
   * § 7.17 in-loop self-grade flag — true ONLY when the backend's
   * grader returned low-band confidence or hallucinated_action /
   * low_confidence verdict. Anchoring guard (Lee & See 2004): no
   * medium / high band is ever surfaced. UI should render a small
   * "low confidence" chip when this is true.
   */
  low_confidence_chip?: boolean;
  /**
   * § 7.15 Phase 1 typed clarifier payload — set when the most
   * recent G-refusal in the request matched a clarifier template.
   * UI renders FelixChatClarifier.
   */
  clarifier_payload?: ClarifierPayload | null;
}

export interface ClarifierOption {
  label: string;
  value: string;
}

export interface ClarifierQuestion {
  question_id: string;
  text: string;
  type: "multiple_choice" | "free_text";
  options: ClarifierOption[];
  hint?: string;
  g_domain?: string;
  evpi?: number;
}

export interface ClarifierPayload {
  question_for_user: string;
  questions: ClarifierQuestion[];
  refused_tool: string;
  refused_error_code: string;
  g_domain: string;
}

// ---------------------------------------------------------------------------
// Streaming event types from POST /assistant/chat/stream
// ---------------------------------------------------------------------------

export interface FelixStreamTextDelta {
  type: "text_delta";
  text: string;
}

export interface FelixStreamReplaceText {
  type: "replace_text";
  text: string;
}

export interface FelixStreamAction {
  type: "action";
  action: FelixAction;
}

export interface FelixStreamDone {
  type: "done";
  usage?: Record<string, number>;
  token_usage?: TokenUsage;
  quick_replies?: QuickReply[];
  /** § 7.17 anchoring chip — see FelixChatResponse.low_confidence_chip */
  low_confidence_chip?: boolean;
  /** § 7B.5 H validator correction — opt-in replacement for streamed text */
  h_validator_correction?: string;
  /** § 7.15 Phase 1 — see FelixChatResponse.clarifier_payload */
  clarifier_payload?: ClarifierPayload | null;
  /** § 7.15 Phase 2 — plan summary on done */
  plan_summary?: { plan_id: string; step_count: number };
  /** Row fad9bff7: the approval cards Felix proposed in this turn. */
  proposals?: FelixProposal[];
}

export interface FelixStreamPlanStarted {
  type: "plan.started";
  plan_id: string;
  summary: string;
  step_count: number;
}

export interface FelixStreamPlanStep {
  type: "plan.step.started" | "plan.step.completed" | "plan.step.failed";
  plan_id: string;
  step_id: string;
  description?: string;
  tool: string;
}

export interface FelixStreamError {
  type: "error";
  error: string;
}

export interface FelixStreamToolExecuting {
  type: "tool_executing";
  tools: string[];
  /** Pithy, Claude-style progress line for this round (Bob 2026-06-20) —
   *  what the user sees while Felix works, instead of the raw tool names.
   *  Never exposes a tool/entity name. */
  narration?: string;
}

export interface FelixStreamToolStarted {
  /** Per-tool progress event. Emitted immediately before each tool
   *  runs in a round, so the UI can show "Calling X (2 of 5)" live
   *  instead of going dark for 30s+ between the round-level
   *  tool_executing list and the first tool_result. */
  type: "tool_started";
  tool: string;
  tool_use_id?: string;
  index?: number;
  total?: number;
  round?: number;
}

export interface FelixStreamResumed {
  /** Mid-flight stream recovery — the upstream failed between tokens
   *  and Felix is continuing from where it left off. The client has
   *  already received the prefix; new text_delta events follow. */
  type: "stream_resumed";
  attempt: number;
  reason: string;
  retry_in_seconds: number;
}

export type FelixStreamEvent =
  | FelixStreamTextDelta
  | FelixStreamReplaceText
  | FelixStreamAction
  | FelixStreamDone
  | FelixStreamError
  | FelixStreamToolExecuting
  | FelixStreamToolStarted
  | FelixStreamResumed
  | FelixStreamPlanStarted
  | FelixStreamPlanStep;

// Autonomous mode stream events — superset of regular stream events
export interface FelixAutonomousActionComplete {
  type: "action_complete";
  tool: string;
  result: {
    success: boolean;
    entity_id?: string;
    entity_type?: string;
    title?: string;
    detail?: string;
    error?: string;
    // Preview mode fields
    preview?: boolean;
    action?: "create" | "update" | "delete" | "skip";
    tool_name?: string;
    table?: string;
    fields?: Record<string, unknown>;
    changes?: Record<string, unknown>;
  };
}

export interface FelixAutonomousProgress {
  type: "progress";
  completed: number;
  detail: string;
}

export interface FelixAutonomousDone {
  type: "done";
  usage?: Record<string, number>;
  total_actions?: number;
  token_usage?: TokenUsage;
  qa_started?: boolean;
  /** Row fad9bff7: the approval cards Felix proposed in this turn. */
  proposals?: FelixProposal[];
}

export interface FelixAutonomousVerification {
  type: "verification";
  succeeded: number;
  failed: number;
  details: { tool: string; success: boolean; detail: string }[];
}

export interface FelixConflictAlert {
  type: "conflict_alert";
  blocking_conflicts?: number;
  escalated_conflicts?: number;
  total_conflicts?: number;
  conflicts?: Array<{
    id: string;
    severity: string;
    conflict_type: string;
    description: string;
    entity_a_type: string;
    entity_b_type: string;
  }>;
}

export interface FelixConflictEscalation {
  type: "conflict_escalation";
  escalated_count?: number;
  message?: string;
  conflicts?: Array<{
    id: string;
    severity: string;
    conflict_type: string;
    description: string;
    attempts: number;
  }>;
}

export interface FelixFrontendAction {
  type: "frontend_action";
  action?: {
    type: string;
    params: Record<string, unknown>;
    /** Present when the org's Felix autonomy level WITHHELD this action: the
     *  action's own label (persona._generate_action_label, e.g.
     *  "File defect 'X'"), which FelixChat sends back as
     *  pending_actions[].label for the server to reconcile. Absent on the
     *  start_qa handoff, which is an interactivity transfer, not a refusal. */
    label?: string;
    /** WHY it was withheld (felix_autonomy.confirmation_reason), naming the
     *  level so an admin knows what to change. Shown as the message text. */
    reason?: string;
  };
}

// Wave 10B — autonomous run lifecycle events (direct stream only).
// The equivalent SSE broadcast events (visible in other tabs via
// useSSE) use the namespaced names `autonomous.goal_started`,
// `autonomous.wave_started`, etc.
export interface FelixAutonomousGoalStarted {
  type: "autonomous_goal_started";
  goal_run_id: string;
  message_preview?: string;
  started_at?: string;
}

export interface FelixAutonomousWaveStarted {
  type: "autonomous_wave_started";
  goal_run_id: string;
  wave_number: number;
  tool_count: number;
  round_number?: number;
}

export interface FelixAutonomousWaveCompleted {
  type: "autonomous_wave_completed";
  goal_run_id: string;
  wave_number: number;
  elapsed_ms: number;
  tool_count: number;
  succeeded?: number;
}

// Wave 10F — durable goal run record (the felix_goal_runs row
// serialized by the observability endpoints). ``failure_category`` is
// the coarse taxonomy the dashboard widget groups by.
export interface FelixGoalRun {
  id: string;
  org_id: string;
  project_id: string | null;
  user_id: string;
  message_preview: string | null;
  status: "RUNNING" | "COMPLETED" | "FAILED" | "CANCELLED";
  stop_reason: string | null;
  failure_category:
    | "success"
    | "cancelled"
    | "auth"
    | "rate_limit"
    | "timeout"
    | "conflict_budget"
    | "internal"
    | "unknown";
  total_waves: number;
  total_entities_created: number;
  total_actions: number;
  elapsed_ms: number | null;
  trace_id: string | null;
  cancel_requested_at: string | null;
  cancel_requested_by: string | null;
  started_at: string;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

// Design Refresh Phase 6 — full detail for the Autonomous Run Detail page.
export interface FelixGoalRunTrace {
  id: string;
  mode: string;
  model_used: string | null;
  model_requested: string | null;
  total_rounds: number;
  tools_count: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  latency_ms: number | null;
  first_token_ms: number | null;
  stop_reason: string | null;
  had_error: boolean;
  error_message: string | null;
  error_category: string | null;
  response_summary: string | null;
  created_at: string | null;
}

export interface FelixGoalRunToolExecution {
  tool_name: string;
  round_number: number;
  tool_use_id: string | null;
  input_summary: string | null;
  output_summary: string | null;
  duration_ms: number | null;
  success: boolean;
  error_message: string | null;
  error_code: string | null;
  retry_count: number;
  was_transient: boolean;
  is_server_side: boolean;
  is_action_tool: boolean;
}

export interface FelixGoalRunDetail {
  run: FelixGoalRun;
  trace: FelixGoalRunTrace | null;
  tool_executions: FelixGoalRunToolExecution[];
}

export interface FelixAutonomousGoalCompleted {
  type: "autonomous_goal_completed";
  goal_run_id: string;
  total_waves: number;
  total_entities_created: number;
  total_actions: number;
  elapsed_ms: number;
  stop_reason?: string | null;
}

export interface FelixAutonomousGoalFailed {
  type: "autonomous_goal_failed";
  goal_run_id: string;
  total_waves: number;
  total_entities_created: number;
  total_actions: number;
  elapsed_ms: number;
  stop_reason?: string | null;
}

// Wave 10D — cooperative cancel
export interface FelixAutonomousGoalCancelled {
  type: "autonomous_goal_cancelled";
  goal_run_id: string;
  total_waves: number;
  total_entities_created: number;
  total_actions: number;
  elapsed_ms: number;
  stop_reason?: string | null;
}

export type FelixAutonomousEvent =
  | FelixStreamTextDelta
  | FelixStreamReplaceText
  | FelixAutonomousActionComplete
  | FelixAutonomousProgress
  | FelixAutonomousDone
  | FelixStreamError
  | FelixStreamToolExecuting
  | FelixStreamToolStarted
  | FelixAutonomousVerification
  | FelixConflictAlert
  | FelixConflictEscalation
  | FelixFrontendAction
  | FelixAutonomousGoalStarted
  | FelixAutonomousWaveStarted
  | FelixAutonomousWaveCompleted
  | FelixAutonomousGoalCompleted
  | FelixAutonomousGoalFailed
  | FelixAutonomousGoalCancelled;

// ==========================================================================
// Wave 10E — Decomposition Completeness
// ==========================================================================

export interface CompletenessGap {
  id: string;
  title: string;
  rationale: string;
  severity: "low" | "medium" | "high" | "critical";
  suggested_parent_type: string | null;
  suggested_parent_id: string | null;
}

export interface CompletenessOmission {
  gap_id: string;
  reason: string;
  declared_by_user_id: string;
  declared_at: string;
}

export interface CompletenessGapClosure {
  gap_id: string;
  closed_by_user_id: string;
  closed_by_entity_id: string | null;
  closed_at: string;
}

export interface CompletenessAssessment {
  id: string;
  org_id: string;
  project_id: string;
  parent_entity_type: string;
  parent_entity_id: string;
  complete: boolean;
  score: number;
  gaps: CompletenessGap[];
  omissions: CompletenessOmission[];
  closed_gaps: CompletenessGapClosure[];
  ai_rationale: string | null;
  thinking_tokens_used: number | null;
  assessed_at: string;
  created_at: string;
  updated_at: string;
  requested_by_user_id: string | null;
}

export interface DocReadinessEntry {
  doc_type: string;
  name: string;
  ready: boolean;
  missing_required: string[];
  missing_recommended: string[];
  completeness_score?: number | null;
  completeness_tier?: string | null;
  warnings: string[];
}

export interface DocReadinessReport {
  ready: boolean;
  blocking_count: number;
  documents: DocReadinessEntry[];
  error?: string;
}

export interface BaselineReadinessResult {
  ready: boolean;
  /** Decomposition gates only (gaps resolved / omitted). */
  decomposition_ready?: boolean;
  /** Document generator pre-flight rolled up across all project-scoped
   *  doc types. Populated for ``entity_type == "project"``. */
  document_ready?: boolean;
  document_readiness?: DocReadinessReport | null;
  assessment_id?: string | null;
  unresolved_gap_count?: number;
  unresolved_gaps?: CompletenessGap[];
  reason?: string | null;
}

export const completenessApi = {
  /** Get the most recent assessment for an entity. 404 if none exists. */
  getLatest(
    entityType: string,
    entityId: string
  ): Promise<CompletenessAssessment> {
    return apiClient.get(`/completeness/${entityType}/${entityId}`);
  },

  /** Run (or re-run) the BA completeness analyzer. Extended thinking
   * on by default. Returns a persisted assessment with gaps. */
  assess(
    entityType: string,
    entityId: string,
    body: { project_id: string; use_thinking?: boolean }
  ): Promise<CompletenessAssessment> {
    return apiClient.post(
      `/completeness/${entityType}/${entityId}/assess`,
      body
    );
  },

  /** Declare an explicit omission with required rationale (min 3 chars). */
  declareOmission(
    assessmentId: string,
    body: { gap_id: string; reason: string }
  ): Promise<{ success: boolean; omission: CompletenessOmission }> {
    return apiClient.post(
      `/completeness/assessments/${assessmentId}/omissions`,
      body
    );
  },

  /** Mark a gap as closed — a child entity was added or edited. */
  closeGap(
    assessmentId: string,
    body: { gap_id: string; closed_by_entity_id?: string | null }
  ): Promise<{ success: boolean; closure: CompletenessGapClosure }> {
    return apiClient.post(
      `/completeness/assessments/${assessmentId}/close_gap`,
      body
    );
  },

  /** Row a31c93bd: every required field missing or invalid on the project's
   *  in-scope items -- what the baseline sign-off and lock refuse on. */
  projectBaselineReadiness(projectId: string): Promise<BaselineFieldReadiness> {
    return apiClient.get(`/projects/${projectId}/baseline-readiness`);
  },

  /** Check whether an entity is baseline-ready per its latest assessment. */
  baselineReady(
    entityType: string,
    entityId: string
  ): Promise<BaselineReadinessResult> {
    return apiClient.get(
      `/completeness/${entityType}/${entityId}/baseline_ready`
    );
  },

  /** Per-entity doc-readiness: is this single entity populated enough
   *  for the downstream document generators. Same logic the
   *  status_service runs as a pre-flight for DRAFT → APPROVED. */
  docReady(
    entityType: string,
    entityId: string,
  ): Promise<EntityDocReadinessResult> {
    return apiClient.get(
      `/completeness/${entityType}/${entityId}/doc_ready`,
    );
  },
};

export interface EntityDocReadinessResult {
  ready: boolean;
  entity_type: string;
  missing_fields: string[];
  warnings: string[];
  error?: string;
}

import type { PlainAsk } from "@/api/developApi";

/** Asking someone outside FTL. `channel` is the sender's explicit choice. */
export interface AskOutwardInput {
  email: string;
  display_name?: string;
  company?: string;
  title?: string;
  channel: "LINK" | "EMAIL";
  chase_after_business_days?: number;
}

export interface AskOutwardResult {
  question_id: string;
  channel: string;
  contact_id: string;
  /** True when this person was not already a contact — worth surfacing so the
   *  sender knows a new record was created on their behalf. */
  contact_created: boolean;
  chase_at: string;
}

/** One entry in a blocker's thread: what happened, when, and who by. */
export interface BlockerThreadEntry {
  kind: string;
  at: string | null;
  text: string;
  actor: string | null;
  detail: Record<string, string>;
}

/** A blocker rendered as one readable thread.
 *
 *  `complete` is false when the blocker was answered but does not say what
 *  changed as a result — an incomplete record, not a rendering hint. */
export interface BlockerThread {
  question_id: string;
  entries: BlockerThreadEntry[];
  complete: boolean;
  reason: string;
}

/** One item in the user's awaiting-decision queue (GET /assistant/decisions-needed). */
export interface DecisionNeededItem {
  kind: "change_request" | "qa_session" | "conflict" | "blocking_question" | string;
  id: string;
  title?: string | null;
  project_id?: string | null;
  /** For blocking_question this carries the URGENCY, not a bug severity. */
  severity?: string | null;
  /** Which agent is blocked. decisions_needed.py really returns this for
   *  blocking_question items; the type omitted it, so the field was
   *  invisible to every consumer. */
  agent_name?: string | null;
  /** Free-text context the agent attached to a blocking_question. */
  context?: string | null;
  /** The blocker rewritten for a reader with no context (migration 596).
   *  NULL until translated — render sites fall back to `title`. */
  plain_ask?: PlainAsk | null;
  created_at?: string | null;
  /** zoho_unplanned_time (row 2e082ba2): what came in, and the code's suggestion. */
  message?: string | null;
  minutes?: number | null;
  suggestion?: {
    entity_type: string | null; entity_id: string | null; title: string | null;
    confidence: string; rule: string; basis: string;
  } | null;
}
export interface DecisionsNeeded {
  total: number;
  items: DecisionNeededItem[];
}

/** Felix's "start here / one root cause" focus band (GET /assistant/command-center-focus). */
export interface CommandCenterFocus {
  headline: string;
  body: string;
  next_move_label: string | null;
  next_move_project_id: string | null;
  calm: boolean;
}

/** What this build of the chat panel can show (row a0fe2825, FB-0071). */
export const FELIX_CLIENT_FEATURES: readonly string[] = ["proposal_cards"];

/** The request with this build's features declared, so the server knows the
 *  panel can show an Approve / Change card. */
export function withClientFeatures(body: FelixChatRequest): FelixChatRequest {
  return { ...body, client_features: [...FELIX_CLIENT_FEATURES] };
}

export const assistantApi = {
  chat(body: FelixChatRequest): Promise<FelixChatResponse> {
    return apiClient.post("/assistant/chat", withClientFeatures(body));
  },

  /** The user's "needs specifically YOU" queue — CRs where they're the
   *  approver, their deferred QA sessions, and open conflicts. Used by the
   *  ranked Home to render a per-item "why you" line. */
  /**
   * Session traces — one row per Felix turn, carrying rounds, tools, token
   * counts, latency and stop reason. Row ca2fcad9: 4,049 of these were
   * reachable only by raw API call, so "Felix seems worse today" needed SQL.
   */
  analyticsTraces(params?: {
    mode?: string;
    had_error?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ items: import("@/api/types").FelixTrace[]; count: number }> {
    return apiClient.get<{ items: import("@/api/types").FelixTrace[]; count: number }>(
      "/assistant/analytics/traces",
      cleanParams(params),
    );
  },

  /** Aggregated quality metrics per period, from the felix_quality_metrics view. */
  analyticsQuality(params?: {
    hours?: number;
  }): Promise<{ items: import("@/api/types").FelixQualityMetric[] }> {
    return apiClient.get<{ items: import("@/api/types").FelixQualityMetric[] }>(
      "/assistant/analytics/quality",
      cleanParams(params),
    );
  },

  /** Tool failure ranking for the past 7 days, from felix_tool_failure_ranking. */
  analyticsToolFailures(): Promise<{ items: import("@/api/types").FelixToolFailure[] }> {
    return apiClient.get<{ items: import("@/api/types").FelixToolFailure[] }>(
      "/assistant/analytics/tool-failures",
    );
  },

  decisionsNeeded(): Promise<DecisionsNeeded> {
    return apiClient.get<DecisionsNeeded>("/assistant/decisions-needed");
  },

    /** Ask someone outside FTL a blocking question.
     *
     *  The CHANNEL IS CHOSEN BY THE SENDER, never inferred: a one-off answer
     *  link, or a plain email they reply to. Neither is a fallback for the
     *  other.
     *
     *  A failed send returns 502 and leaves the blocker UNCHANGED — the thread
     *  records when a message was sent, and recording a send that did not happen
     *  would leave someone waiting on a message nobody received. */
    askOutward(questionId: string, input: AskOutwardInput): Promise<AskOutwardResult> {
      return apiClient.post<AskOutwardResult>(
        `/assistant/decisions-needed/${encodeURIComponent(questionId)}/ask-outward`,
        input,
      );
    },

    /** The whole story of one blocker, in order, in one place.
     *
     *  Returns the ASSEMBLED THREAD, not the row. A caller given the columns
     *  would have to stitch them together and would be free to render Felix's
     *  reading without the verbatim reply beside it — the one thing that must
     *  not happen. `complete` says whether the blocker can explain how it was
     *  decided; render it, do not drop it. */
    blockerThread(questionId: string): Promise<BlockerThread> {
      return apiClient.get<BlockerThread>(
        `/assistant/decisions-needed/${encodeURIComponent(questionId)}/thread`,
      );
    },

  /** Felix's "start here / one root cause" focus for the Command Center Home. */
  commandCenterFocus(): Promise<CommandCenterFocus> {
    return apiClient.get<CommandCenterFocus>("/assistant/command-center-focus");
  },

  /**
   * Stream a Felix chat response via Server-Sent Events.
   *
   * Returns an async iterator of stream events. Text arrives
   * incrementally for real-time display; actions arrive when
   * tool use completes; done signals stream end.
   */
  async *chatStream(
    body: FelixChatRequest,
    signal?: AbortSignal,
  ): AsyncGenerator<FelixStreamEvent> {
    const baseUrl = import.meta.env.VITE_API_URL ?? "/api/v1";
    const url = `${baseUrl}/assistant/chat/stream`;

    const makeHeaders = () => {
      const t = getAccessToken();
      return {
        "Content-Type": "application/json",
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      };
    };

    let response = await fetch(url, {
      method: "POST",
      headers: makeHeaders(),
      body: JSON.stringify(withClientFeatures(body)),
      signal,
    });

    // KI-0110: streaming bypasses executeRequest, so 401 handling must be
    // explicit here.  Attempt the same refresh+retry cycle used by all other
    // API calls.  Without this, an expired token during a long session yields
    // "HTTP 401: Unauthorized" mid-chat with no recovery path.
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // AbortError from a cancelled request must not be swallowed;
        // surface it so the outer try/catch can handle it correctly.
        try {
          response = await fetch(url, {
            method: "POST",
            headers: makeHeaders(),
            body: JSON.stringify(withClientFeatures(body)),
            signal,
          });
        } catch (retryErr) {
          if ((retryErr as Error).name === "AbortError") return;
          throw retryErr;
        }
      }
      if (!response.ok) {
        // Yield the error event BEFORE navigating so the consumer can
        // process it before the page teardown races the unload.
        yield { type: "error", error: "Session expired. Please log in again." };
        clearTokens();
        window.location.href = "/login";
        return;
      }
    }

    if (!response.ok) {
      yield { type: "error", error: `HTTP ${response.status}: ${response.statusText}` };
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      yield { type: "error", error: "No response body" };
      return;
    }

    const decoder = new TextDecoder();
    let buffer = "";
    const STREAM_READ_TIMEOUT_MS = 90_000; // 90s timeout per chunk

    try {
      while (true) {
        // Add a timeout to prevent infinite hanging if server stops sending
        let timeoutId: ReturnType<typeof setTimeout>;
        const readPromise = reader.read();
        const timeoutPromise = new Promise<{ done: true; value: undefined }>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error("Stream read timeout")), STREAM_READ_TIMEOUT_MS);
        });
        try {
          const { done, value } = await Promise.race([readPromise, timeoutPromise]);
          clearTimeout(timeoutId!);
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
        } catch (err) {
          clearTimeout(timeoutId!);
          throw err;
        }

        // Parse SSE events from the buffer
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? ""; // Keep incomplete line in buffer

        let currentEventType = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEventType = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data) as FelixStreamEvent;
              // Use the SSE event type if the parsed data doesn't have one
              if (!parsed.type && currentEventType) {
                (parsed as Record<string, unknown>).type = currentEventType;
              }
              yield parsed;
            } catch {
              // Ignore unparseable data
            }
            currentEventType = "";
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  /**
   * Stream an autonomous Felix response via Server-Sent Events.
   *
   * Autonomous mode executes ALL tools server-side (including CRUD)
   * without frontend confirmation.  Returns action_complete events
   * as each entity is created/updated, plus progress events.
   */
  async *chatAutonomous(
    body: FelixChatRequest,
    signal?: AbortSignal,
  ): AsyncGenerator<FelixAutonomousEvent> {
    const baseUrl = import.meta.env.VITE_API_URL ?? "/api/v1";
    const url = `${baseUrl}/assistant/chat/autonomous`;

    const makeHeaders = () => {
      const t = getAccessToken();
      return {
        "Content-Type": "application/json",
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      };
    };

    let response = await fetch(url, {
      method: "POST",
      headers: makeHeaders(),
      body: JSON.stringify(withClientFeatures(body)),
      signal,
    });

    // KI-0110: same refresh+retry as chatStream — streaming bypasses
    // executeRequest so 401 must be handled explicitly.
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        try {
          response = await fetch(url, {
            method: "POST",
            headers: makeHeaders(),
            body: JSON.stringify(withClientFeatures(body)),
            signal,
          });
        } catch (retryErr) {
          if ((retryErr as Error).name === "AbortError") return;
          throw retryErr;
        }
      }
      if (!response.ok) {
        yield { type: "error", error: "Session expired. Please log in again." };
        clearTokens();
        window.location.href = "/login";
        return;
      }
    }

    if (!response.ok) {
      yield { type: "error", error: `HTTP ${response.status}: ${response.statusText}` };
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      yield { type: "error", error: "No response body" };
      return;
    }

    const decoder = new TextDecoder();
    let buffer = "";
    const STREAM_READ_TIMEOUT_MS = 1_800_000; // 30 min timeout for autonomous (batch approvals + conflict resolution can be very slow)

    try {
      while (true) {
        let timeoutId: ReturnType<typeof setTimeout>;
        const readPromise = reader.read();
        const timeoutPromise = new Promise<{ done: true; value: undefined }>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error("Autonomous stream read timeout")), STREAM_READ_TIMEOUT_MS);
        });
        try {
          const { done, value } = await Promise.race([readPromise, timeoutPromise]);
          clearTimeout(timeoutId!);
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
        } catch (err) {
          clearTimeout(timeoutId!);
          throw err;
        }

        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        let currentEventType = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEventType = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data) as FelixAutonomousEvent;
              if (!parsed.type && currentEventType) {
                (parsed as Record<string, unknown>).type = currentEventType;
              }
              yield parsed;
            } catch {
              // Ignore unparseable data
            }
            currentEventType = "";
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  /** Commit approved previews from a preview-mode autonomous session. */
  commitPreviews(body: {
    project_id?: string;
    previews: Record<string, unknown>[];
  }): Promise<{
    results: Array<{ success: boolean; entity_id?: string; detail?: string; error?: string }>;
    summary: { total: number; succeeded: number; failed: number };
  }> {
    return apiClient.post("/assistant/chat/autonomous/commit", body);
  },

  /** Fetch and consume pending proactive Felix messages. */
  pendingMessages(): Promise<{ messages: FelixPushMessage[] }> {
    return apiClient.get("/assistant/pending-messages");
  },

  /** List the user's background Felix tasks. */
  listTasks(status?: string): Promise<{ tasks: FelixTask[] }> {
    const params = status ? `?status=${status}` : "";
    return apiClient.get(`/assistant/tasks${params}`);
  },

  /** Get a specific background task with progress. */
  getTask(taskId: string): Promise<FelixTask> {
    return apiClient.get(`/assistant/tasks/${taskId}`);
  },

  /** Cancel a running or pending background task. */
  cancelTask(taskId: string): Promise<{ status: string }> {
    return apiClient.post(`/assistant/tasks/${taskId}/cancel`, {});
  },

  /**
   * Wave 10D — cooperatively cancel an autonomous goal-mode run.
   * Sets cancel_requested_at on the server; the autonomous loop
   * observes at the next wave boundary and exits cleanly. Idempotent
   * for already-terminal runs (returns status of the run without
   * erroring).
   */
  cancelGoalRun(goalRunId: string): Promise<{
    goal_run_id: string;
    status: string;
    cancel_requested_at: string | null;
    already_terminal: boolean;
  }> {
    return apiClient.post(`/assistant/goal/${goalRunId}/cancel`, {});
  },

  /**
   * Wave 10F — list recent autonomous goal runs for the observability
   * dashboard. Each row carries a ``failure_category`` derived from
   * stop_reason so the UI can group status rolls-ups without
   * duplicating taxonomy logic.
   */
  listGoalRuns(params?: {
    project_id?: string;
    mine_only?: boolean;
    status?: "RUNNING" | "COMPLETED" | "FAILED" | "CANCELLED";
    limit?: number;
    offset?: number;
  }): Promise<{
    runs: FelixGoalRun[];
    limit: number;
    offset: number;
  }> {
    const q = new URLSearchParams();
    if (params?.project_id) q.set("project_id", params.project_id);
    if (params?.mine_only) q.set("mine_only", "true");
    if (params?.status) q.set("status", params.status);
    if (params?.limit !== undefined) q.set("limit", String(params.limit));
    if (params?.offset !== undefined) q.set("offset", String(params.offset));
    const suffix = q.toString();
    return apiClient.get(`/assistant/goal/runs${suffix ? `?${suffix}` : ""}`);
  },

  /** Wave 10F — fetch a single goal run for the detail view. */
  getGoalRun(runId: string): Promise<FelixGoalRun> {
    return apiClient.get(`/assistant/goal/runs/${runId}`);
  },

  /**
   * Design Refresh Phase 6 — full detail: run summary + trace summary
   * + per-tool execution records. Used by the Autonomous Run Detail
   * page at /projects/:projectId/felix-runs/:runId.
   */
  getGoalRunDetail(runId: string): Promise<FelixGoalRunDetail> {
    return apiClient.get(`/assistant/goal/runs/${runId}/detail`);
  },

  /** Get a personalized briefing for the current user. */
  getBriefing(projectId?: string): Promise<FelixBriefing> {
    const params = projectId ? `?project_id=${projectId}` : "";
    return apiClient.get(`/assistant/briefing${params}`);
  },

  /** Dismiss a nudge type so it won't reappear for 24 hours. */
  dismissNudge(nudgeType: string): Promise<{ status: string }> {
    return apiClient.post("/assistant/nudges/dismiss", { nudge_type: nudgeType });
  },

  /**
   * Felix presence indicator — Teams-style dot.  Returns an
   * operational / degraded / outage signal combining the Anthropic
   * public status page with FTL's last-24h Felix error rate.
   */
  healthStatus(): Promise<FelixHealthStatus> {
    return apiClient.get("/felix/health-status");
  },
};

/** Response payload for assistantApi.healthStatus(). */
export interface FelixHealthStatus {
  status: "operational" | "degraded" | "outage" | "unknown";
  message: string;
  anthropic: { indicator: string; description: string };
  ftl: {
    error_rate_24h: number;
    errored_sessions: number;
    total_sessions: number;
  };
}

/** A background task created by Felix in autonomous mode. */
export interface FelixTask {
  id: string;
  org_id: string;
  user_id: string;
  project_id?: string;
  description: string;
  plan: { tool: string; input: Record<string, unknown> }[];
  status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED" | "CANCELLED";
  progress: number;
  total_steps: number;
  results: {
    success: boolean;
    entity_id?: string;
    entity_type?: string;
    title?: string;
    detail?: string;
    error?: string;
  }[];
  error?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

/** A proactive push message from Felix (background event notification). */
export interface FelixPushMessage {
  message: string;
  source: string;
  timestamp: string;
  /** Push message type — e.g. "qa_review_entity" for approval flow. */
  type?: string;
  /** Entity summary for qa_review_entity push messages. */
  entity_summary?: import("./types").QAReviewEntity;
  /** Entity type for qa_review_entity push messages. */
  entity_type?: string;
  /** Entity ID for qa_review_entity push messages. */
  entity_id?: string;
  /** Q&A session ID for qa_review_entity push messages. */
  session_id?: string;
  /** Document analysis completion fields. */
  document_type?: string;
  total_extracted?: number;
  auto_created?: number;
  needs_followup?: number;
  blocking_conflicts?: number;
  entity_counts?: Record<string, number>;
  /** Error message for failure push notifications. */
  error?: string;
}

/** Personalized Felix briefing returned by GET /assistant/briefing. */
export interface FelixBriefing {
  user_name: string;
  last_seen?: string;
  since_last_visit: {
    team_actions: number;
    completed_tasks: number;
    highlights: string[];
  };
  my_work: {
    open_tasks: { id: string; title: string; status: string; priority: string; project_id?: string | null }[];
    open_task_count: number;
  };
  /** Bucket → count. CAUTION: the live payload can piggyback non-numeric
   *  detail keys on the same record (e.g. `upcoming_meeting_details` is an
   *  ARRAY of meeting objects) — consumers must filter to numeric values
   *  before rendering counts (see AttentionPanel buildPaneItems). */
  needs_attention: Record<string, number>;
  suggested_actions: FelixSuggestion[];
  nudges: FelixNudge[];
}

/** A personalized suggestion chip from the briefing service. */
export interface FelixSuggestion {
  label: string;
  chip_text: string;
  priority: "high" | "medium" | "low";
  /** Optional deep-link: a chip that names one task carries the task's page
   *  (row 5ef53b44). Consumers must prefer the route when present (HOME-01 —
   *  mirrors the v2 envelope's required_actions.url). */
  action_url?: string | null;
}

/** A gentle nudge about something that needs attention. */
export interface FelixNudge {
  type: string;
  message: string;
  priority: "high" | "medium" | "low";
  /** Optional deep-link — see FelixSuggestion.action_url. */
  action_url?: string | null;
}

// ---------------------------------------------------------------------------
// Development Bridge (Felix ↔ Claude Code)
// ---------------------------------------------------------------------------
export interface DevStatus {
  dev_context: Record<string, unknown> | null;
  recent_changes: string | null;
}

export const devBridgeApi = {
  getStatus(): Promise<DevStatus> {
    return apiClient.get("/dev/status");
  },
};

export interface MemoryEntry {
  entry_type: string;
  summary: string;
  details?: Record<string, unknown>;
  source_type?: string;
  source_id?: string;
  source_label?: string;
}

export interface MemorySnapshot {
  snapshot: Record<string, unknown>;
  token_estimate: number;
  entries_included: number;
}

export const memoryApi = {
  getSnapshot(projectId: string): Promise<MemorySnapshot> {
    return apiClient.get(`/projects/${projectId}/memory/snapshot`);
  },
  addEntries(projectId: string, entries: MemoryEntry[]): Promise<{ created: number; ids: string[] }> {
    return apiClient.post(`/projects/${projectId}/memory/entries`, { entries });
  },
};

// ---------------------------------------------------------------------------
// User Feedback
// ---------------------------------------------------------------------------
export interface UserFeedbackTicket {
  id: string;
  org_id: string;
  project_id?: string;
  reporter_id: string;
  code: string;
  title: string;
  description: string;
  page_name?: string;
  page_path?: string;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  error_messages?: string[];
  category: string;
  severity: string;
  status: string;
  assigned_to?: string;
  resolution_notes?: string;
  resolution_type?: string;
  release_fixed_in?: string;
  known_issue_id?: string;
  known_issue_code?: string;
  known_issue_status?: string;
  report_count?: number;
  is_known_issue?: boolean;
  reporter_name?: string;
  reporter_email?: string;
  /** Joined from users on assigned_to — absent while the ticket is unrouted. */
  assignee_name?: string;
  assignee_email?: string;
  email_updates?: boolean;
  validated_at?: string;
  validated_by?: string;
  created_at: string;
  updated_at: string;
}

export interface KnownIssue {
  id: string;
  code: string;
  title: string;
  description: string;
  category: string;
  severity: string;
  status: string;
  resolution_notes?: string;
  resolution_type?: string;
  release_fixed_in?: string;
  report_count: number;
  affected_orgs: number;
  reports?: UserFeedbackTicket[];
  created_at: string;
  updated_at: string;
}

export interface FeedbackDiagnostics {
  /** Recent errors with timestamps (full error buffer, not just the trigger) */
  error_timeline?: Array<{ message: string; timestamp: number; source: string }>;
  /** Browser & environment snapshot */
  environment?: {
    user_agent: string;
    viewport: string;
    url: string;
    timestamp: string;
  };
  /** Project stats at the time of the error */
  project_context?: Record<string, unknown>;
  /** Q&A session state if Felix was in scope discovery mode */
  qa_state?: {
    session_id: string;
    entity_title: string;
    coverage_percent: number;
    round: number;
  } | null;
  /** Entity being viewed when the error occurred */
  entity_context?: {
    entity_type: string;
    entity_id: string;
    page: string;
    page_path: string;
  } | null;
}

export interface FeedbackCreateRequest {
  project_id?: string;
  title: string;
  description: string;
  page_name?: string;
  page_path?: string;
  steps_to_reproduce?: string;
  expected_behavior?: string;
  actual_behavior?: string;
  error_messages?: string[];
  category?: string;
  severity?: string;
  felix_conversation?: Array<{ role: string; content: string }>;
  diagnostics?: FeedbackDiagnostics;
}

export const feedbackApi = {
  create(body: FeedbackCreateRequest): Promise<UserFeedbackTicket> {
    return apiClient.post("/feedback/", body);
  },
  listMine(params?: { status?: string; limit?: number; offset?: number }): Promise<PaginatedResponse<UserFeedbackTicket>> {
    return apiClient.get("/feedback/mine", cleanParams(params));
  },
  get(ticketId: string): Promise<UserFeedbackTicket> {
    return apiClient.get(`/feedback/${ticketId}`);
  },
  listAll(params?: {
    status?: string;
    severity?: string;
    category?: string;
    project_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<UserFeedbackTicket>> {
    return apiClient.get("/feedback/admin/all", cleanParams(params));
  },
  update(ticketId: string, body: Record<string, unknown>): Promise<UserFeedbackTicket> {
    return apiClient.patch(`/feedback/${ticketId}`, body);
  },
  stats(): Promise<Record<string, unknown>> {
    return apiClient.get("/feedback/admin/stats");
  },
  listKnownIssues(params?: { status?: string; limit?: number; offset?: number }): Promise<PaginatedResponse<KnownIssue>> {
    return apiClient.get("/feedback/known-issues/list", cleanParams(params));
  },
  getKnownIssue(id: string): Promise<KnownIssue> {
    return apiClient.get(`/feedback/known-issues/${id}`);
  },
  resolveKnownIssue(id: string, body: {
    resolution_type: string;
    resolution_notes?: string;
    release_fixed_in?: string;
  }): Promise<KnownIssue> {
    return apiClient.post(`/feedback/known-issues/${id}/resolve`, body);
  },
  /** Reporter validates (closes) or reopens their own ticket. */
  validate(ticketId: string, body: { action: "validate" | "reopen"; notes?: string }): Promise<UserFeedbackTicket> {
    return apiClient.post(`/feedback/${ticketId}/validate`, body);
  },
  /** Reporter adds additional information to their own ticket. */
  addInfo(ticketId: string, body: {
    additional_notes?: string;
    steps_to_reproduce?: string;
    error_messages?: string[];
    severity?: string;
  }): Promise<UserFeedbackTicket> {
    return apiClient.patch(`/feedback/${ticketId}/add-info`, body);
  },
  /** Find a ticket by its code (e.g. FB-0042). Searches all org tickets. */
  findByCode(code: string): Promise<UserFeedbackTicket | null> {
    return apiClient.get<UserFeedbackTicket>(`/feedback/by-code/${encodeURIComponent(code)}`).catch(() => null);
  },
  /** Toggle email notifications for a specific ticket. */
  toggleEmailUpdates(ticketId: string, emailUpdates: boolean): Promise<UserFeedbackTicket> {
    return apiClient.patch(`/feedback/${ticketId}/email-preference`, { email_updates: emailUpdates });
  },
};

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export interface EntityCounts {
  total: number;
  by_status?: Record<string, number>;
  by_severity?: Record<string, number>;
  /** test_cases only: latest RUN result per case (passed / failed / blocked /
   *  skipped / not_run), from the shared verdict query. */
  by_verdict?: Record<string, number>;
}

export interface ProjectSummary {
  project_id: string;
  project_name: string;
  counts: Record<string, EntityCounts>;
  completion: Record<string, number>;
}

export interface ActivityTrendDay {
  date: string;
  creates: number;
  updates: number;
  deletes: number;
  total: number;
}

export const analyticsApi = {
  getProjectSummary(projectId: string): Promise<ProjectSummary> {
    return apiClient.get(`/analytics/project/${projectId}/summary`);
  },
  getActivityTrend(projectId: string, days?: number): Promise<{ trend: ActivityTrendDay[] }> {
    return apiClient.get(`/analytics/project/${projectId}/activity-trend`, { days });
  },
  getHealthHistory(projectId: string, limit?: number): Promise<{ assessments: HealthAssessment[] }> {
    return apiClient.get(`/analytics/project/${projectId}/health-history`, { limit });
  },
  getOrgSummary(): Promise<OrgSummary> {
    return apiClient.get<OrgSummary>("/analytics/org/summary");
  },
  getQualityMetrics(projectId: string): Promise<QualityMetrics> {
    return apiClient.get(`/analytics/project/${projectId}/quality-metrics`);
  },
  getVelocity(projectId: string, days?: number): Promise<VelocityData> {
    return apiClient.get(`/analytics/project/${projectId}/velocity`, { days });
  },
  getCoverageMatrix(projectId: string): Promise<CoverageMatrix> {
    return apiClient.get(`/analytics/project/${projectId}/coverage-matrix`);
  },
  getTeamContributions(projectId: string, days?: number): Promise<TeamContributions> {
    return apiClient.get(`/analytics/project/${projectId}/team-contributions`, { days });
  },
  getRiskMatrix(projectId: string): Promise<RiskMatrix> {
    return apiClient.get(`/analytics/project/${projectId}/risk-matrix`);
  },
  getProjectAICosts(projectId: string): Promise<ProjectAICosts> {
    return apiClient.get(`/analytics/project/${projectId}/ai-costs`);
  },
  getOrgAICosts(): Promise<OrgAICostSummary> {
    return apiClient.get("/analytics/org/ai-costs");
  },
  getOrgAICostsTrend(
    bucket: "hour" | "day" | "week" | "month" = "day",
    days: number = 30,
  ): Promise<OrgAICostTrend> {
    return apiClient.get(
      `/analytics/org/ai-costs/trend?bucket=${bucket}&days=${days}`,
    );
  },
  /** Authoritative AI cost straight from Anthropic's billing API. */
  getOrgAICostsAuthoritative(days: number = 30): Promise<OrgAuthoritativeCost> {
    return apiClient.get(
      `/analytics/org/ai-costs/authoritative?days=${days}`,
    );
  },
  getEntityAICost(entityType: string, entityId: string): Promise<EntityAICost> {
    return apiClient.get(`/analytics/entity/${entityType}/${entityId}/ai-cost`);
  },
  getDevelopmentTimeline(projectId: string): Promise<DevelopmentTimeline> {
    return apiClient.get(`/analytics/projects/${projectId}/development-timeline`);
  },
  getCostBreakdown(
    projectId: string,
    bucket: string = "daily",
    days: number = 30,
  ): Promise<ProjectCostBreakdown> {
    return apiClient.get(
      `/analytics/projects/${projectId}/cost-breakdown?bucket=${bucket}&days=${days}`,
    );
  },
  getOrgDevelopmentAnalytics(days: number = 30): Promise<OrgDevelopmentAnalytics> {
    return apiClient.get(`/analytics/org/development-analytics?days=${days}`);
  },
  getOrgQualityMetrics(): Promise<OrgQualityMetrics> {
    return apiClient.get("/analytics/org/quality-metrics");
  },
  getOrgTeamContributions(days: number = 30): Promise<OrgTeamContributions> {
    return apiClient.get(`/analytics/org/team-contributions?days=${days}`);
  },
  getOrgFinancialSummary(days: number = 30): Promise<OrgFinancialSummary> {
    return apiClient.get(`/analytics/org/financial-summary?days=${days}`);
  },
};

// ---------------------------------------------------------------------------
// Presence (real-time collaboration)
// ---------------------------------------------------------------------------

export interface PresenceUser {
  user_id: string;
  display_name: string;
  page: string;
}

export const presenceApi = {
  heartbeat(projectId: string, page: string): Promise<{ active_users: PresenceUser[] }> {
    return apiClient.post("/presence/heartbeat", { project_id: projectId, page });
  },
  leave(projectId: string): Promise<{ status: string }> {
    return apiClient.post("/presence/leave", { project_id: projectId, page: "" });
  },
  getActive(projectId: string): Promise<{ active_users: PresenceUser[] }> {
    return apiClient.get("/presence/active", { project_id: projectId });
  },
};

// ---------------------------------------------------------------------------
// Supervisor (platform admin agent monitoring)
// ---------------------------------------------------------------------------

export interface SupervisorExecution {
  execution_id: string;
  task_id: string;
  org_id: string;
  project_id: string;
  status: string;
  ci_status: string;
  branch: string | null;
  diff: string | null;
  generated_files: string[];
  modified_files: string[];
  pr_url: string | null;
  quality: {
    quality_score: number;
    lint_passed: boolean;
    tests_passed: boolean;
    assessment: string;
  } | null;
  token_usage: {
    input_tokens: number;
    output_tokens: number;
    total_cost_usd: number;
    num_turns: number;
    model: string;
  } | null;
  agent_log: string[];
  error: string | null;
  duration_ms: number;
  attempt: number;
  paused: boolean;
  current_turn: number;
  event_count: number;
  created_at: string;
  updated_at: string;
}

export interface SupervisorEvent {
  event_type: string;
  turn_number: number | null;
  data: Record<string, unknown>;
  timestamp: string;
}

export interface SupervisorDashboard {
  total_executions: number;
  active_executions: number;
  completed_executions: number;
  failed_executions: number;
  total_cost_usd: number;
  total_tokens: number;
  executions: SupervisorExecution[];
}

export interface SupervisorGuidance {
  id: string;
  execution_id: string;
  sent_by: string;
  message: string;
  guidance_type: string;
  status: string;
  acknowledged_at: string | null;
  applied_at?: string | null;
  created_at: string;
}

export interface SupervisorAnalytics {
  period_days: number;
  total_executions: number;
  completed: number;
  failed: number;
  timed_out: number;
  success_rate: number;
  avg_duration_ms: number;
  avg_cost_usd: number;
  total_cost_usd: number;
  avg_tokens: number;
  total_tokens: number;
  avg_turns: number;
  daily: Array<{
    day: string;
    total: number;
    completed: number;
    failed: number;
    cost: number;
  }>;
  models: Array<{
    model: string;
    count: number;
  }>;
}

export interface AgentQuery {
  query: string;
  context: string;
  turn_number: number;
  asked_at: string;
}

export interface BudgetGuardrails {
  id: string;
  org_id: string;
  execution_id: string;
  max_cost_usd: number | null;
  max_tokens: number | null;
  max_turns: number | null;
  violations: Array<{ reason: string; at: string }>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const supervisorApi = {
  getDashboard(): Promise<SupervisorDashboard> {
    return apiClient.get("/platform/supervisor/dashboard");
  },
  listExecutions(): Promise<SupervisorExecution[]> {
    return apiClient.get("/platform/supervisor/executions");
  },
  getExecution(executionId: string): Promise<SupervisorExecution> {
    return apiClient.get(`/platform/supervisor/executions/${executionId}`);
  },
  getEvents(executionId: string, since?: number, source?: string): Promise<SupervisorEvent[]> {
    return apiClient.get(`/platform/supervisor/executions/${executionId}/events`, {
      since: since ?? 0,
      source: source ?? "live",
    });
  },
  sendGuidance(executionId: string, message: string, guidanceType?: string): Promise<Record<string, unknown>> {
    return apiClient.post(`/platform/supervisor/executions/${executionId}/guidance`, {
      message,
      guidance_type: guidanceType ?? "DIRECTION",
    });
  },
  getGuidanceHistory(executionId: string, limit?: number): Promise<SupervisorGuidance[]> {
    return apiClient.get(`/platform/supervisor/executions/${executionId}/guidance`, {
      limit: limit ?? 50,
    });
  },
  pause(executionId: string): Promise<Record<string, unknown>> {
    return apiClient.post(`/platform/supervisor/executions/${executionId}/pause`);
  },
  resume(executionId: string): Promise<Record<string, unknown>> {
    return apiClient.post(`/platform/supervisor/executions/${executionId}/resume`);
  },
  abort(executionId: string, reason?: string): Promise<Record<string, unknown>> {
    return apiClient.post(`/platform/supervisor/executions/${executionId}/abort`, {
      reason: reason ?? "",
    });
  },
  setBudget(executionId: string, budget: {
    max_cost_usd?: number;
    max_tokens?: number;
    max_turns?: number;
  }): Promise<Record<string, unknown>> {
    return apiClient.post(`/platform/supervisor/executions/${executionId}/budget`, budget);
  },
  checkBudgets(): Promise<{ actions: Array<{ execution_id: string; action: string; reason: string }>; checked: number }> {
    return apiClient.post("/platform/supervisor/budget/check");
  },
  getBudget(executionId: string): Promise<BudgetGuardrails | { execution_id: string; budget: null }> {
    return apiClient.get(`/platform/supervisor/executions/${executionId}/budget`);
  },
  getAnalytics(days?: number): Promise<SupervisorAnalytics> {
    return apiClient.get("/platform/supervisor/analytics", { days: days ?? 30 });
  },
  requestCheckpoint(executionId: string, checkpointName: string, description?: string): Promise<Record<string, unknown>> {
    return apiClient.post(`/platform/supervisor/executions/${executionId}/checkpoint`, {
      checkpoint_name: checkpointName,
      description: description ?? "",
    });
  },
  acknowledgeGuidance(executionId: string, guidanceId: string, status?: string): Promise<Record<string, unknown>> {
    return apiClient.post(`/platform/supervisor/executions/${executionId}/guidance/acknowledge`, {
      guidance_id: guidanceId,
      status: status ?? "ACKNOWLEDGED",
    });
  },
  getAgentQuery(executionId: string): Promise<{ execution_id: string; pending_query: AgentQuery | null }> {
    return apiClient.get(`/platform/supervisor/executions/${executionId}/query`);
  },
  respondToAgent(executionId: string, response: string, responder?: string): Promise<Record<string, unknown>> {
    return apiClient.post(`/platform/supervisor/executions/${executionId}/dialogue/respond`, {
      response,
      responder: responder ?? "felix",
    });
  },
  retryExecution(executionId: string, feedback: string): Promise<Record<string, unknown>> {
    return apiClient.post(`/platform/supervisor/executions/${executionId}/retry`, {
      feedback,
    });
  },
};

// ---------------------------------------------------------------------------
// Platform Feedback API (platform admin — cross-org)
// ---------------------------------------------------------------------------

export const platformFeedbackApi = {
  listTickets(params?: {
    status?: string;
    severity?: string;
    category?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<UserFeedbackTicket>> {
    return apiClient.get("/platform/feedback/tickets", cleanParams(params));
  },
  stats(): Promise<Record<string, unknown>> {
    return apiClient.get("/platform/feedback/stats");
  },
  listKnownIssues(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<KnownIssue>> {
    return apiClient.get("/platform/feedback/known-issues", cleanParams(params));
  },
  updateTicket(ticketId: string, body: Record<string, unknown>): Promise<UserFeedbackTicket> {
    return apiClient.patch(`/platform/feedback/tickets/${ticketId}`, body);
  },
  resetAll(): Promise<{ deleted_tickets: number; deleted_known_issues: number }> {
    return apiClient.delete("/platform/feedback/reset");
  },
};

// ---------------------------------------------------------------------------
// Platform Configuration (platform admin — system-wide settings)
// ---------------------------------------------------------------------------

export interface PlatformConfigCategory {
  [category: string]: Setting[];
}

export interface PlatformConfigResponse {
  total: number;
  categories: PlatformConfigCategory;
}

export interface OrgOverrideGroup {
  org_id: string;
  org_name: string | null;
  org_slug: string | null;
  overrides: Setting[];
}

export interface OrgOverridesResponse {
  total: number;
  organizations: OrgOverrideGroup[];
}

/**
 * The agent-loop monitor. app/api/v1/agent_loop_monitor.py exists explicitly
 * because "visibility must NOT require a terminal" — until row ca2fcad9 no
 * frontend file referenced it, which is the same problem one layer up.
 */
export const agentLoopApi = {
  health(): Promise<import("@/api/types").AgentLoopHealth> {
    return apiClient.get<import("@/api/types").AgentLoopHealth>(
      "/platform/agent-loop/health",
    );
  },

  /** The numbers behind the grades, "exposed so they can be argued with". */
  thresholds(): Promise<Record<string, number>> {
    return apiClient.get<Record<string, number>>(
      "/platform/agent-loop/thresholds",
    );
  },
};

export const platformConfigApi = {
  listAll(): Promise<PlatformConfigResponse> {
    return apiClient.get("/platform/configuration");
  },
  getByCategory(category: string): Promise<{ category: string; items: Setting[] }> {
    return apiClient.get(`/platform/configuration/${category}`);
  },
  updateSystemDefault(settingId: string, value: unknown): Promise<Setting> {
    return apiClient.put(`/platform/configuration/settings/${settingId}`, { value });
  },
  listOverrides(params?: {
    org_id?: string;
    category?: string;
  }): Promise<OrgOverridesResponse> {
    return apiClient.get("/platform/configuration/overrides", cleanParams(params));
  },
  deleteOverride(settingId: string): Promise<{ status: string; setting_id: string }> {
    return apiClient.delete(`/platform/configuration/overrides/${settingId}`);
  },
  setOrgOverride(orgId: string, settingId: string, value: unknown): Promise<Setting> {
    return apiClient.put(`/platform/configuration/overrides/${orgId}/${settingId}`, { value });
  },
};

// ---------------------------------------------------------------------------
// Org-admin Felix Steering controls (056fc731 AC3)
// Org admins tune Felix for THEIR org via org-level felix_steering overrides;
// the runtime honours them via _get_settings_batch's org fallthrough. Reset
// clears the org overrides back to the FTL platform-admin defaults.
// ---------------------------------------------------------------------------

/** A Felix Steering value as seen by an org admin. Deliberately carries only
 *  the value + typing — NO mechanism description/display_name (those are
 *  platform-admin internal IP). The customer UI supplies its own plain copy. */
export interface OrgFelixSetting {
  setting_key: string;
  value_type: string;
  constraints?: { min?: number; max?: number; allowed_values?: string[] } | null;
  /** The FTL platform-admin system default (what a reset reverts to). */
  default_value: unknown;
  /** The effective value the runtime reads for this org (override or default). */
  setting_value: unknown;
  /** True when this org has diverged from the FTL default for this knob. */
  is_overridden: boolean;
}

export const orgFelixSettingsApi = {
  /** The caller org's effective Felix Steering settings. */
  get(): Promise<{ items: OrgFelixSetting[]; override_count: number }> {
    return apiClient.get("/org/felix-settings");
  },
  /** Set/update this org's override for one Felix Steering key. */
  set(settingKey: string, value: unknown): Promise<Setting> {
    return apiClient.put(`/org/felix-settings/${settingKey}`, { value });
  },
  /** Reset to FTL defaults — clear all of this org's Felix Steering overrides. */
  reset(): Promise<{ reset: boolean; cleared: string[]; count: number }> {
    return apiClient.delete("/org/felix-settings");
  },
};

// ---------------------------------------------------------------------------
// Email Infrastructure API
// ---------------------------------------------------------------------------

export const emailApi = {
  /** Get email provider status and domain verification info */
  getStatus(): Promise<import("./types").EmailStatus> {
    return apiClient.get("/platform/email/status");
  },
  /** Send a test email to verify configuration */
  sendTestEmail(to: string): Promise<import("./types").EmailTestResult> {
    return apiClient.post("/platform/email/test", { to });
  },
  /** Trigger automated domain setup (Resend + DigitalOcean DNS) */
  setupDomain(domain: string): Promise<import("./types").EmailSetupResult> {
    return apiClient.post("/platform/email/setup", { domain });
  },
  /** Re-trigger domain verification without full setup */
  reverify(): Promise<import("./types").EmailReverifyResult> {
    return apiClient.post("/platform/email/reverify", {});
  },
};

// ---------------------------------------------------------------------------
// NDA (Non-Disclosure Agreement) API
// ---------------------------------------------------------------------------

export const ndaApi = {
  /** Check if current user needs to sign an NDA */
  getStatus(): Promise<NDAStatus> {
    return apiClient.get("/nda/status");
  },
  /** Get the active NDA document for signing */
  getDocument(): Promise<NDADocument> {
    return apiClient.get("/nda/document");
  },
  /** Sign the active NDA */
  sign(data: NDASignRequest): Promise<{ message: string; signature_id: string; signed_at: string; version: string }> {
    return apiClient.post("/nda/sign", data);
  },
  /** Get external NDA for signing (public, token-based). 409 signed, 410 expired/revoked. */
  getExternal(token: string): Promise<NDAExternalInvitation> {
    return apiClient.get(`/nda/external/${encodeURIComponent(token)}`);
  },
  /** Sign external NDA (public, token-based); the signed copy is emailed and returned */
  signExternal(token: string, data: NDAExternalSignRequest): Promise<NDAExternalSignResult> {
    return apiClient.post(`/nda/external/${encodeURIComponent(token)}/sign`, data);
  },
  /** The signer's own download of the signed PDF (public, token-based) */
  downloadSignedCopyByToken(token: string): Promise<{ blob: Blob; filename: string }> {
    return apiClient.blob(`/nda/external/${encodeURIComponent(token)}/signed-copy`);
  },

  // Senders (platform admins and FTL-org members)
  /** Resend: a new link; the old one is revoked */
  resendInvitation(invitationId: string): Promise<{ message: string; invitation_id: string; signing_url: string }> {
    return apiClient.post(`/nda/admin/invitations/${invitationId}/resend`, {});
  },
  /** A sender's download of a signed copy */
  downloadSignedCopy(invitationId: string): Promise<{ blob: Blob; filename: string }> {
    return apiClient.blob(`/nda/admin/invitations/${invitationId}/signed-copy`);
  },
  /** Each external invitee of a meeting and their NDA status */
  meetingAttendees(meetingId: string): Promise<NDAMeetingAttendees> {
    return apiClient.get(`/nda/meetings/${meetingId}/attendees`);
  },
  /** Send the NDA to every external invitee of a meeting, plus extra emails */
  sendToMeeting(meetingId: string, data: { extra_emails?: string[]; custom_message?: string; document_id?: string }): Promise<NDAMeetingSendResult> {
    return apiClient.post(`/nda/meetings/${meetingId}/send`, data);
  },

  // Platform admin
  /** List all NDA signatures (platform admin) */
  listSignatures(params?: { limit?: number; offset?: number; version?: string }): Promise<PaginatedResponse<NDASignature>> {
    return apiClient.get("/nda/admin/signatures", cleanParams(params));
  },
  /** List all NDA invitations (platform admin) */
  listInvitations(params?: { limit?: number; offset?: number; status_filter?: string }): Promise<PaginatedResponse<NDAInvitation>> {
    return apiClient.get("/nda/admin/invitations", cleanParams(params));
  },
  /** Send NDA invitation to external party (platform admin) */
  sendInvitation(data: NDAInviteRequest): Promise<{ message: string; invitation_id: string; signing_url: string; expires_at: string }> {
    return apiClient.post("/nda/admin/invite", data);
  },
  /** Revoke a pending NDA invitation (platform admin) */
  revokeInvitation(invitationId: string): Promise<{ message: string }> {
    return apiClient.post(`/nda/admin/invitations/${invitationId}/revoke`, {});
  },
  /** List all NDA document versions (platform admin) */
  listDocuments(): Promise<{ items: NDADocumentVersion[] }> {
    return apiClient.get("/nda/admin/documents");
  },
  /** Get NDA signing statistics (platform admin) */
  getStats(): Promise<NDAStats> {
    return apiClient.get("/nda/admin/stats");
  },
};

// ---------------------------------------------------------------------------
// Branding API — org-level color palette
// ---------------------------------------------------------------------------

export const brandingApi = {
  /** Get the resolved palette ID for the current user's org */
  getPalette(): Promise<{ palette_id: string }> {
    return apiClient.get("/branding/palette");
  },
  /** Set the palette for the current user's org (ORG_ADMIN only) */
  setPalette(paletteId: string, customHex?: string): Promise<{ palette_id: string; org_id: string; custom_hex?: string }> {
    return apiClient.put("/branding/palette", {
      palette_id: paletteId,
      ...(customHex ? { custom_hex: customHex } : {}),
    });
  },
};

// ---------------------------------------------------------------------------
// Access Requests API — platform admin management
// ---------------------------------------------------------------------------

import type {
  AccessRequest,
  AccessRequestDetail,
  AccessRequestAccount,
  AccessRequestCreatePayload,
} from "./types";

export const accessRequestsApi = {
  /** List all access requests with optional filters */
  list(params?: {
    status_filter?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: AccessRequest[]; total: number }> {
    return apiClient.get("/platform/access-requests", params);
  },
  /** Get full detail for a single access request */
  get(requestId: string): Promise<AccessRequestDetail> {
    return apiClient.get(`/platform/access-requests/${requestId}`);
  },
  /** Approve or deny an access request */
  review(
    requestId: string,
    body: { status: "APPROVED" | "DENIED"; notes?: string; denied_reason?: string },
  ): Promise<AccessRequest> {
    return apiClient.patch(`/platform/access-requests/${requestId}`, body);
  },
  /** Resend the credentials email (optionally with a password reset) */
  resendEmail(
    requestId: string,
    options?: { reset_password?: boolean },
  ): Promise<{ message: string; password_reset: boolean }> {
    return apiClient.post(
      `/platform/access-requests/${requestId}/resend-email`,
      options || {},
    );
  },
  /** Reset the password for the provisioned user */
  resetPassword(
    requestId: string,
    options?: { new_password?: string },
  ): Promise<{ message: string; temp_password: string; email_sent: boolean }> {
    return apiClient.post(
      `/platform/access-requests/${requestId}/reset-password`,
      options || {},
    );
  },
  /** Get provisioned account details */
  getAccount(requestId: string): Promise<AccessRequestAccount> {
    return apiClient.get(`/platform/access-requests/${requestId}/account`);
  },
  /** Submit a new access request (public, no auth) */
  submit(payload: AccessRequestCreatePayload): Promise<{ message: string }> {
    return apiClient.post("/auth/request-access", payload);
  },
};

// ---------------------------------------------------------------------------
// Transcription (Speech-to-Text via Deepgram)
// ---------------------------------------------------------------------------

export const transcriptionApi = {
  /** Transcribe an audio blob using Deepgram Nova-2 */
  async transcribe(
    audioBlob: Blob,
    options?: { language?: string; model?: string },
  ): Promise<TranscriptionResult> {
    const formData = new FormData();
    // Derive file extension from MIME type to avoid content-type mismatch
    const ext = audioBlob.type.includes("ogg") ? "ogg"
      : audioBlob.type.includes("mp4") ? "mp4"
      : audioBlob.type.includes("mpeg") ? "mp3"
      : audioBlob.type.includes("wav") ? "wav"
      : audioBlob.type.includes("flac") ? "flac"
      : "webm";
    formData.append("audio", audioBlob, `recording.${ext}`);
    const params = new URLSearchParams();
    if (options?.language) params.set("language", options.language);
    if (options?.model) params.set("model", options.model);
    const qs = params.toString();
    const url = `/transcription/transcribe${qs ? `?${qs}` : ""}`;
    return apiClient.upload<TranscriptionResult>(url, formData);
  },

  /** Check whether STT is available on the server */
  status(): Promise<TranscriptionStatus> {
    return apiClient.get<TranscriptionStatus>("/transcription/status");
  },
};

// ---------------------------------------------------------------------------
// Q&A Templates
// ---------------------------------------------------------------------------

export const qaTemplatesApi = {
  /** List all available project types with category summaries */
  listTypes(): Promise<QAProjectTypeInfo[]> {
    return apiClient.get<QAProjectTypeInfo[]>('/qa-templates/types');
  },

  /** Get categories for a specific project type (merged with org overrides) */
  getCategories(projectType: string, entityType?: string): Promise<QACategoryDetail[]> {
    const params: Record<string, string> = {};
    if (entityType) params.entity_type = entityType;
    return apiClient.get<QACategoryDetail[]>(`/qa-templates/types/${projectType}/categories`, params);
  },

  /** Get template coverage progress for a Q&A session */
  getProgress(sessionId: string): Promise<QATemplateProgress> {
    return apiClient.get<QATemplateProgress>(`/qa-templates/sessions/${sessionId}/progress`);
  },

  /** AI-classify project type from name/description */
  classify(name: string, description?: string): Promise<{ project_type: string }> {
    return apiClient.post<{ project_type: string }>('/qa-templates/classify', { name, description });
  },

  /** Create/update an org override for a Q&A category */
  upsertOverride(body: {
    project_type: string;
    category_key: string;
    enabled?: boolean;
    sort_order?: number;
    custom_questions?: Array<{ question: string; dimension?: string; rationale?: string }>;
  }): Promise<Record<string, unknown>> {
    return apiClient.put<Record<string, unknown>>('/qa-templates/overrides', body);
  },

  /** Delete an org override for a Q&A category */
  deleteOverride(projectType: string, categoryKey: string): Promise<{ status: string }> {
    return apiClient.delete<{ status: string }>(`/qa-templates/overrides/${projectType}/${categoryKey}`);
  },
};

// ---------------------------------------------------------------------------
// Active Process Monitoring (Platform Admin)
// ---------------------------------------------------------------------------

export const processesApi = {
  /** List all active processes with token/cost data */
  list(includeCompleted = false): Promise<ActiveProcessesResponse> {
    return apiClient.get<ActiveProcessesResponse>("/platform/processes", {
      include_completed: includeCompleted,
    });
  },
  /** Cancel a running process */
  cancel(processId: string, processType: string): Promise<{ status: string; type: string; id: string }> {
    return apiClient.post(`/platform/processes/${processId}/cancel`, {
      type: processType,
    });
  },
};

// ---------------------------------------------------------------------------
// Platform Development Analytics (Platform Admin)
// ---------------------------------------------------------------------------

export const platformDevAnalyticsApi = {
  /** Cross-org development analytics (platform admin only) */
  get(days: number = 30): Promise<PlatformDevelopmentAnalytics> {
    return apiClient.get<PlatformDevelopmentAnalytics>(
      `/platform/analytics/development?days=${days}`,
    );
  },
};

export const platformAnalyticsApi = {
  getOverview(): Promise<PlatformOverview> {
    return apiClient.get("/platform/analytics/overview");
  },
  getFinancialTrends(days: number = 90): Promise<PlatformFinancialTrends> {
    return apiClient.get(`/platform/analytics/financial-trends?days=${days}`);
  },
  getOrgComparison(days: number = 30): Promise<PlatformOrgComparison> {
    return apiClient.get(`/platform/analytics/org-comparison?days=${days}`);
  },
  getUserAnalytics(days: number = 90): Promise<PlatformUserAnalytics> {
    return apiClient.get(`/platform/analytics/user-analytics?days=${days}`);
  },
};

// ---------------------------------------------------------------------------
// Project Favorites & Dashboard Configuration
// ---------------------------------------------------------------------------

import type {
  ProjectFavorite,
  FavoriteToggleResponse,
  DashboardConfig as DashboardConfigType,
  ExecutiveSummary,
} from "./types";

export const favoritesApi = {
  /** List the current user's favorited project IDs */
  list(): Promise<{ items: ProjectFavorite[] }> {
    return apiClient.get<{ items: ProjectFavorite[] }>("/projects/favorites");
  },

  /** Add a project to favorites (idempotent) */
  add(projectId: string): Promise<FavoriteToggleResponse> {
    return apiClient.post<FavoriteToggleResponse>(`/projects/${projectId}/favorite`, {});
  },

  /** Remove a project from favorites */
  remove(projectId: string): Promise<FavoriteToggleResponse> {
    return apiClient.delete<FavoriteToggleResponse>(`/projects/${projectId}/favorite`);
  },
};

// ---------------------------------------------------------------------------
// Dashboard Configuration
// ---------------------------------------------------------------------------

export const dashboardApi = {
  /** Get the current user's dashboard widget configuration */
  getConfig(): Promise<DashboardConfigType> {
    return apiClient.get<DashboardConfigType>("/user-profiles/me/dashboard-config");
  },

  /** Save the current user's dashboard widget configuration */
  saveConfig(config: DashboardConfigType): Promise<DashboardConfigType> {
    return apiClient.put<DashboardConfigType>("/user-profiles/me/dashboard-config", config);
  },

  /** Get the executive summary for the dashboard widget */
  getExecutiveSummary(): Promise<ExecutiveSummary> {
    return apiClient.get<ExecutiveSummary>("/assistant/executive-summary");
  },

  /**
   * Aggregated "coming due" list for the logged-in user — meeting action
   * items, QA action items, impl tasks, and defects merged into one feed
   * by the backend at ``GET /dashboard/my-work``.
   */
  listMyWork(params?: { limit?: number; include_no_due_date?: boolean; kind?: MyWorkKind }): Promise<MyWorkResponse> {
    return apiClient.get<MyWorkResponse>("/dashboard/my-work", cleanParams(params));
  },
};

// ---------------------------------------------------------------------------
// User Org Memberships (multi-org)
// ---------------------------------------------------------------------------

/** Platform staff who can be given a support ticket. */
export interface AssignableAdmin {
  id: string;
  email: string;
  display_name?: string;
}

export const supportAssigneeApi = {
  /**
   * The routable population for a support ticket.
   *
   * Filtered SERVER-SIDE via ``platform_admin_only`` rather than by narrowing a
   * page of users here — a client-side filter over one page silently omits any
   * admin past the page boundary and the picker looks complete regardless.
   */
  listAssignable(): Promise<{ items: AssignableAdmin[] }> {
    return apiClient.get<{ items: AssignableAdmin[] }>(
      "/platform/users?limit=200&platform_admin_only=true&include_test=true",
    );
  },
};

export const userOrgsApi = {
  /** List all org memberships for a user */
  list(userId: string): Promise<{ items: UserOrgMembership[] }> {
    return apiClient.get<{ items: UserOrgMembership[] }>(`/platform/users/${userId}/orgs`);
  },

  /** Add a user to an organization */
  add(userId: string, orgId: string, role?: string): Promise<UserOrgMembership> {
    return apiClient.post<UserOrgMembership>(`/platform/users/${userId}/orgs`, {
      org_id: orgId,
      role: role || "MEMBER",
    });
  },

  /** Remove a user from an organization (cannot remove primary) */
  remove(userId: string, orgId: string): Promise<{ status: string }> {
    return apiClient.delete<{ status: string }>(`/platform/users/${userId}/orgs/${orgId}`);
  },

  /** Set an org as the user's primary organization */
  setPrimary(userId: string, orgId: string): Promise<{ status: string; note: string }> {
    return apiClient.patch<{ status: string; note: string }>(`/platform/users/${userId}/orgs/${orgId}`);
  },
};

// ---------------------------------------------------------------------------
// Platform Release Notes API
// ---------------------------------------------------------------------------

export const releaseNotesApi = {
  /** List release note entries with optional date filter */
  list(params?: import("./types").ReleaseNotesListParams): Promise<import("./types").ReleaseNotesResponse> {
    return apiClient.get("/platform/release-notes", cleanParams(params));
  },

  /** Export release notes as markdown — returns raw text for download */
  async exportMarkdown(params?: { date_from?: string; date_to?: string }): Promise<string> {
    const qs = new URLSearchParams();
    if (params?.date_from) qs.set("date_from", params.date_from);
    if (params?.date_to) qs.set("date_to", params.date_to);
    const qsStr = qs.toString() ? `?${qs.toString()}` : "";
    const token = getAccessToken();
    const base = import.meta.env.VITE_API_URL ?? "/api/v1";
    const res = await fetch(`${base}/platform/release-notes/export${qsStr}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Export failed: ${res.statusText}`);
    return res.text();
  },

  /** Email release notes to a recipient */
  email(data: import("./types").EmailReleaseNotesRequest): Promise<import("./types").EmailReleaseNotesResult> {
    return apiClient.post("/platform/release-notes/email", data);
  },

  /** Ingest release notes from CHANGELOG.md into the database */
  ingest(): Promise<{ success: boolean; entries_ingested: number }> {
    return apiClient.post("/platform/release-notes/ingest");
  },
};

// ---------------------------------------------------------------------------
// Org-Accessible Release Notes API (all authenticated users)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Organization profile (GET/PUT /organizations/current) — name/description/
// logo/timezone. Previously had no frontend caller at all; A7 wires the
// timezone writer (config-audit §3) through here.
// ---------------------------------------------------------------------------
export const organizationApi = {
  getCurrent(): Promise<import("./types").OrgProfile> {
    return apiClient.get<import("./types").OrgProfile>("/organizations/current");
  },
  updateCurrent(
    body: Partial<
      Pick<import("./types").OrgProfile, "name" | "description" | "logo_url" | "timezone">
    >,
  ): Promise<import("./types").OrgProfile> {
    return apiClient.put<import("./types").OrgProfile>("/organizations/current", body);
  },
  // A6 (config-audit §3): set/clear the branding logo (branding|logo_url) — the
  // key the export header + Topbar render. `dataUri` is a bounded image data URI
  // (the caller converts the picked file via FileReader).
  setLogo(dataUri: string): Promise<{ branding_logo_url: string | null }> {
    return apiClient.put("/organizations/current/logo", { logo_data_uri: dataUri });
  },
  clearLogo(): Promise<{ branding_logo_url: string | null }> {
    return apiClient.delete("/organizations/current/logo");
  },
};

export const orgReleaseNotesApi = {
  /** List release note entries with optional date filter */
  list(params?: import("./types").ReleaseNotesListParams): Promise<import("./types").ReleaseNotesResponse> {
    return apiClient.get("/release-notes", cleanParams(params));
  },

  /** Export release notes as markdown — returns raw text for download */
  async exportMarkdown(params?: { date_from?: string; date_to?: string }): Promise<string> {
    const qs = new URLSearchParams();
    if (params?.date_from) qs.set("date_from", params.date_from);
    if (params?.date_to) qs.set("date_to", params.date_to);
    const qsStr = qs.toString() ? `?${qs.toString()}` : "";
    const token = getAccessToken();
    const base = import.meta.env.VITE_API_URL ?? "/api/v1";
    const res = await fetch(`${base}/release-notes/export${qsStr}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Export failed: ${res.statusText}`);
    return res.text();
  },
};

// ---------------------------------------------------------------------------
// Anthropic Workspaces API (Platform Admin)
// ---------------------------------------------------------------------------

export const workspacesApi = {
  /** List all Anthropic workspaces with org details */
  list(): Promise<{ workspaces: import("./types").AnthropicWorkspace[]; total: number }> {
    return apiClient.get("/platform/workspaces");
  },

  /** Get workspace for a specific org */
  get(orgId: string): Promise<import("./types").AnthropicWorkspace> {
    return apiClient.get(`/platform/workspaces/${orgId}`);
  },

  /** Create a new Anthropic workspace for an org */
  create(data: import("./types").CreateWorkspaceRequest): Promise<import("./types").AnthropicWorkspace> {
    return apiClient.post("/platform/workspaces", data);
  },

  /** Store an encrypted API key for a workspace */
  storeApiKey(orgId: string, data: import("./types").StoreWorkspaceApiKeyRequest): Promise<{ status: string; api_key_hint: string; workspace_id: string }> {
    return apiClient.post(`/platform/workspaces/${orgId}/api-key`, data);
  },

  /** Remove the API key (revert org to global key) */
  removeApiKey(orgId: string): Promise<{ status: string; org_id: string }> {
    return apiClient.delete(`/platform/workspaces/${orgId}/api-key`);
  },

  /** Archive a workspace */
  archive(orgId: string): Promise<{ status: string; workspace_id: string }> {
    return apiClient.post(`/platform/workspaces/${orgId}/archive`);
  },

  /** Trigger immediate usage sync for a workspace */
  syncUsage(orgId: string): Promise<Record<string, unknown>> {
    return apiClient.post(`/platform/workspaces/${orgId}/sync`);
  },

  /** Get usage history for a workspace */
  getUsage(orgId: string, params?: { start_date?: string; end_date?: string }): Promise<import("./types").WorkspaceUsageResponse> {
    return apiClient.get(`/platform/workspaces/${orgId}/usage`, cleanParams(params));
  },
};

/** List all workspaces */
export function useWorkspaces() {
  return useQuery({
    queryKey: ["workspaces"],
    queryFn: () => workspacesApi.list(),
    staleTime: 30_000,
  });
}

/** Get workspace for a specific org */
export function useWorkspace(orgId: string | undefined) {
  return useQuery({
    queryKey: ["workspace", orgId],
    queryFn: () => workspacesApi.get(orgId!),
    enabled: !!orgId,
    staleTime: 30_000,
  });
}

/** Get workspace usage history */
export function useWorkspaceUsage(orgId: string | undefined, params?: { start_date?: string; end_date?: string }) {
  return useQuery({
    queryKey: ["workspace-usage", orgId, params],
    queryFn: () => workspacesApi.getUsage(orgId!, params),
    enabled: !!orgId,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Conversation Ratings (user-scoped)
// ---------------------------------------------------------------------------

export const conversationRatingsApi = {
  submit(body: import("./types").ConversationRatingCreate): Promise<import("./types").ConversationRating> {
    return apiClient.post("/conversation-ratings", body);
  },
  updateComment(ratingId: string, comment: string): Promise<import("./types").ConversationRating> {
    return apiClient.patch(`/conversation-ratings/${ratingId}`, { user_comment: comment });
  },
};

// ---------------------------------------------------------------------------
// Platform Conversation Insights (platform admin)
// ---------------------------------------------------------------------------

export const platformActivationFunnelApi = {
  get(days = 30): Promise<import("./types").ActivationFunnel> {
    return apiClient.get("/platform/activation-funnel", cleanParams({ days }));
  },
};

export const platformConversationInsightsApi = {
  list(params?: {
    rating?: string;
    page_context?: string;
    days?: number;
    limit?: number;
    offset?: number;
    unprocessed?: boolean;
  }): Promise<import("./types").PaginatedResponse<import("./types").ConversationRating>> {
    return apiClient.get("/platform/conversation-insights", cleanParams(params ?? {}));
  },

  stats(days?: number): Promise<import("./types").ConversationInsightsStats> {
    return apiClient.get("/platform/conversation-insights/stats", cleanParams({ days }));
  },

  getRating(id: string): Promise<import("./types").ConversationRating> {
    return apiClient.get(`/platform/conversation-insights/${id}`);
  },

  listCases(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<import("./types").PaginatedResponse<import("./types").ImprovementCase>> {
    return apiClient.get("/platform/conversation-insights/cases", cleanParams(params ?? {}));
  },

  getCase(id: string): Promise<import("./types").ImprovementCase> {
    return apiClient.get(`/platform/conversation-insights/cases/${id}`);
  },

  updateCase(id: string, body: { status: string }): Promise<import("./types").ImprovementCase> {
    return apiClient.patch(`/platform/conversation-insights/cases/${id}`, body);
  },

  generateCase(body?: {
    rating_ids?: string[];
    days?: number;
  }): Promise<import("./types").ImprovementCase & { ratings_linked: number }> {
    return apiClient.post("/platform/conversation-insights/generate-case", body ?? {});
  },
};

// ───────────────────────────────────────────────────────────────────
// Email Composition
// ───────────────────────────────────────────────────────────────────

export const emailCompositionApi = {
  compose(
    req: import("./types").ComposeEmailRequest,
  ): Promise<import("./types").ComposeEmailResponse> {
    return apiClient.post("/emails/compose", req);
  },

  listSent(params?: {
    limit?: number;
    offset?: number;
  }): Promise<import("./types").PaginatedResponse<import("./types").SentEmail>> {
    return apiClient.get("/emails/sent", cleanParams(params ?? {}));
  },

  getEmail(id: string): Promise<import("./types").SentEmail> {
    return apiClient.get(`/emails/${id}`);
  },

  uploadAttachment(
    emailId: string,
    file: File,
  ): Promise<import("./types").EmailAttachment> {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.upload(`/emails/${emailId}/attachments`, formData);
  },

  shareEntity(
    req: import("./types").ShareEntityRequest,
  ): Promise<import("./types").ComposeEmailResponse> {
    return apiClient.post("/emails/share-entity", req);
  },

  searchUsers(
    q: string,
    limit?: number,
  ): Promise<{ users: import("./types").UserSearchResult[] }> {
    return apiClient.get("/emails/users/search", cleanParams({ q, limit }));
  },

  getEntityCommunications(
    entityType: string,
    entityId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<import("./types").PaginatedResponse<import("./types").EntityCommunication>> {
    return apiClient.get(
      `/emails/entity/${entityType}/${entityId}`,
      cleanParams(params ?? {}),
    );
  },
};

export const platformEmailApi = {
  broadcast(
    req: import("./types").BroadcastEmailRequest,
  ): Promise<import("./types").BroadcastEmailResponse> {
    return apiClient.post("/platform/emails/broadcast", req);
  },

  history(params?: {
    limit?: number;
    offset?: number;
  }): Promise<
    import("./types").PaginatedResponse<{
      id: string;
      subject: string;
      status: string;
      recipient_count: number;
      sender_name: string;
      sender_email: string;
      org_name: string;
      sent_at: string | null;
      created_at: string;
    }>
  > {
    return apiClient.get("/platform/emails/history", cleanParams(params ?? {}));
  },
};

// --- Conversation History ---

export const conversationHistoryApi = {
  getStorageStats(): Promise<import("./types").ConversationStorageStats> {
    return apiClient.get("/conversation-history/storage");
  },

  getEntitySummary(
    entityType: string,
    entityId: string,
  ): Promise<import("./types").EntityConversationSummary> {
    return apiClient.get(
      `/conversation-history/entity/${entityType}/${entityId}/summary`,
    );
  },

  purge(
    req: import("./types").ConversationPurgeRequest,
  ): Promise<import("./types").ConversationPurgeResponse> {
    return apiClient.post("/conversation-history/purge", req);
  },
};

export const platformConversationStorageApi = {
  getStats(): Promise<import("./types").PlatformConversationStorageStats> {
    return apiClient.get("/platform/conversation-storage");
  },
};

// --- Conversation Sessions (Server-Side Storage) ---

export const conversationSessionsApi = {
  list(params?: {
    project_id?: string;
    search?: string;
    visibility?: string;
    limit?: number;
    offset?: number;
  }): Promise<import("./types").ConversationSessionListResponse> {
    return apiClient.get("/conversation-history/sessions", cleanParams(params ?? {}));
  },

  get(sessionId: string): Promise<import("./types").ConversationSessionDetail> {
    return apiClient.get(`/conversation-history/sessions/${sessionId}`);
  },

  save(
    sessionId: string,
    data: import("./types").SaveConversationSessionRequest,
  ): Promise<Record<string, unknown>> {
    return apiClient.put(`/conversation-history/sessions/${sessionId}`, data);
  },

  delete(sessionId: string): Promise<{ deleted: boolean }> {
    return apiClient.delete(`/conversation-history/sessions/${sessionId}`);
  },

  updateVisibility(
    sessionId: string,
    visibility: "private" | "team" | "public",
  ): Promise<{ visibility: string }> {
    return apiClient.patch(`/conversation-history/sessions/${sessionId}/visibility`, {
      visibility,
    });
  },

  listTeam(params?: {
    project_id?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<import("./types").ConversationSessionListResponse> {
    return apiClient.get("/conversation-history/sessions/team", cleanParams(params ?? {}));
  },
};

// ---------------------------------------------------------------------------
// Knowledge Base
// ---------------------------------------------------------------------------

export const knowledgeBaseApi = {
  list(
    projectId: string,
    params?: { status?: string; limit?: number; offset?: number },
  ): Promise<import("./types").KBDocumentListResponse> {
    return apiClient.get(
      `/projects/${projectId}/knowledge-base`,
      cleanParams(params ?? {}),
    );
  },

  async upload(projectId: string, file: File): Promise<{ id: string; status: string; file_name: string }> {
    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("ftl_access_token");
    const base = import.meta.env.VITE_API_URL ?? "/api/v1";
    const resp = await fetch(`${base}/projects/${projectId}/knowledge-base`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!resp.ok) throw new Error(`Upload failed: ${resp.status}`);
    return resp.json();
  },

  get(projectId: string, documentId: string): Promise<import("./types").KBDocumentDetail> {
    return apiClient.get(`/projects/${projectId}/knowledge-base/${documentId}`);
  },

  delete(projectId: string, documentId: string): Promise<{ deleted: boolean }> {
    return apiClient.delete(`/projects/${projectId}/knowledge-base/${documentId}`);
  },

  search(
    projectId: string,
    query: string,
    limit?: number,
  ): Promise<import("./types").KBSearchResponse> {
    return apiClient.get(
      `/projects/${projectId}/knowledge-base/search`,
      cleanParams({ q: query, limit }),
    );
  },

  getEntitySources(
    entityType: string,
    entityId: string,
  ): Promise<import("./types").KBEntitySource[]> {
    return apiClient.get(`/knowledge-base/entity-sources/${entityType}/${entityId}`);
  },
};

// ---------------------------------------------------------------------------
// Module hooks (React Query)
// ---------------------------------------------------------------------------

export function useModules(params?: ListParams & { module_type?: string }) {
  return useQuery({
    queryKey: ["modules", params],
    queryFn: () => modulesApi.list(params),
  });
}

export function useModule(id: string | undefined) {
  return useQuery({
    queryKey: ["modules", id],
    queryFn: () => modulesApi.get(id!),
    enabled: !!id,
  });
}

// --- Unified Search (hybrid semantic + keyword) ---

export const searchApi = {
  search(params: {
    q: string;
    types?: string;
    project_id?: string;
    limit?: number;
    threshold?: number;
    mode?: "semantic" | "keyword" | "hybrid";
  }): Promise<import("./types").UnifiedSearchResponse> {
    return apiClient.get<import("./types").UnifiedSearchResponse>(
      "/search",
      cleanParams(params),
    );
  },
};

// ---------------------------------------------------------------------------
// Felix Behavioral Benchmarks API (Platform Admin)
// ---------------------------------------------------------------------------

export interface BehaviorTrendDay {
  date: string;
  avg_quality: number | null;
  total_operations: number;
  validation_pass_rate: number;
  drift_events: number;
  total_cost_usd: number;
  total_tokens: number;
  felix_operations: number;
  cost_per_operation: number;
}

export interface BehaviorTrends {
  period_days: number;
  org_id: string | null;
  daily: BehaviorTrendDay[];
}

export interface LearningInsights {
  period_days: number;
  failure_categories: Record<string, number>;
  quality_trend: Array<{
    date: string;
    avg_score: number;
    sample_size: number;
    pass_rate: number;
  }>;
  improvement_rate: number;
  negative_rating_count: number;
}

export interface CandidateScenario {
  id: string;
  rating_id: string | null;
  user_message: string;
  felix_response: string;
  tools_called: string[];
  failure_category: string;
  status: string;
  promoted_scenario_id: string | null;
  created_at: string | null;
}

export const felixBenchmarksApi = {
  getTrends(params?: {
    days?: number;
    org_id?: string;
  }): Promise<BehaviorTrends> {
    return apiClient.get("/platform/felix-benchmarks/trends", cleanParams(params ?? {}));
  },

  getInsights(params?: { days?: number }): Promise<LearningInsights> {
    return apiClient.get("/platform/felix-benchmarks/insights", cleanParams(params ?? {}));
  },

  getCandidates(params?: {
    status?: string;
    limit?: number;
  }): Promise<CandidateScenario[]> {
    return apiClient.get("/platform/felix-benchmarks/candidates", cleanParams(params ?? {}));
  },

  extractFromRating(ratingId: string): Promise<{ extracted: boolean; candidate?: CandidateScenario }> {
    return apiClient.post("/platform/felix-benchmarks/extract", { rating_id: ratingId });
  },
};

// ---------------------------------------------------------------------------
// User Guide Screenshots & Generation
// ---------------------------------------------------------------------------

export interface GuideScreenshot {
  id: string;
  page_path: string;
  page_title: string;
  section_ref: string;
  screenshot_url: string | null;
  annotated_url: string | null;
  annotations: Array<{
    x: number;
    y: number;
    width?: number;
    height?: number;
    label: string;
    number: number;
    type: "callout" | "highlight";
    color?: string;
  }>;
  alt_text: string;
  viewport_width: number;
  viewport_height: number;
  created_at: string;
}

export const userGuidesApi = {
  captureScreenshots(body: {
    project_id: string;
    guide_type?: "user" | "admin";
    base_url?: string;
  }): Promise<{ status: string; message: string; screenshot_count: number }> {
    return apiClient.post("/user-guides/capture", body);
  },

  listScreenshots(
    projectId: string,
    section?: string,
  ): Promise<{ items: GuideScreenshot[]; total: number }> {
    const params: Record<string, string> = {};
    if (section) params.section = section;
    return apiClient.get(`/user-guides/screenshots/${projectId}`, params);
  },

  generateGuide(body: {
    project_id: string;
    guide_type?: "user" | "admin";
    custom_instructions?: string;
  }): Promise<{ status: string; message: string; doc_type: string }> {
    return apiClient.post("/user-guides/generate", body);
  },
};

// ---------------------------------------------------------------------------
// Q&A Action Items
// ---------------------------------------------------------------------------
export const qaActionItemsApi = {
  listMine(params?: Record<string, string>): Promise<{ items: QAActionItem[]; total: number; limit: number; offset: number }> {
    return apiClient.get("/qa-action-items/mine", params);
  },
  list(params?: Record<string, string>): Promise<{ items: QAActionItem[]; total: number; limit: number; offset: number }> {
    return apiClient.get("/qa-action-items", params);
  },
  get(id: string): Promise<QAActionItem> {
    return apiClient.get(`/qa-action-items/${id}`);
  },
  respond(id: string, body: { response_text: string }): Promise<QAActionItem> {
    return apiClient.post(`/qa-action-items/${id}/respond`, body);
  },
  verify(id: string): Promise<QAActionItem> {
    return apiClient.post(`/qa-action-items/${id}/verify`);
  },
  reassign(id: string, body: { assigned_to: string }): Promise<QAActionItem> {
    return apiClient.post(`/qa-action-items/${id}/reassign`, body);
  },
  cancel(id: string): Promise<QAActionItem> {
    return apiClient.post(`/qa-action-items/${id}/cancel`);
  },
  slaSummary(params?: Record<string, string>): Promise<QAActionItemSLASummary> {
    return apiClient.get("/qa-action-items/sla-summary", params);
  },
  recommend(id: string): Promise<{ recommendations: ActionItemRecommendation[] }> {
    return apiClient.post(`/qa-action-items/${id}/recommend`);
  },
};

// ---------------------------------------------------------------------------
// Onboarding API
// ---------------------------------------------------------------------------

export const meetingBotApi = {
  joinMeeting: (meetingId: string) =>
    apiClient.post<MeetingBotSession>(`/meetings/${meetingId}/bot/join`),
  leaveMeeting: (meetingId: string) =>
    apiClient.post<MeetingBotSession>(`/meetings/${meetingId}/bot/leave`),
  resetSession: (meetingId: string) =>
    apiClient.post<{ reset: number; meeting_id: string }>(`/meetings/${meetingId}/bot/reset`),
  getSession: (meetingId: string) =>
    apiClient.get<MeetingBotSession | { status: string; meeting_id: string }>(`/meetings/${meetingId}/bot/session`),
  listActive: () =>
    apiClient.get<{ sessions: MeetingBotSession[] }>('/meetings/bot/active-sessions'),
  getCalendarSync: () =>
    apiClient.get<{ items: CalendarSyncState[]; total: number }>('/meeting-bot/calendar-sync'),
  triggerCalendarSync: () =>
    apiClient.post<CalendarSyncResult>('/meeting-bot/calendar-sync/trigger'),
  scheduleAutoJoin: (meetingId: string) =>
    apiClient.post<{ status: string; meeting_id: string }>(`/meeting-bot/schedule-auto-join/${meetingId}`),
  // Meeting Intelligence endpoints
  getTranscript: (sessionId: string) =>
    apiClient.get<{ items: MeetingTranscriptSegment[]; total: number }>(`/meeting-bot/${sessionId}/transcript`),
  getIntelligence: (sessionId: string) =>
    apiClient.get<MeetingIntelligenceState>(`/meeting-bot/${sessionId}/intelligence`),
  getReports: (sessionId: string) =>
    apiClient.get<{ items: MeetingReport[]; total: number }>(`/meeting-bot/${sessionId}/reports`),
  getReport: (sessionId: string, reportType: 'CLIENT' | 'INTERNAL') =>
    apiClient.get<MeetingReport>(`/meeting-bot/${sessionId}/reports/${reportType}`),
  // Delegates to the meeting-keyed route server-side, and now returns the
  // same per-report outcome shape — a caller that only reads `distributed`
  // cannot tell a total failure from a successful send to nobody.
  distributeReports: (sessionId: string) =>
    apiClient.post<MeetingReportDistribution>(
      `/meeting-bot/${sessionId}/reports/distribute`
    ),
  sendChat: (sessionId: string, message: string) =>
    apiClient.post<{ status: string; message_length?: number }>(`/meeting-bot/${sessionId}/chat`, { message }),
  checkInfrastructure: () =>
    apiClient.get<{ ready: boolean; bot_enabled: boolean; livekit_configured: boolean; google_bot_email_set: boolean; playwright_available: boolean; worker_available: boolean; deepgram_available: boolean; issues: Array<{ component: string; message: string }> }>('/meeting-bot/infrastructure'),
};

export const onboardingApi = {
  getStatus(): Promise<OnboardingStatus> {
    return apiClient.get("/onboarding/status");
  },
  complete(data: OnboardingSubmission): Promise<OnboardingCompleteResponse> {
    return apiClient.post("/onboarding/complete", data);
  },
  getProfile(): Promise<OnboardingProfileResponse> {
    return apiClient.get("/onboarding/profile");
  },
  retake(): Promise<{ status: string; message: string }> {
    return apiClient.post("/onboarding/retake");
  },
  skip(): Promise<{ status: string; message: string }> {
    return apiClient.post("/onboarding/skip");
  },
  getCommunicationStyle(): Promise<CommunicationStyleResponse> {
    return apiClient.get("/onboarding/communication-style");
  },
};

// ---------------------------------------------------------------------------
// Platform Infrastructure (DigitalOcean billing & monitoring)
// ---------------------------------------------------------------------------

export const infrastructureApi = {
  getSummary(): Promise<InfrastructureSummary> {
    return apiClient.get("/platform/infrastructure/summary");
  },
  getBalance(): Promise<InfrastructureBalance> {
    return apiClient.get("/platform/infrastructure/balance");
  },
  getBillingHistory(limit: number = 25): Promise<{ billing_history: BillingHistoryEntry[] }> {
    return apiClient.get("/platform/infrastructure/billing-history", { limit });
  },
  getDroplets(): Promise<{ droplets: InfrastructureDroplet[] }> {
    return apiClient.get("/platform/infrastructure/droplets");
  },
  getVolumes(): Promise<{ volumes: InfrastructureVolume[] }> {
    return apiClient.get("/platform/infrastructure/volumes");
  },
  getDropletMetrics(dropletId: number, metricType: string, minutes: number = 60) {
    return apiClient.get(`/platform/infrastructure/droplets/${dropletId}/metrics/${metricType}`, { minutes });
  },
  getInvoices(limit: number = 24): Promise<{ invoices: InfrastructureInvoice[] }> {
    return apiClient.get("/platform/infrastructure/invoices", { limit });
  },
  getInvoiceSummary(invoiceUuid: string): Promise<InfrastructureInvoiceSummary> {
    return apiClient.get(`/platform/infrastructure/invoices/${invoiceUuid}/summary`);
  },
  getBillingSummary(): Promise<InfrastructureBillingSummary> {
    return apiClient.get("/platform/infrastructure/billing-summary");
  },
  /** Org-admin-accessible billing summary (subset of platform-admin data). */
  getOrgBillingSummary(): Promise<InfrastructureBillingSummary> {
    return apiClient.get("/org/infrastructure/billing-summary");
  },
  /** Org-admin-accessible current balance. */
  getOrgBalance(): Promise<{ configured: boolean } & Partial<InfrastructureBalance>> {
    return apiClient.get("/org/infrastructure/balance");
  },
};

// ======================================================================
// Google Workspace Integration
// ======================================================================

export const googleWorkspaceApi = {
  getConfig() {
    return apiClient.get("/google-workspace/config");
  },
  saveConfig(body: Record<string, unknown>) {
    return apiClient.put("/google-workspace/config", body);
  },
  getStatus() {
    return apiClient.get("/google-workspace/status");
  },
  getUserStatus() {
    return apiClient.get("/google-workspace/user-status");
  },
  testConnection() {
    return apiClient.post("/google-workspace/test", {});
  },
  getConnectedUsers() {
    return apiClient.get("/google-workspace/connected-users");
  },
};

export const gmailApi = {
  listMessages(params?: Record<string, string | number | boolean | null | undefined>) {
    return apiClient.get("/gmail/messages", params);
  },
  getMessage(messageId: string) {
    return apiClient.get(`/gmail/messages/${messageId}`);
  },
  getThread(threadId: string) {
    return apiClient.get(`/gmail/threads/${threadId}`);
  },
  send(body: { to: string[]; subject: string; body_text?: string; body_html?: string; cc?: string[]; bcc?: string[]; thread_id?: string }) {
    return apiClient.post("/gmail/send", body);
  },
  reply(messageId: string, body: { body_text?: string; body_html?: string; reply_all?: boolean }) {
    return apiClient.post(`/gmail/messages/${messageId}/reply`, body);
  },
  forward(messageId: string, body: { to: string[]; body_text?: string }) {
    return apiClient.post(`/gmail/messages/${messageId}/forward`, body);
  },
  listLabels() {
    return apiClient.get("/gmail/labels");
  },
  modifyLabels(body: { message_ids: string[]; add_labels?: string[]; remove_labels?: string[] }) {
    return apiClient.post("/gmail/labels/modify", body);
  },
  markRead(messageId: string) {
    return apiClient.post(`/gmail/messages/${messageId}/read`, {});
  },
  starMessage(messageId: string) {
    return apiClient.post(`/gmail/messages/${messageId}/star`, {});
  },
  archiveMessage(messageId: string) {
    return apiClient.post(`/gmail/messages/${messageId}/archive`, {});
  },
  trashMessage(messageId: string) {
    return apiClient.post(`/gmail/messages/${messageId}/trash`, {});
  },
  listDrafts() {
    return apiClient.get("/gmail/drafts");
  },
  saveDraft(body: { to?: string[]; subject?: string; body_text?: string; body_html?: string; thread_id?: string }) {
    return apiClient.post("/gmail/drafts", body);
  },
  sync() {
    return apiClient.post("/gmail/sync", {});
  },
  getUnreadCount() {
    return apiClient.get("/gmail/unread-count");
  },
};

export const calendarApi = {
  listEvents(params?: Record<string, string | number | boolean | null | undefined>): Promise<{ events: CalendarEvent[] }> {
    return apiClient.get("/calendar/events", params) as Promise<{ events: CalendarEvent[] }>;
  },
  getEvent(eventId: string, calendarId?: string): Promise<CalendarEvent> {
    return apiClient.get(`/calendar/events/${eventId}`, calendarId ? { calendar_id: calendarId } : undefined) as Promise<CalendarEvent>;
  },
  createEvent(body: CreateCalendarEventRequest | Record<string, unknown>) {
    return apiClient.post("/calendar/events", body);
  },
  updateEvent(eventId: string, body: UpdateCalendarEventRequest | Record<string, unknown>) {
    return apiClient.patch(`/calendar/events/${eventId}`, body);
  },
  deleteEvent(eventId: string) {
    return apiClient.delete(`/calendar/events/${eventId}`);
  },
  checkAvailability(body: { attendees: string[]; start_time: string; end_time: string }) {
    return apiClient.post("/calendar/availability", body);
  },
  findFreeSlots(body: { attendees: string[]; date: string; duration_minutes?: number }) {
    return apiClient.post("/calendar/free-slots", body);
  },
  listCalendars() {
    return apiClient.get("/calendar/calendars");
  },
  upsertFelixConfig(eventId: string, body: Record<string, unknown>) {
    return apiClient.put(`/calendar/events/${eventId}/felix-config`, body);
  },
  /**
   * One-stop personal timeline — projects every date-bearing FTL entity
   * (meetings, tasks, milestones, objectives, sprints, action items,
   * domains, Felix tasks) into a normalized event list. `zoom` only sets
   * the default [start, end] window when the explicit bounds are omitted;
   * `sources` (comma-separated) optionally narrows the projected set.
   * Backend: GET /calendar/timeline (calendar_service.get_timeline).
   */
  timeline(params: {
    start?: string;
    end?: string;
    zoom?: "day" | "week" | "month" | "year";
    sources?: string;
  }): Promise<CalendarTimelineResponse> {
    return apiClient.get("/calendar/timeline", params) as Promise<CalendarTimelineResponse>;
  },
  /**
   * Comms hub — pending meeting invites, inbound action-item assignments,
   * and unread notifications, all in the same event shape (bucket="comms").
   * Backend: GET /calendar/comms (calendar_service.get_comms).
   */
  comms(params: {
    start?: string;
    end?: string;
    zoom?: "day" | "week" | "month" | "year";
  }): Promise<CalendarTimelineResponse> {
    return apiClient.get("/calendar/comms", params) as Promise<CalendarTimelineResponse>;
  },
};

export const contactsApi = {
  list(params?: Record<string, string | number | boolean | null | undefined>) {
    return apiClient.get("/contacts", params);
  },
  search(query: string, maxResults?: number) {
    return apiClient.get("/contacts/search", { q: query, max_results: maxResults });
  },
  recent(limit?: number) {
    return apiClient.get("/contacts/recent", { limit });
  },
  get(contactId: string) {
    return apiClient.get(`/contacts/${contactId}`);
  },
  create(body: {
    display_name: string;
    email?: string;
    phone?: string;
    company?: string;
    title?: string;
    notes?: string;
    tags?: string[];
    source?: string;
  }) {
    return apiClient.post("/contacts", body);
  },
  update(
    contactId: string,
    body: Partial<{
      display_name: string;
      email: string | null;
      phone: string | null;
      company: string | null;
      title: string | null;
      notes: string | null;
      tags: string[];
    }>,
  ) {
    return apiClient.patch(`/contacts/${contactId}`, body);
  },
  remove(contactId: string) {
    return apiClient.delete(`/contacts/${contactId}`);
  },
  getDirectory(params?: Record<string, string | number | boolean | null | undefined>) {
    return apiClient.get("/contacts/directory", params);
  },
  sync() {
    return apiClient.post("/contacts/sync", {});
  },
};

export const driveApi = {
  listFiles(params?: Record<string, string | number | boolean | null | undefined>) {
    return apiClient.get("/drive/files", params);
  },
  getFile(fileId: string) {
    return apiClient.get(`/drive/files/${fileId}`);
  },
  searchFiles(query: string, maxResults?: number) {
    return apiClient.get("/drive/search", { q: query, max_results: maxResults });
  },
  upload(file: File, folderId?: string) {
    const formData = new FormData();
    formData.append("file", file);
    if (folderId) formData.append("folder_id", folderId);
    return apiClient.upload("/drive/upload", formData);
  },
  download(fileId: string) {
    return apiClient.blob(`/drive/files/${fileId}/download`);
  },
  rename(fileId: string, name: string) {
    return apiClient.patch(`/drive/files/${fileId}`, { name });
  },
  createFolder(body: { name: string; parent_folder_id?: string }) {
    return apiClient.post("/drive/folders", body);
  },
  shareFile(fileId: string, body: { email: string; role?: string; message?: string }) {
    return apiClient.post(`/drive/files/${fileId}/share`, body);
  },
  attachToEntity(body: { google_file_id: string; entity_type: string; entity_id: string; project_id?: string }) {
    return apiClient.post("/drive/attach", body);
  },
  detachFromEntity(referenceId: string) {
    return apiClient.delete(`/drive/attach/${referenceId}`);
  },
  getEntityFiles(entityType: string, entityId: string) {
    return apiClient.get(`/drive/entity/${entityType}/${entityId}`);
  },
  exportToDrive(body: { document_content: string; document_name: string; folder_id?: string }) {
    return apiClient.post("/drive/export", body);
  },
  moveFile(fileId: string, body: { new_parent_id: string; old_parent_id?: string }) {
    return apiClient.post(`/drive/files/${fileId}/move`, body);
  },
  trashFile(fileId: string) {
    return apiClient.delete(`/drive/files/${fileId}`);
  },
};

// ---------------------------------------------------------------------------
// Content Enhancement (AI-powered quality improvement)
// ---------------------------------------------------------------------------

export const contentEnhancementApi = {
  enhance(body: { entity_type: string; entity_id: string; project_id: string }) {
    return apiClient.post("/content-enhancement/enhance", body);
  },
  apply(body: { entity_type: string; entity_id: string; project_id: string; changes: Record<string, unknown> }) {
    return apiClient.post("/content-enhancement/apply", body);
  },
};

// ---------------------------------------------------------------------------
// Workspace Hub (aggregated summary)
// ---------------------------------------------------------------------------

export const workspaceApi = {
  getSummary() {
    return apiClient.get("/workspace/summary");
  },
};

// ---------------------------------------------------------------------------
// Dashboard aggregation — types for the command-center "my work" endpoint.
// (The endpoint method itself is attached to the existing `dashboardApi`
// defined above so we have one object per domain.)
// ---------------------------------------------------------------------------

export type MyWorkKind =
  | "meeting_action_item"
  | "qa_action_item"
  | "task"
  | "defect"
  | "change_request"
  | "document_draft"
  | "user_story_review";

export interface MyWorkItem {
  id: string;
  kind: MyWorkKind;
  title: string;
  status: string;
  priority: string | null;
  due_date: string | null;
  project_id: string | null;
  project_name: string | null;
  href: string;
  created_at: string;
  updated_at: string;
}

export interface MyWorkBreakdown {
  meeting_action_items: number;
  qa_action_items: number;
  tasks: number;
  defects: number;
  change_requests: number;
  document_drafts: number;
  user_story_reviews: number;
}

export interface MyWorkResponse {
  items: MyWorkItem[];
  total: number;
  breakdown: MyWorkBreakdown;
}

// ---------------------------------------------------------------------------
// Legal documents & support contact form
// ---------------------------------------------------------------------------

export interface LegalDocMeta {
  slug: string;
  title: string;
  category: string;
  version: string;
  effective_date: string;
}

export interface LegalDoc extends LegalDocMeta {
  content: string;
}

export interface SupportContactRequest {
  name: string;
  email: string;
  topic: string;
  message: string;
}

/**
 * One signup document whose version in force has moved past what this user
 * accepted. `accepted_version` is null when they never accepted it at all.
 */
export interface LegalOutstandingDoc {
  document_slug: string;
  current_version: string;
  accepted_version: string | null;
}

export interface LegalAcceptanceStatus {
  outstanding: LegalOutstandingDoc[];
  up_to_date: boolean;
}

import type { KnownLimitsResponse } from "./types";

export const legalApi = {
  list() {
    return apiClient.get<{ items: LegalDocMeta[] }>("/legal/documents");
  },
  get(slug: string) {
    return apiClient.get<LegalDoc>(`/legal/${slug}`);
  },
  /** The customer lines of docs/KNOWN_LIMITS.md, for the public /known-limits page (row 16439c4c). */
  knownLimits() {
    return apiClient.get<KnownLimitsResponse>("/legal/known-limits");
  },
  /**
   * Which signup documents this user must (re-)accept because the version in
   * force moved since they last agreed. `up_to_date` is the gate.
   */
  acceptanceStatus() {
    return apiClient.get<LegalAcceptanceStatus>("/legal/acceptance-status");
  },
  /**
   * Record acceptance of the CURRENT version. Omit `slugs` to accept everything
   * outstanding, which is what the re-consent prompt does.
   */
  accept(slugs?: string[]) {
    // `recorded` is the list of SLUGS actually written — the service skips any
    // doc whose current version is "unversioned", so it can be shorter than
    // what was asked for.
    return apiClient.post<{ recorded: string[] }>(
      "/legal/accept",
      slugs ? { slugs } : {},
    );
  },
  submitContact(body: SupportContactRequest) {
    return apiClient.post<{ status: string }>("/support/contact", body);
  },
};

// ── QA Agent ────────────────────────────────────────────────────────────────

export const qaAgentApi = {
  listRuns(projectId?: string, params?: { status?: string; limit?: number; offset?: number }) {
    return apiClient.get("/qa-agent/runs", projectId ? { project_id: projectId, ...params } : params);
  },
  getRun(runId: string) {
    return apiClient.get(`/qa-agent/runs/${runId}`);
  },
  createRun(data: { project_id?: string; run_type?: string; target_routes?: string[] }) {
    return apiClient.post("/qa-agent/runs", data);
  },
  cancelRun(runId: string) {
    return apiClient.post(`/qa-agent/runs/${runId}/cancel`);
  },
  getRunResults(runId: string, params?: { category?: string; status?: string; limit?: number }) {
    return apiClient.get(`/qa-agent/runs/${runId}/results`, params);
  },
  getRunCategorySummary(runId: string): Promise<{ categories: Array<{ category: string; total: number; passed: number; failed: number; errors: number }> }> {
    return apiClient.get(`/qa-agent/runs/${runId}/category-summary`);
  },
  getQualityAssessment(runId: string): Promise<import("./types").QAQualityAssessment | null> {
    return apiClient.get(`/qa-agent/runs/${runId}/quality-assessment`).then(
      (data) => data as import("./types").QAQualityAssessment,
      () => null,
    );
  },
  listRecommendations(runId: string): Promise<{ items: import("./types").QAQualityRecommendation[]; total: number }> {
    return apiClient.get(`/qa-agent/runs/${runId}/recommendations`) as Promise<{ items: import("./types").QAQualityRecommendation[]; total: number }>;
  },
  approveRecommendation(runId: string, recId: string): Promise<{ status: string; defect_id: string }> {
    return apiClient.post(`/qa-agent/runs/${runId}/recommendations/${recId}/approve`) as Promise<{ status: string; defect_id: string }>;
  },
  declineRecommendation(runId: string, recId: string, reason?: string): Promise<{ status: string }> {
    return apiClient.post(`/qa-agent/runs/${runId}/recommendations/${recId}/decline`, { reason: reason || "" }) as Promise<{ status: string }>;
  },
  getQualityTrends(params?: { limit?: number }): Promise<{ runs: Array<{ run_id: string; date: string; quality_score: number | null; quality_status: string | null }>; total: number }> {
    return apiClient.get("/qa-agent/quality-trends", params) as Promise<{ runs: Array<{ run_id: string; date: string; quality_score: number | null; quality_status: string | null }>; total: number }>;
  },
  getRunReport(runId: string, format: "json" | "html" = "json") {
    return apiClient.get(`/qa-agent/runs/${runId}/report`, { format });
  },
  listBaselines(projectId: string) {
    return apiClient.get("/qa-agent/baselines", { project_id: projectId });
  },
  deleteBaseline(baselineId: string) {
    return apiClient.delete(`/qa-agent/baselines/${baselineId}`);
  },
  listSchedules(projectId?: string) {
    return apiClient.get("/qa-agent/schedules", projectId ? { project_id: projectId } : undefined);
  },
  createSchedule(data: { project_id: string; cron_expression: string; config?: Record<string, unknown> }) {
    return apiClient.post("/qa-agent/schedules", data);
  },
  deleteSchedule(scheduleId: string) {
    return apiClient.delete(`/qa-agent/schedules/${scheduleId}`);
  },
  checkGate(projectId: string) {
    return apiClient.get(`/qa-agent/gate/${projectId}`);
  },
  reviewTestResult(runId: string, testId: string, data: { review_status: string; review_notes?: string }) {
    return apiClient.patch(`/qa-agent/runs/${runId}/results/${testId}/review`, data);
  },
  exportRunCsv(runId: string) {
    const token = localStorage.getItem("ftl_access_token");
    fetch(`/api/v1/qa-agent/runs/${runId}/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Export failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `qa-agent-run-${runId.slice(0, 8)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => console.error("CSV export failed:", err));
  },
  exportRunMarkdown(runId: string, failuresOnly = true) {
    const token = localStorage.getItem("ftl_access_token");
    fetch(`/api/v1/qa-agent/runs/${runId}/export/markdown?failures_only=${failuresOnly}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Export failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `qa-run-${runId.slice(0, 8)}-errors.md`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => console.error("Markdown export failed:", err));
  },

  // ── Diagnostics Dashboard ──────────────────────────────────────
  getTrends(params?: { limit?: number; project_id?: string }) {
    return apiClient.get("/qa-agent/trends", params);
  },
  getCategoryTrends(params?: { limit?: number; project_id?: string }) {
    return apiClient.get("/qa-agent/category-trends", params);
  },
  getRecurringFailures(params?: { min_consecutive?: number; limit?: number; project_id?: string }) {
    return apiClient.get("/qa-agent/recurring-failures", params);
  },
  getTestHistory(testName: string, params?: { limit?: number }) {
    return apiClient.get(`/qa-agent/test-history/${encodeURIComponent(testName)}`, params);
  },
  getRunComparison(runA: string, runB: string) {
    return apiClient.get("/qa-agent/run-comparison", { run_a: runA, run_b: runB });
  },
  getHealthSummary(params?: { project_id?: string }) {
    return apiClient.get("/qa-agent/health-summary", params);
  },
  getNarrativeAnalysis(params?: { project_id?: string }) {
    return apiClient.get("/qa-agent/narrative-analysis", params);
  },

  // ── Test Intelligence ──────────────────────────────────────────
  getFlakyTests() {
    return apiClient.get<import("./types").QAFlakyResponse>("/qa-agent/intelligence/flaky");
  },
  getRouteHistory(routePath: string) {
    // route_path is a {path:path} segment on the backend (captures slashes);
    // strip the leading slash so we don't produce a double slash — the handler re-adds it.
    return apiClient.get<import("./types").QARouteHistoryResponse>(
      `/qa-agent/intelligence/history/${routePath.replace(/^\//, "")}`,
    );
  },
  getCoverageAnalysis() {
    return apiClient.get<import("./types").QACoverageAnalysis>("/qa-agent/coverage-analysis");
  },
};

// ---------------------------------------------------------------------------
// Git Configuration — Delivery UX Plan 1.4
// ---------------------------------------------------------------------------

import type {
  ProjectGitConfig,
  GitConfigUpdate,
  GitConnectionTestResult,
  BranchStrategyTemplate,
} from "./types";

export const gitConfigApi = {
  get(projectId: string) {
    return apiClient.get<ProjectGitConfig>(`/projects/${projectId}/git-config`);
  },
  upsert(projectId: string, body: GitConfigUpdate) {
    return apiClient.put<ProjectGitConfig>(`/projects/${projectId}/git-config`, body);
  },
  testConnection(projectId: string, pat?: string) {
    return apiClient.post<GitConnectionTestResult>(
      `/projects/${projectId}/git-config/test-connection`,
      { pat: pat ?? null },
    );
  },
  getTemplates(projectId: string) {
    return apiClient.get<BranchStrategyTemplate[]>(
      `/projects/${projectId}/git-config/templates`,
    );
  },
};

// ---------------------------------------------------------------------------
// Trial invite codes (platform admin) — code-gated trials
// ---------------------------------------------------------------------------

export interface TrialCode {
  id: string;
  code: string;
  assigned_email: string | null;
  trial_days: number;
  redeemed: boolean;
  redeemed_at: string | null;
  redeemed_by_user_id: string | null;
  code_expires_at: string | null;
  is_active: boolean;
  notes: string | null;
  created_by_email: string | null;
  created_at: string;
}

export interface CreateTrialCodeBody {
  assigned_email?: string | null;
  trial_days?: number | null;
  code_expires_at?: string | null;
  notes?: string | null;
}

export const trialCodesApi = {
  list(includeRedeemed = true): Promise<{ codes: TrialCode[]; count: number }> {
    return apiClient.get(`/platform/trial-codes/?include_redeemed=${includeRedeemed}`);
  },
  create(body: CreateTrialCodeBody): Promise<TrialCode> {
    return apiClient.post("/platform/trial-codes/", body);
  },
  revoke(id: string): Promise<{ revoked: boolean; id: string }> {
    return apiClient.delete(`/platform/trial-codes/${id}`);
  },
};

/**
 * Compliance / DSAR operations (GDPR Art.15 export, Art.17 erasure, PII audit).
 *
 * Every endpoint here was built, tested and live — and had **no frontend
 * consumer at all**, so orgs paying for the COMPLIANCE_OPS feature had no way to
 * run a data-subject request. The whole router is gated on
 * ORG_ADMIN + COMPLIANCE_OPS, so a 403 here means the plan lacks the add-on.
 */
/** WP-0.6 — the SOC2 authentication-evidence surface.
 *
 *  Three routes on the append-only `auth_login_events` trail (6,262 rows and
 *  still recording) that nothing in frontend/, scripts/, .github/ or
 *  felix-agent invoked. The hard part — a bounded, self-describing export that
 *  is defensible to an auditor — was already written; there was simply no way
 *  to run it except by curling the endpoint and hoping the bounds were right. */
export const platformSecurityApi = {
  loginEvents: (params: {
    email?: string;
    ip?: string;
    outcome?: string;
    failures_only?: boolean;
    limit?: number;
  } = {}): Promise<{ events: AuthLoginEvent[]; count: number }> => {
    const q = new URLSearchParams();
    if (params.email) q.set("email", params.email);
    if (params.ip) q.set("ip", params.ip);
    if (params.outcome) q.set("outcome", params.outcome);
    if (params.failures_only) q.set("failures_only", "true");
    q.set("limit", String(params.limit ?? 200));
    return apiClient.get(`/platform/security/login-events?${q}`);
  },

  loginSummary: (hours = 168, email?: string): Promise<{
    window_hours: number;
    email: string | null;
    sources: LoginSourceRollup[];
  }> => {
    const q = new URLSearchParams({ hours: String(hours) });
    if (email) q.set("email", email);
    return apiClient.get(`/platform/security/login-events/summary?${q}`);
  },

  /** The auditor's control sample: chronological, windowed by date, and
   *  self-audited — who pulled which window is itself recorded. */
  evidenceExport: (since: string, until: string, outcome?: string):
    Promise<AuthEvidenceBundle> => {
    const q = new URLSearchParams({ since, until, format: "json" });
    if (outcome) q.set("outcome", outcome);
    return apiClient.get(`/platform/security/evidence-export?${q}`);
  },
};

/** The maintenance banner an admin can actually turn off.
 *
 *  deploy.yml raises the banner via psql at job start and — deliberately —
 *  LEAVES IT UP when the deploy fails, so users see the outage rather than a
 *  silently broken build. Nothing in the product could lower it again: the two
 *  admin routes had no caller, so clearing a stranded banner meant database or
 *  curl access. */
export const maintenanceApi = {
  get: (): Promise<MaintenanceState> =>
    apiClient.get("/system/maintenance"),

  start: (message?: string): Promise<MaintenanceState> =>
    apiClient.post("/platform/system/maintenance/start", { message: message || null }),

  end: (): Promise<MaintenanceState> =>
    apiClient.post("/platform/system/maintenance/end", {}),
};

export const complianceApi = {
  listOperations(limit = 50, offset = 0): Promise<{
    items: ComplianceOperation[];
    total: number;
    limit: number;
    offset: number;
  }> {
    return apiClient.get(`/compliance/operations?limit=${limit}&offset=${offset}`);
  },
  exportOrgData(body: {
    justification: string;
    format?: string;
    legal_basis?: string;
  }): Promise<ComplianceOperation> {
    return apiClient.post("/compliance/export-data", body);
  },

  /** POST /compliance/customer-data-export — ToS §14.4(b): the org's Customer
   *  Data as DATA.
   *
   *  Distinct from exportOrgData above, which returns a SUMMARY — entity
   *  counts, workspace metadata, usage totals. Its own docstring is honest
   *  about that, and it is not what §14.4(b) promises: "a customer exercising
   *  that clause learned how many user stories they had, not what any of them
   *  said". This returns the rows.
   *
   *  The route also enforces the 30-day post-termination window, so a
   *  terminated org gets a clear refusal rather than an empty bundle. */
  exportCustomerData(): Promise<CustomerDataExport> {
    return apiClient.post<CustomerDataExport>("/compliance/customer-data-export", {});
  },
  exportUserData(userId: string, body: {
    justification: string;
    legal_basis?: string;
  }): Promise<ComplianceOperation> {
    return apiClient.post(`/compliance/users/${userId}/export`, body);
  },
  eraseUserData(userId: string, body: {
    confirm_deletion: boolean;
    justification: string;
    legal_basis?: string;
    /** Required when the user is their org's last Org Admin (row c1b23e5d). */
    successor_user_id?: string;
    /** One successor per org id, for every org they are the last Org Admin of
     *  (row 88a80a65). */
    successors?: Record<string, string>;
  }): Promise<ComplianceOperation> {
    return apiClient.post(`/compliance/users/${userId}/erase`, body);
  },
  runPiiAudit(): Promise<ComplianceOperation> {
    return apiClient.post("/compliance/pii-audit", undefined);
  },

  /** GET /compliance/retention-assessment — what is held and how much is past
   *  its retention class. Writes nothing. */
  retentionAssessment: (): Promise<RetentionAssessment> =>
    apiClient.get("/compliance/retention-assessment"),


  /** Legal holds SUSPEND retention deletion for a scope. A hold nobody can see
   *  is indistinguishable from no hold, and that is how data under litigation
   *  hold gets deleted. */
  listLegalHolds: (): Promise<LegalHold[]> =>
    apiClient.get("/compliance/legal-holds"),



  /** GET /compliance/disposition-certificates — the evidence that data due for
   *  deletion actually was deleted. This is what an auditor asks for, and it
   *  existed with no way to read it. */
  dispositionCertificates: (): Promise<DispositionCertificate[]> =>
    apiClient.get("/compliance/disposition-certificates"),

  /** Row 6aa9d34c — an erased person's WORDS. Felix proposes a curated version
   *  of their transcript lines; nothing is written until a reviewer approves
   *  it having seen the original beside it, and apply writes it into the
   *  transcript itself. */
  proposeTranscriptCurations: (userId: string, body: {
    erasure_request_id?: string;
  } = {}): Promise<TranscriptCurationProposal> =>
    apiClient.post(`/compliance/users/${userId}/transcript-curations`, body),

  /** The person's OPEN erasure requests, latest first. A proposal is linked
   *  to the one picked (review of #3766, H2). */
  listOpenErasureRequests: (userId: string): Promise<{ items: OpenErasureRequest[]; total: number }> =>
    apiClient.get(`/compliance/users/${userId}/erasure-requests`),

  /** Refuse an erasure request: the person is sent the reasons (Art.12). The
   *  request counts as answered only if that notice was sent. */
  rejectErasureRequest: (requestId: string, reasons: string): Promise<ErasureRefusalResult> =>
    apiClient.post(`/compliance/erasure-requests/${requestId}/reject`, { reasons }),

  /** Approve an org-level data deletion request (row 33a50464): an Org Admin
   *  other than the filer, or a platform admin when the filer is the org's
   *  only Org Admin. The request stays open until it is carried out. */
  approveOrgErasureRequest: (requestId: string): Promise<OrgErasureApproval> =>
    apiClient.post(`/compliance/erasure-requests/${requestId}/approve`, {}),

  /** Carry out an APPROVED org-level data deletion: archives the org and
   *  erases its people (row 33a50464). COMPLETED only when everyone was told. */
  executeOrgErasureRequest: (requestId: string): Promise<OrgErasureResult> =>
    apiClient.post(`/compliance/erasure-requests/${requestId}/execute`, {}),

  /** Record that a person owed an org-erasure notice the system could not send
   *  was told by hand (row 33a50464). Only for someone on the request's
   *  members_to_contact_by_hand; closes it COMPLETED when nothing is left. */
  recordOrgErasureContactedByHand: (
    requestId: string,
    body: { user_id: string; how: string; contacted_at?: string },
  ): Promise<OrgErasureContactedByHand> =>
    apiClient.post(`/compliance/erasure-requests/${requestId}/contacted-by-hand`, body),

  /** Extend an erasure request by two further months (GDPR Art.12(3)): once,
   *  within its first month. The person is sent the reason and the new
   *  deadline; the request is extended only if that notice was sent (503 and
   *  nothing changed otherwise; 409 if already extended, closed or late). */
  extendErasureRequest: (requestId: string, reason: string): Promise<ErasureExtensionResult> =>
    apiClient.post(`/compliance/erasure-requests/${requestId}/extend`, { reason }),

  listTranscriptCurations: (params: {
    subject_user_id?: string;
    erasure_request_id?: string;
    status?: TranscriptCurationStatus;
  } = {}): Promise<TranscriptCurationList> => {
    const q = new URLSearchParams();
    if (params.subject_user_id) q.set("subject_user_id", params.subject_user_id);
    if (params.erasure_request_id) q.set("erasure_request_id", params.erasure_request_id);
    q.set("status", params.status ?? "PROPOSED");
    return apiClient.get(`/compliance/transcript-curations?${q}`);
  },

  /** An APPROVAL must echo `original_sha256` — the hash of the original the
   *  screen showed beside the curated text. The server refuses one without it. */
  reviewTranscriptCurations: (body: {
    decision: "APPROVED" | "REJECTED";
    items: { curation_id: string; original_sha256?: string }[];
  }): Promise<{ decision: string; reviewed: number; curation_ids: string[] }> =>
    apiClient.post("/compliance/transcript-curations/review", body),

  applyTranscriptCurations: (body: {
    curation_ids?: string[];
    subject_user_id?: string;
    erasure_request_id?: string;
  }): Promise<{ applied: number; curation_ids: string[] }> =>
    apiClient.post("/compliance/transcript-curations/apply", body),

  /** Row 6a973ef9 step 3 — the person in DERIVED meeting records (founder
   *  item 19): the same propose -> side-by-side review flow; the erasure
   *  writes approved passages into the records. */
  proposeRecordCurations: (userId: string, body: {
    erasure_request_id?: string;
  } = {}): Promise<RecordCurationProposal> =>
    apiClient.post(`/compliance/users/${userId}/record-curations`, body),

  listRecordCurations: (params: {
    subject_user_id?: string;
    erasure_request_id?: string;
    status?: TranscriptCurationStatus;
  } = {}): Promise<RecordCurationList> => {
    const q = new URLSearchParams();
    if (params.subject_user_id) q.set("subject_user_id", params.subject_user_id);
    if (params.erasure_request_id) q.set("erasure_request_id", params.erasure_request_id);
    q.set("status", params.status ?? "PROPOSED");
    return apiClient.get(`/compliance/record-curations?${q}`);
  },

  /** As reviewTranscriptCurations: an APPROVAL echoes `original_sha256`. */
  reviewRecordCurations: (body: {
    decision: "APPROVED" | "REJECTED";
    items: { curation_id: string; original_sha256?: string }[];
  }): Promise<{ decision: string; reviewed: number; curation_ids: string[] }> =>
    apiClient.post("/compliance/record-curations/review", body),

  /** Row 0f17fbff — an EXTERNAL MEETING GUEST (no account), named by address
   *  or contact. The request is on the Art.12 clock and owned from filing. */
  fileGuestErasureRequest: (body: {
    email?: string;
    contact_id?: string;
    justification?: string;
    legal_basis?: string;
  }): Promise<GuestErasureRequestFiled> =>
    apiClient.post("/compliance/guests/erasure-requests", body),

  /** Felix curates the guest's named lines; review and apply are the routes
   *  above, exactly as for a user. */
  proposeGuestTranscriptCurations: (requestId: string): Promise<TranscriptCurationProposal> =>
    apiClient.post(`/compliance/erasure-requests/${requestId}/guest-transcript-curations`, {}),

  /** Row 6a973ef9 step 3: the guest in the project's meeting records (founder
   *  item 19), curated and reviewed through the record routes above. */
  proposeGuestRecordCurations: (requestId: string): Promise<RecordCurationProposal> =>
    apiClient.post(`/compliance/erasure-requests/${requestId}/guest-record-curations`, {}),

  /** Dry run by default; a real erasure needs dryRun=false AND confirmDeletion. */
  eraseGuest: (requestId: string, opts: { dryRun?: boolean; confirmDeletion?: boolean } = {}):
    Promise<GuestErasureResult> =>
    apiClient.post(
      `/compliance/erasure-requests/${requestId}/erase-guest?dry_run=${opts.dryRun ?? true}`,
      { confirm_deletion: opts.confirmDeletion ?? false },
    ),
};

/**
 * Change-control IMPACTS (the `change_control` table), distinct from change
 * REQUESTS. When a baselined entity changes, the platform records the knock-on
 * impacts here — and `wf020_health_assessment` counts the unresolved ones
 * against the project's health score. Both endpoints existed and were tested;
 * neither had a frontend consumer, so the score dropped for a reason the user
 * could neither see nor clear.
 */
export const changeControlApi = {
  listImpacts(params: {
    project_id?: string;
    resolution_status?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<{ items: ChangeImpact[]; total: number; limit: number; offset: number }> {
    const q = new URLSearchParams();
    if (params.project_id) q.set("project_id", params.project_id);
    if (params.resolution_status) q.set("resolution_status", params.resolution_status);
    q.set("limit", String(params.limit ?? 50));
    q.set("offset", String(params.offset ?? 0));
    return apiClient.get(`/change-control/?${q.toString()}`);
  },
  resolveImpact(itemId: string, body: {
    resolution_status?: string;
    resolution_notes?: string;
  }): Promise<ChangeImpact> {
    return apiClient.post(`/change-control/${itemId}/resolve`, body);
  },
};


/** T1 (row 7f612cac): the per-role "confirm before Felix changes things" flag. */
export interface FelixConfirmRole {
  role: string;
  org_default: boolean | null;
  project_override: boolean | null;
  effective: boolean;
  effective_source: "project" | "org" | "org_autonomy_level" | "default";
}

export interface FelixConfirmPolicy {
  project_id: string | null;
  autonomy_level: string | null;
  roles: FelixConfirmRole[];
}

export interface FelixConfirmPolicyChange {
  project_id: string | null;
  role: string;
  op: "INSERT" | "UPDATE" | "DELETE";
  old_value: boolean | null;
  new_value: boolean | null;
  changed_by: string | null;
  changed_at: string;
}

export const felixConfirmPolicyApi = {
  get(projectId?: string | null): Promise<FelixConfirmPolicy> {
    return apiClient.get<FelixConfirmPolicy>(
      "/felix/confirm-policy", projectId ? { project_id: projectId } : undefined);
  },
  setOrgDefault(role: string, confirm: boolean): Promise<unknown> {
    return apiClient.put("/felix/confirm-policy/org", { role, confirm_before_changes: confirm });
  },
  /** Every change to the flag, newest first (Org Admins only; audited by trigger). */
  changes(limit = 5): Promise<FelixConfirmPolicyChange[]> {
    return apiClient.get<FelixConfirmPolicyChange[]>("/felix/confirm-policy/changes", { limit });
  },
  setProjectOverride(projectId: string, role: string, confirm: boolean | null): Promise<unknown> {
    return apiClient.put(`/felix/confirm-policy/projects/${projectId}`,
      { role, confirm_before_changes: confirm });
  },
};

import type { PlatformApiKey, SupportRequest, SupportRequestStatus } from "./types";

/** Operator controls that had a route and no page (row 83b07d62). */
export const platformOpsApi = {
  listApiKeys(): Promise<PlatformApiKey[]> {
    return apiClient.get("/platform/api-keys/");
  },
  revokeApiKey(keyId: string): Promise<{ revoked: boolean; id: string; name: string }> {
    return apiClient.delete(`/platform/api-keys/${keyId}`);
  },
  listSupportRequests(status?: SupportRequestStatus): Promise<{ items: SupportRequest[]; open: number }> {
    return apiClient.get("/platform/support-requests", status ? { status } : undefined);
  },
  setSupportRequestStatus(id: string, status: SupportRequestStatus): Promise<{ id: string; status: SupportRequestStatus }> {
    return apiClient.patch(`/platform/support-requests/${id}`, { status });
  },
};

import type { RetentionNoticeSummary } from "./types";

/** Row 79d0e9e0: a retention-disposal notice and the copy its owners can take. */
export const retentionNoticeApi = {
  get(batchId: string): Promise<RetentionNoticeSummary> {
    return apiClient.get<RetentionNoticeSummary>(`/compliance/retention-notices/${batchId}`);
  },
  /** The complete export of the records the disposal will delete, as a zip.
   *  Through the API client so the bearer token goes with it -- an emailed
   *  link cannot authenticate a direct download. */
  download(batchId: string): Promise<{ blob: Blob; filename: string }> {
    return apiClient.blob(`/compliance/retention-notices/${batchId}/export`);
  },
};

import type {
  FelixProposal,
  FelixProposalApproveResponse,
  FelixProposalChangeResponse,
} from "./types";

/** Row fad9bff7: the person's Approve / Change on Felix's approval cards. */
export const felixProposalsApi = {
  /** The cards still waiting (a meeting's proposals reach the app here). */
  pending(projectId?: string | null): Promise<{ proposals: FelixProposal[] }> {
    return apiClient.get<{ proposals: FelixProposal[] }>(
      "/felix/proposals",
      projectId ? { project_id: projectId } : undefined,
    );
  },
  approve(proposalId: string): Promise<FelixProposalApproveResponse> {
    return apiClient.post<FelixProposalApproveResponse>(`/felix/proposals/${proposalId}/approve`);
  },
  change(proposalId: string): Promise<FelixProposalChangeResponse> {
    return apiClient.post<FelixProposalChangeResponse>(`/felix/proposals/${proposalId}/change`);
  },
};


// ── Zoho Projects (row 96ef2210) ─────────────────────────────────────────
export const zohoApi = {
  /** Whether this org is connected, to which centre and portal, and the import's health. */
  status(): Promise<import("./types").ZohoStatus> {
    return apiClient.get<import("./types").ZohoStatus>("/integrations/zoho/status");
  },
  /** Zoho's authorize URL for an org admin (opened once, for the whole organisation). */
  /** seed: write access too, for FTL's own test portal; the server allows it to platform admins only.
   *  people: also read Zoho People's time tracker (opt-in). */
  connect(seed = false, people = false): Promise<{ authorization_url: string }> {
    const q = [seed ? "seed=true" : "", people ? "people=true" : ""].filter(Boolean).join("&");
    return apiClient.get<{ authorization_url: string }>(`/oauth/zoho/connect${q ? `?${q}` : ""}`);
  },
  /** Forget the connection; hours already imported stay. */
  disconnect(): Promise<{ connected: false }> {
    return apiClient.post<{ connected: false }>("/integrations/zoho/disconnect", {});
  },
  /** Each Zoho project the poller has seen, and the FTL project it was confirmed as. */
  projects(): Promise<{ items: import("./types").ZohoProjectMapping[] }> {
    return apiClient.get<{ items: import("./types").ZohoProjectMapping[] }>("/integrations/zoho/projects");
  },
  /** Confirm (or clear) which FTL project a Zoho project is. */
  mapProject(zohoProjectId: string, ftlProjectId: string | null): Promise<unknown> {
    return apiClient.put(`/integrations/zoho/projects/${encodeURIComponent(zohoProjectId)}`, { ftl_project_id: ftlProjectId });
  },
  /** Zoho time logs FTL couldn't place yet. */
  review(): Promise<{ items: import("./types").ZohoReviewItem[] }> {
    return apiClient.get<{ items: import("./types").ZohoReviewItem[] }>("/integrations/zoho/review");
  },
  /** Run this organisation's import now (and, with write access, push its plan). */
  importNow(): Promise<import("./types").ZohoImportResult> {
    return apiClient.post<import("./types").ZohoImportResult>("/integrations/zoho/import-now", {});
  },
  /** What this org pushes to Zoho: its level, its projects and the push's health. */
  pushSettings(): Promise<import("./types").ZohoPushSettings> {
    return apiClient.get<import("./types").ZohoPushSettings>("/integrations/zoho/push");
  },
  /** The FTL level that becomes a Zoho task, for the whole organisation. */
  setPushLevel(level: "TASK" | "STORY"): Promise<unknown> {
    return apiClient.put("/integrations/zoho/push/level", { push_level: level });
  },
  /** Push this FTL project to Zoho from the next sync on. */
  pushProject(ftlProjectId: string): Promise<unknown> {
    return apiClient.post("/integrations/zoho/push/projects", { ftl_project_id: ftlProjectId });
  },
  /** Stop pushing this project; what is in Zoho stays and its hours still come back. */
  stopPushing(ftlProjectId: string): Promise<unknown> {
    return apiClient.delete(`/integrations/zoho/push/projects/${encodeURIComponent(ftlProjectId)}`);
  },
  /** FTL's estimate beside the approved hours Zoho sent back, per pushed item and person. */
  estimateVsActual(projectId: string): Promise<import("./types").ZohoEstimateVsActual> {
    return apiClient.get<import("./types").ZohoEstimateVsActual>(
      `/integrations/zoho/estimate-vs-actual?project_id=${encodeURIComponent(projectId)}`);
  },
};

// ── Hours actually spent (row 96ef2210) ───────────────────────────────────
export const timeEntriesApi = {
  /** Record the caller's own hours; they count at once unless the org requires approval. */
  record(body: import("./types").TimeEntryCreate): Promise<import("./types").TimeEntry> {
    return apiClient.post<import("./types").TimeEntry>("/time-entries", body);
  },
  /** What the hours prompt suggests when a task is done. Saves nothing. */
  suggestion(taskId: string): Promise<import("./types").HoursSuggestion> {
    return apiClient.get<import("./types").HoursSuggestion>(`/time-entries/suggestion/${taskId}`);
  },
  /** Time entries in the org, every status (the review view). */
  list(params: { project_id?: string; user_id?: string; status?: "APPROVED" | "PENDING" | "REJECTED"; limit?: number }): Promise<{ items: import("./types").TimeEntry[] }> {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) q.set(k, String(v));
    return apiClient.get<{ items: import("./types").TimeEntry[] }>(`/time-entries?${q.toString()}`);
  },
  /** A project owner or org admin approves hours entered in FTL that are waiting. */
  approve(entryId: string): Promise<import("./types").TimeEntry> {
    return apiClient.post<import("./types").TimeEntry>(`/time-entries/${entryId}/approve`, {});
  },
  /** A project owner or org admin rejects them: kept, never counted. */
  reject(entryId: string): Promise<import("./types").TimeEntry> {
    return apiClient.post<import("./types").TimeEntry>(`/time-entries/${entryId}/reject`, {});
  },
  /** A person removes hours they entered in FTL. */
  remove(entryId: string): Promise<void> {
    return apiClient.delete(`/time-entries/${entryId}`);
  },
  /** Each task's estimate beside its approved hours, and the project's totals. */
  estimateVsActual(projectId: string): Promise<import("./types").EstimateVsActual> {
    return apiClient.get<import("./types").EstimateVsActual>(`/time-entries/projects/${projectId}/estimate-vs-actual`);
  },
};
